import React, { useState, useEffect } from "react";
import Plot, { Plotly } from "./Plot";  // <-- the ONLY Plot/Plotly import
import { app, db, auth, authPromise } from "./firebase";
import {
  doc,
  onSnapshot,
  collection,
  setDoc,
  addDoc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, getBytes } from "firebase/storage";
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import "./App.css";


// Assuming images are in the public directory
const combatIcon = "/combatseq-icon.png";
const heatmapIcon = "/heatmap-icon.png";
const volcanoIcon = "/volcano-icon.png";
const infoIcon = "/info-icon.png";
const pcaIcon = "/pca-icon.png";
const predictIcon = "/predict-icon.png";

function getProjectRunMetadata(user, activeProject) {
  return {
    ownerUid: user?.uid || null,
    projectId: activeProject?.id || null,
    projectName: activeProject?.name || null,
    researchQuestion: activeProject?.question || null,
    projectContext: activeProject?.context || null,
  };
}

function getUploadedInputSource(files) {
  const names = files.filter(Boolean).map((file) => file.name);
  return {
    inputSourceType: "uploaded-file",
    inputSourceLabel: names.join(" + "),
    inputFileNames: names,
  };
}

function getImportedInputSource(inputs) {
  const selected = inputs.filter(Boolean);
  const names = selected.map((input) => input.name || input.sourceFileName || input.storagePath || "Imported file");
  return {
    inputSourceType: "imported-analysis-file",
    inputSourceLabel: names.join(" + "),
    inputFileNames: names,
    inputStoragePaths: selected.map((input) => input.storagePath).filter(Boolean),
    inputDatasetIds: selected.map((input) => input.id).filter(Boolean),
  };
}

function getCombinedInputSource(uploadedFiles = [], importedInputs = []) {
  const files = uploadedFiles.filter(Boolean);
  const imports = importedInputs.filter(Boolean);
  if (files.length && imports.length) {
    const uploaded = getUploadedInputSource(files);
    const imported = getImportedInputSource(imports);
    return {
      inputSourceType: "mixed-uploaded-and-imported-files",
      inputSourceLabel: [uploaded.inputSourceLabel, imported.inputSourceLabel].filter(Boolean).join(" + "),
      inputFileNames: [...uploaded.inputFileNames, ...imported.inputFileNames],
      inputStoragePaths: imported.inputStoragePaths,
      inputDatasetIds: imported.inputDatasetIds,
    };
  }
  return imports.length ? getImportedInputSource(imports) : getUploadedInputSource(files);
}

function importedDatasetSummary(dataset) {
  if (!dataset) return null;
  return {
    id: dataset.id,
    name: dataset.name || dataset.sourceFileName || "Imported analysis file",
    storagePath: dataset.storagePath || "",
    sourceUrl: dataset.sourceUrl || "",
    source: dataset.source || "",
    reviewStatus: dataset.reviewStatus || "",
  };
}

function buildHeatmapClusterOverlays(heatmapJson) {
  const orderedSamples = heatmapJson?.x || heatmapJson?.orderedSamples || [];
  const clusters = heatmapJson?.sampleClusters || [];
  if (!orderedSamples.length || !clusters.length) {
    return { shapes: [], annotations: [] };
  }

  const colors = ["#2f7d32", "#1d6fb8", "#9b6bd3", "#d99000", "#c75050", "#0f9f8f"];
  const shapes = [];
  const annotations = [];

  clusters.forEach((cluster, index) => {
    const sampleIndexes = (cluster.samples || [])
      .map((sample) => orderedSamples.indexOf(sample))
      .filter((sampleIndex) => sampleIndex >= 0);

    if (!sampleIndexes.length) return;

    const start = Math.min(...sampleIndexes);
    const end = Math.max(...sampleIndexes);
    const x0 = orderedSamples[start];
    const x1 = orderedSamples[end];
    const mid = orderedSamples[Math.floor((start + end) / 2)];
    const color = colors[index % colors.length];
    const yBase = 1.015;
    const yTop = 1.075 + (index % 2) * 0.035;

    shapes.push(
      {
        type: "line",
        xref: "x",
        yref: "paper",
        x0,
        x1,
        y0: yTop,
        y1: yTop,
        line: { color, width: 3 },
      },
      {
        type: "line",
        xref: "x",
        yref: "paper",
        x0,
        x1: x0,
        y0: yBase,
        y1: yTop,
        line: { color, width: 3 },
      },
      {
        type: "line",
        xref: "x",
        yref: "paper",
        x0: x1,
        x1,
        y0: yBase,
        y1: yTop,
        line: { color, width: 3 },
      }
    );

    annotations.push({
      x: mid,
      y: yTop + 0.025,
      xref: "x",
      yref: "paper",
      text: cluster.label || `Cluster ${index + 1}`,
      showarrow: false,
      font: { color, size: 12 },
      bgcolor: "rgba(255,255,255,0.86)",
      bordercolor: color,
      borderwidth: 1,
      borderpad: 3,
    });
  });

  return { shapes, annotations };
}

const RUN_COLLECTIONS = [
  { collectionName: "deseq2Data", toolName: "DESeq2 Analysis", toolKey: "deseq2" },
  { collectionName: "pcaData", toolName: "PCA Analysis", toolKey: "pca" },
  { collectionName: "heatmapData", toolName: "Heatmap Generator", toolKey: "heatmap" },
  { collectionName: "combatSeqResults", toolName: "ComBat-Seq Batch Correction", toolKey: "combat" },
  { collectionName: "geneExpressionData", toolName: "Gene Expression Predictor", toolKey: "predictor" },
  { collectionName: "scrnaData", toolName: "scRNA Cluster Explorer", toolKey: "scrna" },
];

const PUBLIC_DATASET_LEADS = [
  {
    id: "geo-diabetes-rnaseq",
    title: "Diabetes RNA-seq leads",
    source: "GEO",
    organism: "Human / mouse",
    topic: "diabetes, beta cells, insulin signaling",
    analysisFit: "DESeq2, PCA, heatmap",
    url: "https://www.ncbi.nlm.nih.gov/gds/?term=diabetes+RNA-seq+beta+cell",
    notes:
      "Search lead for public diabetes RNA-seq studies. Review sample design and availability before choosing a dataset.",
  },
  {
    id: "geo-breast-cancer-rnaseq",
    title: "Breast cancer RNA-seq leads",
    source: "GEO",
    organism: "Human",
    topic: "breast cancer, tumor biology, treatment response",
    analysisFit: "DESeq2, PCA, heatmap, predictor",
    url: "https://www.ncbi.nlm.nih.gov/gds/?term=breast+cancer+RNA-seq",
    notes:
      "Search lead for public breast cancer RNA-seq studies. Good candidate area for hypothesis testing and classroom comparisons.",
  },
  {
    id: "geo-brain-cancer-rnaseq",
    title: "Brain cancer RNA-seq leads",
    source: "GEO",
    organism: "Human / model systems",
    topic: "glioma, glioblastoma, brain tumor expression",
    analysisFit: "DESeq2, PCA, heatmap",
    url: "https://www.ncbi.nlm.nih.gov/gds/?term=glioblastoma+RNA-seq",
    notes:
      "Search lead for brain cancer expression datasets. Use project notes to record phenotype groups and sample exclusions.",
  },
  {
    id: "sra-immune-response",
    title: "Immune response RNA-seq leads",
    source: "SRA",
    organism: "Human / mouse",
    topic: "immune stimulation, inflammation, infection response",
    analysisFit: "PCA, DESeq2, heatmap",
    url: "https://www.ncbi.nlm.nih.gov/sra/?term=immune+response+RNA-seq",
    notes:
      "Search lead for sequencing reads. Later GATOR can add guided import from SRA metadata and processed count files.",
  },
  {
    id: "gdc-tcga-brca",
    title: "TCGA breast cancer expression leads",
    source: "GDC/TCGA",
    organism: "Human",
    topic: "TCGA-BRCA, cancer cohort discovery",
    analysisFit: "PCA, heatmap, predictor",
    url: "https://portal.gdc.cancer.gov/projects/TCGA-BRCA",
    notes:
      "GDC project lead for TCGA-BRCA. Useful for building a future guided GDC import workflow.",
  },
  {
    id: "gdc-tcga-gbm",
    title: "TCGA glioblastoma expression leads",
    source: "GDC/TCGA",
    organism: "Human",
    topic: "TCGA-GBM, brain cancer cohort discovery",
    analysisFit: "PCA, heatmap, predictor",
    url: "https://portal.gdc.cancer.gov/projects/TCGA-GBM",
    notes:
      "GDC project lead for TCGA-GBM. Good target for public data discovery workflows once GDC import is automated.",
  },
  {
    id: "classroom-demo-counts",
    title: "Classroom RNA-seq demo dataset",
    source: "GATOR demo",
    organism: "Teaching example",
    topic: "small CSV practice workflow",
    analysisFit: "PCA, heatmap, DESeq2 walkthrough",
    url: "/templates/fpkm_data.csv",
    notes:
      "Local demo file for teaching upload, visualization, and project organization without needing a large public dataset first.",
  },
];

function getRunTime(run) {
  const raw = run.updatedAt || run.createdAt || run.timestamp;
  if (!raw) return 0;
  if (typeof raw?.toDate === "function") return raw.toDate().getTime();
  if (raw instanceof Date) return raw.getTime();
  if (typeof raw === "number") return raw;

  const parsed = Number(raw);
  if (Number.isFinite(parsed)) return parsed;

  const dateParsed = Date.parse(raw);
  return Number.isFinite(dateParsed) ? dateParsed : 0;
}

function formatRunTime(run) {
  const time = getRunTime(run);
  return time ? new Date(time).toLocaleString() : "No timestamp";
}

function formatDuration(seconds) {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return "Not enough data";
  const totalSeconds = Number(seconds);
  if (totalSeconds < 60) return `${Math.round(totalSeconds)} sec`;
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = Math.round(totalSeconds % 60);
  if (minutes < 60) return `${minutes} min ${remainingSeconds} sec`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours} hr ${remainingMinutes} min`;
}

function shouldIncludeRunInReport(run) {
  if (run?.includeInReport !== undefined) return run.includeInReport !== false;
  const status = (run?.status || "").toLowerCase();
  return status !== "failed" && status !== "error";
}

function shouldIncludeArtifactInReport(artifact) {
  return artifact?.includeInReport !== false;
}

function createAutoArtifact(run, suffix, artifact) {
  return {
    ...artifact,
    runId: run.id,
    relatedRunCollection: run.collectionName,
    autoCaptured: true,
    autoArtifactKey: `${run.collectionName}:${run.id}:${suffix}`,
    includeInReport: true,
  };
}

function getAutoArtifactsForRun(run) {
  if (!run || (run.status || "").toLowerCase() !== "completed") return [];

  if (run.collectionName === "deseq2Data") {
    return [
      createAutoArtifact(run, "volcano", {
        title: "DESeq2 volcano plot",
        type: "Plot",
        notes: "Auto-captured volcano plot generated from the DESeq2 run.",
      }),
      run.allResultsPath
        ? createAutoArtifact(run, "all-results", {
            title: "DESeq2 all results table",
            type: "CSV",
            urlOrPath: run.allResultsPath,
            notes: "Auto-captured full DESeq2 results table.",
          })
        : null,
      run.significantResultsPath
        ? createAutoArtifact(run, "significant-results", {
            title: "DESeq2 significant genes table",
            type: "CSV",
            urlOrPath: run.significantResultsPath,
            notes: "Auto-captured significant gene results table.",
          })
        : null,
      run.strongHitsPath
        ? createAutoArtifact(run, "strong-hits", {
            title: "DESeq2 strong hits table",
            type: "CSV",
            urlOrPath: run.strongHitsPath,
            notes: "Auto-captured strong-hit gene table.",
          })
        : null,
      run.heatmapPath
        ? createAutoArtifact(run, "significant-heatmap", {
            title: "DESeq2 significant genes heatmap",
            type: "PNG",
            urlOrPath: run.heatmapPath,
            notes: "Auto-captured heatmap for significant DESeq2 genes.",
          })
        : null,
    ].filter(Boolean);
  }

  if (run.collectionName === "pcaData") {
    return [
      createAutoArtifact(run, "plot", {
        title: "PCA plot",
        type: "Plot",
        notes: "Auto-captured PCA plot generated from the analysis run.",
      }),
      createAutoArtifact(run, "coordinates", {
        title: "PCA coordinates",
        type: "Table",
        notes: "Auto-captured PCA coordinate table available in the run record.",
      }),
    ];
  }

  if (run.collectionName === "heatmapData") {
    return [
      createAutoArtifact(run, "plot", {
        title: "Heatmap plot",
        type: "Plot",
        urlOrPath: run.resultPath || "",
        notes: "Auto-captured heatmap result generated from uploaded expression data.",
      }),
    ];
  }

  if (run.collectionName === "combatSeqResults") {
    return [
      run.correctedCountsPath
        ? createAutoArtifact(run, "corrected-counts", {
            title: "ComBat-Seq corrected counts",
            type: "CSV",
            urlOrPath: run.correctedCountsPath,
            notes: "Auto-captured corrected counts table from ComBat-Seq.",
          })
        : null,
      createAutoArtifact(run, "before-after-pca", {
        title: "ComBat-Seq before/after PCA",
        type: "Plot",
        notes: "Auto-captured PCA comparison showing structure before and after batch correction.",
      }),
    ].filter(Boolean);
  }

  if (run.collectionName === "geneExpressionData") {
    return [
      createAutoArtifact(run, "ranking", {
        title: "Gene Expression Predictor ranking",
        type: "Table",
        notes: "Auto-captured gene predictability ranking from the predictor run.",
      }),
      createAutoArtifact(run, "top-genes-plot", {
        title: "Top predictable genes plot",
        type: "Plot",
        notes: "Auto-captured bar plot of top genes by predictability score.",
      }),
    ];
  }

  return [];
}

function sanitizeFilename(value) {
  return (value || "gator-project-report")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "gator-project-report";
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function getRunMethods(runHistory) {
  const tools = [...new Set(runHistory.map((run) => run.toolName).filter(Boolean))];
  if (!tools.length) return "No selected analysis runs were included.";
  return `Selected analyses were performed in GATOR using ${tools.join(", ")}.`;
}

function artifactCanRenderAsImage(artifact) {
  const path = artifact?.urlOrPath || "";
  return /^https?:\/\//i.test(path) && /\.(png|jpg|jpeg|webp|gif)(\?|$)/i.test(path);
}

function getLinkedRun(artifact, runHistory) {
  return runHistory.find(
    (run) =>
      run.id === artifact?.runId &&
      (!artifact?.relatedRunCollection || run.collectionName === artifact.relatedRunCollection)
  );
}

function scaleNumber(value, min, max, low, high) {
  const n = Number(value);
  if (!Number.isFinite(n) || max === min) return (low + high) / 2;
  return low + ((n - min) / (max - min)) * (high - low);
}

function renderScatterSvg(points, options = {}) {
  const clean = (points || [])
    .map((point) => ({
      x: Number(point.pc1 ?? point.PC1),
      y: Number(point.pc2 ?? point.PC2),
      label: point.sample || "",
      group: point.batch || point.group || "",
    }))
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));

  if (!clean.length) return "";

  const xs = clean.map((point) => point.x);
  const ys = clean.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const width = options.width || 560;
  const height = options.height || 320;
  const pad = 42;
  const colors = ["#2f7d32", "#1d6fb8", "#c94f24", "#7b4cc2", "#2a9d8f", "#b8870b"];
  const groups = [...new Set(clean.map((point) => point.group || "Samples"))];

  const circles = clean
    .map((point) => {
      const x = scaleNumber(point.x, minX, maxX, pad, width - pad);
      const y = scaleNumber(point.y, minY, maxY, height - pad, pad);
      const color = colors[Math.max(0, groups.indexOf(point.group || "Samples")) % colors.length];
      return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="5" fill="${color}" opacity="0.86"><title>${escapeHtml(point.label)}</title></circle>`;
    })
    .join("");

  return `<svg viewBox="0 0 ${width} ${height}" role="img" aria-label="${escapeHtml(options.title || "Scatter plot")}">
    <rect width="${width}" height="${height}" fill="#ffffff" />
    <line x1="${pad}" y1="${height - pad}" x2="${width - pad}" y2="${height - pad}" stroke="#9fb39f" />
    <line x1="${pad}" y1="${pad}" x2="${pad}" y2="${height - pad}" stroke="#9fb39f" />
    <text x="${width / 2}" y="20" text-anchor="middle" font-size="14" font-weight="700" fill="#173f1b">${escapeHtml(options.title || "PCA Plot")}</text>
    <text x="${width / 2}" y="${height - 8}" text-anchor="middle" font-size="10" fill="#4e5d52">${escapeHtml(options.xLabel || "PC1")}</text>
    <text x="14" y="${height / 2}" text-anchor="middle" transform="rotate(-90 14 ${height / 2})" font-size="10" fill="#4e5d52">${escapeHtml(options.yLabel || "PC2")}</text>
    ${circles}
  </svg>`;
}

function renderBarSvg(rows, options = {}) {
  const clean = (rows || [])
    .map((row) => ({
      label: row.gene || row.label || "",
      value: Number(row.r2_score ?? row.value),
    }))
    .filter((row) => row.label && Number.isFinite(row.value))
    .sort((a, b) => b.value - a.value)
    .slice(0, options.limit || 12);

  if (!clean.length) return "";

  const width = 560;
  const height = 350;
  const pad = 44;
  const bottomPad = 76;
  const maxValue = Math.max(...clean.map((row) => row.value), 1);
  const barWidth = (width - pad * 2) / clean.length;
  const bars = clean
    .map((row, index) => {
      const barHeight = scaleNumber(row.value, 0, maxValue, 0, height - pad - bottomPad);
      const x = pad + index * barWidth + 4;
      const y = height - bottomPad - barHeight;
      const labelX = (x + barWidth / 2).toFixed(1);
      const labelY = height - bottomPad + 18;
      return `<g>
        <rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${Math.max(4, barWidth - 8).toFixed(1)}" height="${barHeight.toFixed(1)}" fill="#2f7d32" />
        <text x="${labelX}" y="${labelY}" text-anchor="end" transform="rotate(-38 ${labelX} ${labelY})" font-size="10" fill="#4e5d52">${escapeHtml(row.label.slice(0, 14))}</text>
      </g>`;
    })
    .join("");

  return `<svg viewBox="0 0 ${width} ${height}" role="img" aria-label="${escapeHtml(options.title || "Bar chart")}">
    <rect width="${width}" height="${height}" fill="#ffffff" />
    <line x1="${pad}" y1="${height - bottomPad}" x2="${width - pad}" y2="${height - bottomPad}" stroke="#9fb39f" />
    <line x1="${pad}" y1="${pad}" x2="${pad}" y2="${height - bottomPad}" stroke="#9fb39f" />
    <text x="${width / 2}" y="20" text-anchor="middle" font-size="14" font-weight="700" fill="#173f1b">${escapeHtml(options.title || "Top Genes")}</text>
    ${bars}
  </svg>`;
}

function renderHeatmapSvg(run) {
  let heatmap = run?.resultJsonText ? JSON.parse(run.resultJsonText) : run?.resultJson;
  if (!heatmap && run?.z_log2) heatmap = run;
  const matrix = heatmap?.z_zscore || heatmap?.z_log2 || heatmap?.z;
  if (!Array.isArray(matrix) || !matrix.length || !Array.isArray(matrix[0])) return "";

  const rows = matrix.slice(0, 40);
  const cols = rows[0].slice(0, 40).map((_, index) => index);
  const values = rows.flatMap((row) => row.slice(0, cols.length).map(Number)).filter(Number.isFinite);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const cell = 9;
  const width = cols.length * cell + 120;
  const height = rows.length * cell + 70;
  const colorFor = (value) => {
    const t = Math.max(0, Math.min(1, (Number(value) - min) / (max - min || 1)));
    const r = Math.round(34 + t * 221);
    const g = Math.round(76 + t * 164);
    const b = Math.round(132 - t * 80);
    return `rgb(${r},${g},${b})`;
  };
  const cells = rows
    .map((row, rowIndex) =>
      row
        .slice(0, cols.length)
        .map(
          (value, colIndex) =>
            `<rect x="${80 + colIndex * cell}" y="${36 + rowIndex * cell}" width="${cell}" height="${cell}" fill="${colorFor(value)}" />`
        )
        .join("")
    )
    .join("");

  return `<svg viewBox="0 0 ${width} ${height}" role="img" aria-label="Heatmap">
    <rect width="${width}" height="${height}" fill="#ffffff" />
    <text x="${width / 2}" y="20" text-anchor="middle" font-size="14" font-weight="700" fill="#173f1b">Heatmap (${rows.length} genes x ${cols.length} samples)</text>
    ${cells}
  </svg>`;
}

function renderVolcanoSvg(run) {
  const rows = Array.isArray(run?.results) ? run.results.slice(0, 1200) : [];
  const points = rows
    .map((row) => ({
      x: Number(row.log2FoldChange),
      y: Number(row.padj) > 0 ? -Math.log10(Number(row.padj)) : 0,
      gene: row.gene || "",
      padj: Number(row.padj),
    }))
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
  if (!points.length) return "";

  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const minX = Math.min(...xs, -1);
  const maxX = Math.max(...xs, 1);
  const minY = 0;
  const maxY = Math.max(...ys, 1);
  const width = 560;
  const height = 320;
  const pad = 44;
  const dots = points
    .map((point) => {
      const x = scaleNumber(point.x, minX, maxX, pad, width - pad);
      const y = scaleNumber(point.y, minY, maxY, height - pad, pad);
      const color = point.padj <= 0.05 && Math.abs(point.x) >= 1 ? (point.x > 0 ? "#c62828" : "#1d6fb8") : point.padj <= 0.05 ? "#d99000" : "#9aa39a";
      return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2.2" fill="${color}" opacity="0.68"><title>${escapeHtml(point.gene)}</title></circle>`;
    })
    .join("");

  return `<svg viewBox="0 0 ${width} ${height}" role="img" aria-label="Volcano plot">
    <rect width="${width}" height="${height}" fill="#ffffff" />
    <line x1="${pad}" y1="${height - pad}" x2="${width - pad}" y2="${height - pad}" stroke="#9fb39f" />
    <line x1="${pad}" y1="${pad}" x2="${pad}" y2="${height - pad}" stroke="#9fb39f" />
    <text x="${width / 2}" y="20" text-anchor="middle" font-size="14" font-weight="700" fill="#173f1b">DESeq2 Volcano Plot</text>
    ${dots}
  </svg>`;
}

function renderPosterVisual(artifact, runHistory) {
  const linkedRun = getLinkedRun(artifact, runHistory);
  const title = `${artifact?.title || ""} ${artifact?.type || ""}`.toLowerCase();

  try {
    if (title.includes("heatmap") && linkedRun) return renderHeatmapSvg(linkedRun);
    if (title.includes("volcano") && linkedRun) return renderVolcanoSvg(linkedRun);
    if (title.includes("combat") && linkedRun) {
      return `<div class="paired-visual">${renderScatterSvg(linkedRun.pcaPre, { title: "Before Correction" })}${renderScatterSvg(linkedRun.pcaPost, { title: "After Correction" })}</div>`;
    }
    if (title.includes("pca") && linkedRun) return renderScatterSvg(linkedRun.pca, { title: artifact.title || "PCA Plot" });
    if ((title.includes("predict") || title.includes("top genes")) && linkedRun) {
      return renderBarSvg(linkedRun.predictions, { title: "Top Predictable Genes" });
    }
  } catch (err) {
    console.warn("Could not render poster visual:", err);
  }

  return "";
}

function buildScientificPosterHtml({ project, datasets, runHistory, artifacts, posterSummary }) {
  const title = project?.name || "GATOR Research Poster";
  const question = project?.question || "Research question not provided.";
  const context = project?.context || "Dataset or biological context not provided.";
  const completedRuns = runHistory.filter((run) => (run.status || "").toLowerCase() === "completed");
  const visualArtifacts = artifacts.filter((artifact) =>
    /plot|png|heatmap|pca|volcano|figure|bar/i.test(`${artifact.type || ""} ${artifact.title || ""}`)
  );
  const tableArtifacts = artifacts.filter((artifact) =>
    /csv|table|ranking|results/i.test(`${artifact.type || ""} ${artifact.title || ""}`)
  );

  const datasetItems = datasets.length
    ? datasets
        .slice(0, 6)
        .map(
          (dataset) => `
            <li>
              <strong>${escapeHtml(dataset.name || "Untitled dataset")}</strong>
              <span>${escapeHtml(dataset.source || "Source not provided")}</span>
              <small>${escapeHtml(dataset.datasetKind || "dataset-lead")} | ${escapeHtml(dataset.reviewStatus || "Review status not set")}</small>
              <small>${escapeHtml(dataset.context || dataset.notes || "")}</small>
            </li>`
        )
        .join("")
    : "<li><strong>No datasets registered</strong><span>Add project datasets before final poster export.</span></li>";

  const methodItems = completedRuns.length
    ? completedRuns
        .map(
          (run) => `
            <li>
              <strong>${escapeHtml(run.toolName || "Analysis run")}</strong>
              <span>${escapeHtml(formatRunTime(run))}</span>
              <small>Input source: ${escapeHtml(run.inputSourceLabel || "Not recorded for this run")}</small>
            </li>`
        )
        .join("")
    : "<li><strong>No completed runs selected</strong><span>Select completed runs in Run History.</span></li>";

  const visualPanels = visualArtifacts.length
    ? visualArtifacts
        .slice(0, 6)
        .map((artifact) => {
          const linkedRun = getLinkedRun(artifact, runHistory);
          const generatedVisual = renderPosterVisual(artifact, runHistory);
          const imageHtml = generatedVisual
            ? `<div class="generated-visual">${generatedVisual}</div>`
            : artifactCanRenderAsImage(artifact)
            ? `<img src="${escapeHtml(artifact.urlOrPath)}" alt="${escapeHtml(artifact.title)}" />`
            : `<div class="visual-placeholder">
                <strong>${escapeHtml(artifact.type || "Visual artifact")}</strong>
                <span>${escapeHtml(artifact.urlOrPath || "Plot is tracked in GATOR; image embedding is coming in a future version.")}</span>
              </div>`;

          return `
            <article class="visual-card">
              ${imageHtml}
              <h3>${escapeHtml(artifact.title || "Untitled visual")}</h3>
              <p class="input-source"><strong>Input source:</strong> ${escapeHtml(linkedRun?.inputSourceLabel || "Not recorded for this run")}</p>
              <p>${escapeHtml(artifact.notes || "Selected visual artifact from the GATOR workspace.")}</p>
            </article>`;
        })
        .join("")
    : `<article class="visual-card">
        <div class="visual-placeholder"><strong>No selected visuals</strong><span>Include artifacts in the report to populate this section.</span></div>
        <h3>Visual Results</h3>
        <p>Select heatmaps, PCA plots, volcano plots, or predictor plots in the Artifact Library.</p>
      </article>`;

  const artifactItems = tableArtifacts.length
    ? tableArtifacts
        .slice(0, 8)
        .map(
          (artifact) => `
            <li>
              <strong>${escapeHtml(artifact.title || "Untitled artifact")}</strong>
              <span>${escapeHtml(artifact.type || "Artifact")}</span>
              <small>${escapeHtml(artifact.urlOrPath || artifact.notes || "")}</small>
            </li>`
        )
        .join("")
    : "<li><strong>No selected tables/files</strong><span>Include result tables or CSV artifacts to list them here.</span></li>";

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(title)} - GATOR Scientific Poster</title>
  <style>
    :root {
      --bg: #f7faf7;
      --ink: #162016;
      --muted: #4e5d52;
      --line: #cbd8cc;
      --green: #2f7d32;
      --green-dark: #173f1b;
      --panel: #ffffff;
    }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      background: #dfe8df;
      color: var(--ink);
      font-family: Arial, Helvetica, sans-serif;
    }
    .poster {
      width: min(1600px, 100vw);
      min-height: 100vh;
      margin: 0 auto;
      padding: 28px;
      background: var(--bg);
      display: grid;
      grid-template-rows: auto 1fr auto;
      gap: 20px;
    }
    header {
      padding: 22px 26px;
      border-radius: 10px;
      background: linear-gradient(135deg, var(--green-dark), #245b28);
      color: white;
      display: grid;
      gap: 8px;
    }
    header h1 {
      margin: 0;
      font-size: clamp(34px, 4vw, 68px);
      line-height: 1;
      letter-spacing: 0;
    }
    header p {
      margin: 0;
      max-width: 1150px;
      font-size: clamp(17px, 1.4vw, 24px);
      color: rgba(255,255,255,0.86);
    }
    main {
      display: grid;
      grid-template-columns: 0.9fr 1.5fr 0.9fr;
      gap: 18px;
      align-items: stretch;
    }
    section {
      background: var(--panel);
      border: 1px solid var(--line);
      border-radius: 10px;
      padding: 18px;
      display: grid;
      gap: 12px;
      align-content: start;
      box-shadow: 0 10px 26px rgba(22,32,22,0.08);
    }
    h2 {
      margin: 0;
      color: var(--green-dark);
      font-size: 24px;
      border-bottom: 3px solid var(--green);
      padding-bottom: 6px;
    }
    h3 { margin: 0; font-size: 18px; }
    p { margin: 0; line-height: 1.45; color: var(--muted); }
    ul { margin: 0; padding-left: 20px; display: grid; gap: 10px; }
    li strong { display: block; color: var(--ink); }
    li span, li small { display: block; color: var(--muted); line-height: 1.35; }
    .left, .right { display: grid; gap: 18px; }
    .visuals {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px;
    }
    .visual-card {
      border: 1px solid var(--line);
      border-radius: 8px;
      padding: 10px;
      min-height: 260px;
      display: grid;
      gap: 5px;
      background: #fbfdfb;
    }
    .visual-card h3 {
      margin: 4px 0 0;
      font-size: 16px;
      line-height: 1.05;
    }
    .visual-card p {
      margin: 0;
      font-size: 12px;
      line-height: 1.22;
      color: var(--muted);
    }
    .visual-card .input-source {
      font-size: 10.5px;
      line-height: 1.15;
      overflow-wrap: anywhere;
    }
    .visual-card img {
      width: 100%;
      max-height: 360px;
      object-fit: contain;
      border: 1px solid var(--line);
      border-radius: 6px;
      background: white;
    }
    .generated-visual {
      border: 1px solid var(--line);
      border-radius: 6px;
      background: white;
      overflow: hidden;
      min-height: 280px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .generated-visual svg {
      display: block;
      width: 100%;
      height: auto;
      max-height: 380px;
    }
    .paired-visual {
      display: grid;
      gap: 8px;
    }
    .visual-placeholder {
      min-height: 170px;
      border: 2px dashed #9fb39f;
      border-radius: 8px;
      display: grid;
      place-content: center;
      gap: 8px;
      text-align: center;
      padding: 16px;
      background: #f0f6f0;
      color: var(--muted);
      overflow-wrap: anywhere;
    }
    .visual-placeholder strong {
      color: var(--green-dark);
      font-size: 20px;
    }
    .callout {
      border-left: 6px solid var(--green);
      background: #eef7ee;
      padding: 12px;
      border-radius: 8px;
    }
    footer {
      display: flex;
      justify-content: space-between;
      gap: 18px;
      color: var(--muted);
      font-size: 13px;
      border-top: 1px solid var(--line);
      padding-top: 12px;
    }
    @media print {
      body { background: white; }
      .poster { width: 100%; min-height: auto; padding: 18px; }
      section { box-shadow: none; break-inside: avoid; }
    }
    @media (max-width: 1000px) {
      main { grid-template-columns: 1fr; }
      .visuals { grid-template-columns: 1fr; }
    }
  </style>
</head>
<body>
  <div class="poster">
    <header>
      <h1>${escapeHtml(title)}</h1>
      <p>${escapeHtml(question)}</p>
    </header>
    <main>
      <div class="left">
        <section>
          <h2>Background</h2>
          <p>${escapeHtml(context)}</p>
          <div class="callout">
            <strong>GATOR project context</strong>
            <p>Generated ${escapeHtml(new Date().toLocaleString())} from selected workspace runs and artifacts.</p>
          </div>
        </section>
        <section>
          <h2>Datasets</h2>
          <ul>${datasetItems}</ul>
        </section>
        <section>
          <h2>Methods</h2>
          <p>${escapeHtml(getRunMethods(runHistory))}</p>
          <ul>${methodItems}</ul>
        </section>
      </div>
      <section>
        <h2>Visual Results</h2>
        <div class="visuals">${visualPanels}</div>
      </section>
      <div class="right">
        <section>
          <h2>Key Findings</h2>
          <p>${escapeHtml(posterSummary?.keyFindings || "Use this section to summarize the biological pattern supported by the selected visual results. Treat AI-generated interpretations as exploratory and verify findings against study design, metadata, and statistical outputs.")}</p>
        </section>
        <section>
          <h2>Visual Interpretation</h2>
          <p>${escapeHtml(posterSummary?.visualInterpretation || "Generate an AI poster summary in GATOR to add neutral interpretation of selected heatmaps, PCA plots, volcano plots, and predictor outputs.")}</p>
        </section>
        <section>
          <h2>Result Files</h2>
          <ul>${artifactItems}</ul>
        </section>
        <section>
          <h2>Limitations</h2>
          <p>${escapeHtml(posterSummary?.limitations || "Public sequencing data may have batch effects, incomplete metadata, heterogeneous protocols, and study-specific confounders. Results should be interpreted as research evidence, not clinical or diagnostic conclusions.")}</p>
        </section>
        <section>
          <h2>Next Steps</h2>
          <p>${escapeHtml(posterSummary?.nextSteps || "Review imported dataset metadata, confirm sample groups, compare methods across tools, and use selected artifacts to guide follow-up hypotheses.")}</p>
        </section>
      </div>
    </main>
    <footer>
      <span>Generated by GATOR: Genomic Analysis Toward Official Research</span>
      <span>Project report companion: preserve input files, parameters, tables, and run IDs for reproducibility.</span>
    </footer>
  </div>
</body>
</html>`;
}

function buildLocalPosterSummary(project, runs, artifacts) {
  const toolNames = [...new Set((runs || []).map((run) => run.toolName).filter(Boolean))];
  const visualNames = (artifacts || [])
    .filter((artifact) => /plot|heatmap|pca|volcano|figure|bar/i.test(`${artifact.type || ""} ${artifact.title || ""}`))
    .map((artifact) => artifact.title)
    .filter(Boolean);

  return {
    keyFindings: `Selected GATOR analyses${toolNames.length ? ` (${toolNames.join(", ")})` : ""} produced report-ready results for ${project?.name || "this project"}. Use the selected visual and tabular artifacts as exploratory evidence for the project question.`,
    visualInterpretation: visualNames.length
      ? `The selected visuals (${visualNames.join(", ")}) should be reviewed for clustering, separation, batch structure, expression-pattern changes, and high-ranking genes. Interpret these patterns in the context of the project metadata and dataset design.`
      : "Review selected visual artifacts for clustering, separation, batch structure, expression-pattern changes, and high-ranking genes.",
    limitations: "Interpret findings cautiously. Sequencing data may include batch effects, incomplete metadata, heterogeneous protocols, and study-specific confounders. Results are research evidence, not clinical or diagnostic conclusions.",
    nextSteps: "Confirm sample groups and metadata, inspect data quality, compare findings across GATOR tools, and use selected artifacts to guide follow-up hypotheses.",
    localFallback: true,
  };
}

function buildProjectReport({ project, datasets, runHistory, artifacts }) {
  const lines = [
    `# GATOR Project Report: ${project?.name || "Untitled Project"}`,
    "",
    `Generated: ${new Date().toLocaleString()}`,
    "",
    "## Project Context",
    "",
    `- Project name: ${project?.name || "Not provided"}`,
    `- Research question: ${project?.question || "Not provided"}`,
    `- Dataset/context note: ${project?.context || "Not provided"}`,
    "",
    "## Dataset Library",
    "",
  ];

  if (!datasets.length) {
    lines.push("No datasets have been registered for this project.", "");
  } else {
    datasets.forEach((dataset, index) => {
      lines.push(
        `### ${index + 1}. ${dataset.name || "Untitled dataset"}`,
        "",
        `- Source: ${dataset.source || "Not provided"}`,
        `- Dataset kind: ${dataset.datasetKind || "dataset-lead"}`,
        `- Review status: ${dataset.reviewStatus || "Not provided"}`,
        `- Context: ${dataset.context || "Not provided"}`,
        `- Source URL: ${dataset.sourceUrl || "Not provided"}`,
        `- Storage path: ${dataset.storagePath || "Not imported into GATOR Storage"}`,
        `- Notes: ${dataset.notes || "Not provided"}`,
        ""
      );
    });
  }

  lines.push("## Analysis Run History", "");

  if (!runHistory.length) {
    lines.push("No analysis runs are attached to this project yet.", "");
  } else {
    runHistory.forEach((run, index) => {
      lines.push(
        `### ${index + 1}. ${run.toolName || "Analysis run"}`,
        "",
        `- Status: ${run.status || "unknown"}`,
        `- Timestamp: ${formatRunTime(run)}`,
        `- Run ID: ${run.id}`,
        `- Firestore collection: ${run.collectionName}`,
        `- Input source: ${run.inputSourceLabel || "Not recorded for this run"}`,
        ""
      );
    });
  }

  lines.push("## Artifact Library", "");

  if (!artifacts.length) {
    lines.push("No artifacts have been registered for this project yet.", "");
  } else {
    artifacts.forEach((artifact, index) => {
      lines.push(
        `### ${index + 1}. ${artifact.title || "Untitled artifact"}`,
        "",
        `- Type: ${artifact.type || "Not provided"}`,
        `- Related run: ${artifact.runId || "Not linked"}`,
        `- Auto-captured: ${artifact.autoCaptured ? "Yes" : "No"}`,
        `- Run collection: ${artifact.relatedRunCollection || "Not provided"}`,
        `- URL/path: ${artifact.urlOrPath || "Not provided"}`,
        `- Notes: ${artifact.notes || "Not provided"}`,
        ""
      );
    });
  }

  lines.push(
    "## Interpretation Notes",
    "",
    "This report summarizes workspace metadata and analysis run records from GATOR. AI-generated interpretations should be treated as exploratory guidance, not as clinical, diagnostic, or definitive biological conclusions.",
    "",
    "## Reproducibility Notes",
    "",
    "For complete reproducibility, preserve the uploaded input files, analysis parameters, generated result tables, plots, and any downstream notes associated with each run.",
    ""
  );

  return lines.join("\n");
}

function downloadTextFile(filename, text, mimeType = "text/markdown;charset=utf-8") {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Initialize functions
const functions = getFunctions(app);

async function prepareImportedInputForAnalysis({ importedInput, runId, targetTool, role }) {
  const prepareInput = httpsCallable(getFunctions(app, "us-central1"), "prepareImportedAnalysisInput");
  const response = await prepareInput({
    sourcePath: importedInput.storagePath,
    sourceFileName: importedInput.name,
    runId,
    targetTool,
    role,
  });
  return response.data;
}

function ImportedInputNotice({ importedInput, importedCountsInput, importedMetadataInput, onClear }) {
  const selected = [importedInput, importedCountsInput, importedMetadataInput].filter(Boolean);
  if (!selected.length) return null;

  return (
    <div
      style={{
        border: "1px solid rgba(130,205,120,0.45)",
        background: "rgba(52, 96, 58, 0.22)",
        borderRadius: 8,
        padding: 12,
        margin: "12px 0",
        display: "grid",
        gap: 6,
      }}
    >
      <strong>Using imported project file</strong>
      {selected.map((input) => (
        <p key={`${input.id || input.storagePath}-${input.name}`} style={{ margin: 0, color: "rgba(255,255,255,0.78)" }}>
          {input.role ? `${input.role}: ` : ""}
          {input.name}
        </p>
      ))}
      <p style={{ margin: 0, color: "rgba(255,255,255,0.58)", fontSize: 13 }}>
        GATOR will stage this file from Firebase Storage when you run the analysis.
      </p>
      {onClear ? (
        <button type="button" onClick={onClear} style={{ width: "fit-content", marginTop: 4 }}>
          Clear imported file selection
        </button>
      ) : null}
    </div>
  );
}

function HeatmapGenerator({ setError, setIsProcessing, isProcessing, user, error, projectRunMetadata, importedInput, clearImportedInput }) {
  const [heatmapData, setHeatmapData] = useState(null);   // Plotly trace
  const [heatmapJson, setHeatmapJson] = useState(null);   // raw JSON from Storage
  const [scaleMode, setScaleMode] = useState("log2");     // "log2" or "zscore"
  const [file, setFile] = useState(null);
  const [timestamp, setTimestamp] = useState(null);
  const [processingTriggered, setProcessingTriggered] = useState(false);
  const [heatmapExplanation, setHeatmapExplanation] = useState(null);
  const [isInterpretingHeatmap, setIsInterpretingHeatmap] = useState(false);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleRun = async () => {
    if (!file && !importedInput?.storagePath) {
      setError("Please select a file to upload or choose an imported project file.");
      return;
    }

    if (!user) {
      setError("You must be signed in to upload files.");
      return;
    }

    const newTimestamp = Date.now().toString();
    setTimestamp(newTimestamp);
    setIsProcessing(true);

    try {
      await setDoc(doc(collection(db, "heatmapData"), newTimestamp), {
        status: "pending",
        timestamp: newTimestamp,
        ...(importedInput ? getImportedInputSource([importedInput]) : getUploadedInputSource([file])),
        ...projectRunMetadata,
      });

      if (importedInput) {
        await prepareImportedInputForAnalysis({
          importedInput,
          runId: newTimestamp,
          targetTool: "heatmap",
          role: "counts",
        });
      } else {
        const storage = getStorage(app);
        const fileRef = storageRef(storage, `rnaseqData/${newTimestamp}_${file.name}`);
        console.log("Uploading file to rnaseqData:", fileRef.fullPath);
        await uploadBytes(fileRef, file);
      }

      setProcessingTriggered(true);
      setError(null);
    } catch (err) {
      console.error("Heatmap upload error:", err);
      setError("Failed to upload file: " + err.message);
      setIsProcessing(false);
    }
  };

  const reset = () => {
    setFile(null);
    clearImportedInput?.();
    setHeatmapData(null);
    setHeatmapJson(null);
    setScaleMode("log2");
    setTimestamp(null);
    setProcessingTriggered(false);
    setHeatmapExplanation(null);
    setIsInterpretingHeatmap(false);
    setError(null);
    setIsProcessing(false);
  };

  // Watch Firestore doc and load JSON from Storage when ready
  useEffect(() => {
    if (!timestamp || !processingTriggered) return;

    const timeoutId = setTimeout(() => {
      setError("Processing timed out. Please try again or use a smaller file.");
      setIsProcessing(false);
      setProcessingTriggered(false);
    }, 300000); // 5 minutes

    const docRef = doc(db, "heatmapData", timestamp);
    const unsubscribe = onSnapshot(
      docRef,
      async (snap) => {
        if (!snap.exists()) return;

        const data = snap.data();

        if (data.status === "completed") {
          try {
            let json = data.resultJsonText ? JSON.parse(data.resultJsonText) : data.resultJson;

            if (!json) {
              const resultPath = data.resultPath;
              if (!resultPath) {
                throw new Error("Missing heatmap result data.");
              }

              const storage = getStorage(app);
              const fileRef = storageRef(storage, resultPath);
              const bytes = await getBytes(fileRef);
              json = JSON.parse(new TextDecoder("utf-8").decode(bytes));
            }

            // store raw JSON; another effect builds the Plotly trace
            setHeatmapJson(json);
          } catch (e) {
            console.error("Failed to load heatmap JSON:", e);
            setError("Failed to load heatmap results: " + e.message);
          } finally {
            setIsProcessing(false);
            setProcessingTriggered(false);
            clearTimeout(timeoutId);
          }
        } else if (data.status === "failed") {
          setError("Processing failed: " + (data.error || "Unknown error"));
          setIsProcessing(false);
          setProcessingTriggered(false);
          clearTimeout(timeoutId);
        } else {
          console.log("Waiting for Cloud Function to process the heatmap file...");
        }
      },
      (err) => {
        console.error("Error fetching Firestore data:", err);
        setError("Failed to fetch heatmap data from Firestore: " + err.message);
        setIsProcessing(false);
        setProcessingTriggered(false);
        clearTimeout(timeoutId);
      }
    );

    return () => {
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [timestamp, processingTriggered, setError, setIsProcessing]);

  // Build Plotly trace from JSON + current scaleMode
  useEffect(() => {
    if (!heatmapJson) {
      setHeatmapData(null);
      return;
    }

    try {
      let z, zmin, zmax;

      if (scaleMode === "log2") {
        // new backend uses z_log2; fall back to z for older results
        z = heatmapJson.z_log2 ?? heatmapJson.z;
        zmin = heatmapJson.zmin_log2 ?? heatmapJson.zmin ?? 0;
        zmax = heatmapJson.zmax_log2 ?? heatmapJson.zmax ?? 10;
      } else {
        // z-score matrix
        z = heatmapJson.z_zscore ?? heatmapJson.z;
        zmin = heatmapJson.zmin_z ?? -3;
        zmax = heatmapJson.zmax_z ?? 3;
      }

      // We expect a proper 2D matrix: [row][col]
      if (
        !Array.isArray(z) ||
        !z.length ||
        !Array.isArray(z[0]) ||
        !z[0].length
      ) {
        throw new Error("Heatmap matrix is empty or has the wrong shape.");
      }

      const trace = {
        z,
        x: heatmapJson.x,
        y: heatmapJson.y,
        type: "heatmap",
        colorscale: heatmapJson.colorscale || "Viridis",
        zmin,
        zmax,
      };

      setHeatmapData(trace);
    } catch (e) {
      console.error("Error building heatmap trace from JSON:", heatmapJson, e);
      setHeatmapData(null);
      setError(
        "Heatmap results were loaded but had an unexpected format: " + e.message
      );
    }
  }, [heatmapJson, scaleMode, setError]);

  const handleInterpretHeatmap = async () => {
    if (!heatmapJson?.sampleClusters?.length) {
      setError("No heatmap sample clusters are available to interpret. Run a new heatmap after deploying the clustering update.");
      return;
    }

    setIsInterpretingHeatmap(true);
    setError(null);

    try {
      const interpretFn = httpsCallable(getFunctions(app, "us-central1"), "interpretHeatmap");
      const contextParts = [
        projectRunMetadata?.projectName ? `Project: ${projectRunMetadata.projectName}` : "",
        projectRunMetadata?.researchQuestion ? `Research question: ${projectRunMetadata.researchQuestion}` : "",
        projectRunMetadata?.projectContext ? `Context: ${projectRunMetadata.projectContext}` : "",
        heatmapJson?.selectionMethod ? `Selection: ${heatmapJson.selectionMethod}` : "",
      ].filter(Boolean);

      const res = await interpretFn({
        summaryText: contextParts.join("\n"),
        sampleClusters: heatmapJson.sampleClusters,
        displayedGenes: heatmapJson.displayedGenes,
        totalGenes: heatmapJson.totalGenes,
        clusteringMethod: heatmapJson.clusteringMethod,
      });

      setHeatmapExplanation(res.data.explanation);
    } catch (err) {
      console.error("interpretHeatmap failed:", err);
      setError("AI heatmap interpretation failed: " + (err.message || err));
    } finally {
      setIsInterpretingHeatmap(false);
    }
  };

  const heatmapClusterOverlays = buildHeatmapClusterOverlays(heatmapJson);

  return (
    <div style={{ flex: 1, padding: "20px", borderRight: "1px solid #ccc" }}>
      <h2>Heatmap Generator</h2>

      {heatmapData ? (
        <>
          {heatmapJson?.displayedGenes && heatmapJson?.totalGenes ? (
            <p style={{ color: "rgba(255,255,255,0.72)", marginBottom: "10px" }}>
              Displaying {heatmapJson.displayedGenes} of {heatmapJson.totalGenes} genes
              {heatmapJson.selectionMethod ? ` (${heatmapJson.selectionMethod}).` : "."}
            </p>
          ) : null}
          {heatmapJson?.clusteringMethod ? (
            <p style={{ color: "rgba(255,255,255,0.62)", marginBottom: "10px" }}>
              {heatmapJson.clusteringMethod}. Cluster summaries are exploratory and should be checked against sample metadata.
            </p>
          ) : null}
          <div style={{ marginBottom: "10px" }}>
            <label>
              Scale:&nbsp;
              <select
                value={scaleMode}
                onChange={(e) => setScaleMode(e.target.value)}
              >
                <option value="log2">log₂(FPKM + 1)</option>
                <option value="zscore">Row-wise z-score</option>
              </select>
            </label>
          </div>

          {/* Wrap in a scroll container so wide heatmaps are usable */}
<div style={{ overflowX: "auto" }}>
  <div style={{ width: Math.max(900, (heatmapJson?.x?.length || 0) * 18) }}>
    <Plot
      data={[heatmapData]}
      layout={{
        title: "Unsupervised Hierarchical Heatmap of RNAseq FPKM",

        // Scale width with number of samples
        width: Math.max(900, (heatmapJson?.x?.length || 0) * 18),
        height: 1000,
        shapes: heatmapClusterOverlays.shapes,
        annotations: heatmapClusterOverlays.annotations,

        xaxis: {
          title: "Samples",

          // Force all sample labels
          tickmode: "array",
          tickvals: heatmapJson?.x || [],
          ticktext: heatmapJson?.x || [],

          tickangle: -90,
          tickfont: { size: 8 },
          automargin: true,
        },

        yaxis: {
          title: "Genes",
          tickfont: { size: 8 },
          automargin: true,
          tickmode: "auto",
          nticks: 20,
          ticks: "outside",
          showticklabels: true,
        },

        margin: { l: 150, r: 50, t: heatmapClusterOverlays.shapes.length ? 170 : 100, b: 220 },
        showlegend: false,
      }}
      config={{ responsive: true, displaylogo: false }}
    />
  </div>
</div>

          {heatmapJson?.sampleClusters?.length ? (
            <div style={{ marginTop: "20px", display: "grid", gap: "12px" }}>
              <h3>Hierarchical Sample Clusters</h3>
              <p style={{ color: "rgba(255,255,255,0.66)", marginBottom: 0 }}>
                These groups are inferred from adjacent samples in the hierarchical heatmap order using the selected variable genes.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "12px" }}>
                {heatmapJson.sampleClusters.map((cluster) => (
                  <div
                    key={cluster.id || cluster.label}
                    style={{
                      border: "1px solid rgba(130,205,120,0.35)",
                      borderRadius: 8,
                      background: "rgba(22, 32, 22, 0.45)",
                      padding: 12,
                    }}
                  >
                    <h4 style={{ margin: "0 0 8px" }}>
                      {cluster.label || "Sample cluster"} ({cluster.size || cluster.samples?.length || 0} samples)
                    </h4>
                    <p style={{ color: "rgba(255,255,255,0.72)", marginTop: 0 }}>
                      {(cluster.samples || []).slice(0, 12).join(", ")}
                      {(cluster.samples || []).length > 12 ? "..." : ""}
                    </p>
                    <p style={{ color: "rgba(255,255,255,0.62)" }}>{cluster.pattern}</p>
                    {cluster.highGenes?.length ? (
                      <p style={{ color: "rgba(255,255,255,0.72)" }}>
                        <strong>Relatively higher genes:</strong>{" "}
                        {cluster.highGenes.map((gene) => `${gene.gene} (${gene.score})`).join(", ")}
                      </p>
                    ) : null}
                    {cluster.lowGenes?.length ? (
                      <p style={{ color: "rgba(255,255,255,0.72)" }}>
                        <strong>Relatively lower genes:</strong>{" "}
                        {cluster.lowGenes.map((gene) => `${gene.gene} (${gene.score})`).join(", ")}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div style={{ marginTop: "20px" }}>
            <button
              onClick={handleInterpretHeatmap}
              disabled={isInterpretingHeatmap || !heatmapJson?.sampleClusters?.length}
              style={{ padding: "5px 10px", marginRight: "10px" }}
            >
              {isInterpretingHeatmap ? "Getting AI interpretation..." : "AI Interpret Heatmap"}
            </button>
          </div>

          {heatmapExplanation && (
            <div
              style={{
                marginTop: "15px",
                padding: "16px",
                border: "1px solid #3a4553",
                borderRadius: "8px",
                backgroundColor: "#151a20",
                color: "#f4f7fb",
                maxHeight: "300px",
                overflowY: "auto",
                whiteSpace: "pre-wrap",
                lineHeight: 1.55,
              }}
            >
              <h3 style={{ marginTop: 0 }}>AI Heatmap Interpretation</h3>
              <p style={{ marginBottom: 0 }}>{heatmapExplanation}</p>
            </div>
          )}

          <button onClick={reset} style={{ marginTop: "20px" }}>
            Upload Another File
          </button>
        </>        
      ) : (
        <>
          <div style={{ marginTop: "20px", marginBottom: "20px" }}>
  <p style={{ marginBottom: "8px", fontWeight: "bold" }}>
    Example counts CSV format
  </p>
  <img
    src="/combatseq_counts_example.png"
    alt="Example counts CSV format"
    style={{
      width: "100%",
      maxWidth: "260px",
      border: "1px solid #444",
      borderRadius: "6px",
      background: "white",
    }}
  />
  <p style={{ fontSize: "0.9rem", marginTop: "8px", color: "#ccc" }}>
    First column should be <code>gene</code>. Each remaining column should be a
    sample name.
  </p>
</div>

            <div>
            <label htmlFor="heatmap-file-input">
              Upload RNAseq FPKM CSV File:
            </label>
            <input
              id="heatmap-file-input"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={!!importedInput}
              style={{ marginBottom: "20px", marginLeft: "10px" }}
            />
          </div>
          <ImportedInputNotice importedInput={importedInput} onClear={clearImportedInput} />
          <p>
            {importedInput
              ? `Selected imported file: ${importedInput.name}`
              : file
              ? `Selected file: ${file.name}`
              : "Upload an RNAseq FPKM CSV file to generate a heatmap."}
          </p>
          {(file || importedInput) && (
            <button
              onClick={handleRun}
              disabled={isProcessing || !user}
              style={{ marginTop: "10px" }}
            >
              {isProcessing ? "Processing..." : "Run"}
            </button>
          )}
          {isProcessing && <p>Processing your file, please wait...</p>}
          {error && <p style={{ color: "red" }}>{error}</p>}
        </>
      )}
    </div>
  );
}

function ScRNAClusterExplorer({ setError, setIsProcessing, isProcessing, user, error, projectRunMetadata }) {
  const [file, setFile] = useState(null);
  const [docId, setDocId] = useState(null);
  const [processingTriggered, setProcessingTriggered] = useState(false);
  const [scrnaData, setScrnaData] = useState(null);
  const [plotUrls, setPlotUrls] = useState({});
  const [params, setParams] = useState({
    species: "human",
    knownTissueType: "",
    qcMaxPercentMt: "10",
    resolution: "0.5",
    dims: "10",
  });

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const updateParam = (key, value) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  };

  const handleRun = async () => {
    if (!file) {
      setError("Please select a 10x Genomics .h5 file.");
      return;
    }

    if (!user) {
      setError("You must be signed in to run scRNA analysis.");
      return;
    }

    setIsProcessing(true);
    setPlotUrls({});
    setScrnaData(null);

    try {
      const docRefNew = await addDoc(collection(db, "scrnaData"), {
        status: "pending",
        createdAt: new Date(),
        ...getUploadedInputSource([file]),
        ...projectRunMetadata,
        runnerParams: {
          species: params.species,
          knownTissueType: params.knownTissueType.trim(),
          qcMaxPercentMt: Number(params.qcMaxPercentMt) || 10,
          resolution: Number(params.resolution) || 0.5,
          dims: Number(params.dims) || 10,
        },
      });

      const newDocId = docRefNew.id;
      setDocId(newDocId);

      const storage = getStorage(app, "gs://gator-7bf72.firebasestorage.app");
      const inputPath = `scrnaInput/${newDocId}_${file.name.replace(/[^A-Za-z0-9._-]+/g, "_")}`;
      await uploadBytes(storageRef(storage, inputPath), file);

      const startScRNA = httpsCallable(getFunctions(app, "us-central1"), "startScRNA");
      await startScRNA({
        docId: newDocId,
        inputPath,
        params: {
          species: params.species,
          knownTissueType: params.knownTissueType.trim(),
          qcMaxPercentMt: Number(params.qcMaxPercentMt) || 10,
          resolution: Number(params.resolution) || 0.5,
          dims: Number(params.dims) || 10,
        },
      });

      setProcessingTriggered(true);
      setError(null);
    } catch (err) {
      console.error("scRNA start failed:", err);
      setError("Failed to start scRNA analysis: " + (err.message || err));
      setIsProcessing(false);
    }
  };

  const reset = () => {
    setFile(null);
    setDocId(null);
    setProcessingTriggered(false);
    setScrnaData(null);
    setPlotUrls({});
    setError(null);
    setIsProcessing(false);
  };

  useEffect(() => {
    if (!docId || !processingTriggered) return;

    const timeoutId = setTimeout(() => {
      setError("scRNA processing timed out. For larger datasets, try a smaller filtered matrix or fewer dimensions.");
      setIsProcessing(false);
      setProcessingTriggered(false);
    }, 1800000);

    const docRef_ = doc(db, "scrnaData", docId);
    const unsubscribe = onSnapshot(
      docRef_,
      async (snap) => {
        if (!snap.exists()) return;

        const data = snap.data();
        if (data.status === "completed") {
          setScrnaData(data);
          const paths = data.plotPaths || {};
          const storage = getStorage(app, "gs://gator-7bf72.firebasestorage.app");
          const entries = await Promise.all(
            Object.entries(paths).map(async ([key, path]) => {
              try {
                const url = await getDownloadURL(storageRef(storage, path));
                return [key, url];
              } catch (e) {
                console.warn("Could not load scRNA plot URL:", key, e);
                return [key, null];
              }
            })
          );
          setPlotUrls(Object.fromEntries(entries.filter(([, url]) => !!url)));
          setIsProcessing(false);
          setProcessingTriggered(false);
          clearTimeout(timeoutId);
        } else if (data.status === "failed") {
          setError("scRNA processing failed: " + (data.error || "Unknown error"));
          setIsProcessing(false);
          setProcessingTriggered(false);
          clearTimeout(timeoutId);
        }
      },
      (err) => {
        console.error("Error fetching scRNA data:", err);
        setError("Failed to fetch scRNA data from Firestore: " + err.message);
        setIsProcessing(false);
        setProcessingTriggered(false);
        clearTimeout(timeoutId);
      }
    );

    return () => {
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [docId, processingTriggered, setError, setIsProcessing]);

  const resultLinks = [
    ["Cluster annotations", scrnaData?.clusterAnnotationsPath],
    ["Cluster QC summary", scrnaData?.clusterQcSummaryPath],
    ["UMAP cell table", scrnaData?.umapCellsPath],
    ["Marker genes", scrnaData?.markersPath],
    ["Full result package", scrnaData?.resultsZipPath],
  ].filter(([, path]) => !!path);

  return (
    <div style={{ flex: 1, padding: "20px", borderRight: "1px solid #ccc" }}>
      <h2>scRNA Cluster Explorer</h2>
      <p style={{ color: "rgba(255,255,255,0.72)", maxWidth: 860 }}>
        Upload a 10x Genomics filtered feature-barcode matrix .h5 file to run QC,
        normalization, clustering, UMAP visualization, marker detection, and ScType
        cell-type labeling.
      </p>

      {!scrnaData ? (
        <>
          <div style={{ display: "grid", gap: 12, maxWidth: 720, marginTop: 18 }}>
            <label>
              Species:&nbsp;
              <select value={params.species} onChange={(e) => updateParam("species", e.target.value)}>
                <option value="human">Human</option>
                <option value="mouse">Mouse</option>
              </select>
            </label>

            <label>
              Tissue hint for cell typing:&nbsp;
              <input
                value={params.knownTissueType}
                onChange={(e) => updateParam("knownTissueType", e.target.value)}
                placeholder="Optional: Immune system, blood, brain, tumor, etc."
                style={{ width: "100%", maxWidth: 420 }}
              />
            </label>

            <label>
              Max mitochondrial percent:&nbsp;
              <input
                type="number"
                min="1"
                max="50"
                step="1"
                value={params.qcMaxPercentMt}
                onChange={(e) => updateParam("qcMaxPercentMt", e.target.value)}
              />
            </label>

            <label>
              Clustering resolution:&nbsp;
              <input
                type="number"
                min="0.1"
                max="2"
                step="0.1"
                value={params.resolution}
                onChange={(e) => updateParam("resolution", e.target.value)}
              />
            </label>

            <label>
              PCA dimensions for clustering:&nbsp;
              <input
                type="number"
                min="2"
                max="50"
                step="1"
                value={params.dims}
                onChange={(e) => updateParam("dims", e.target.value)}
              />
            </label>

            <label htmlFor="scrna-h5-file-input">
              Upload 10x .h5 file:&nbsp;
              <input
                id="scrna-h5-file-input"
                type="file"
                accept=".h5"
                onChange={handleFileChange}
              />
            </label>
          </div>

          <p style={{ marginTop: 18 }}>
            {file ? `Selected file: ${file.name}` : "Select a filtered_feature_bc_matrix.h5 file to begin."}
          </p>

          {file ? (
            <button onClick={handleRun} disabled={isProcessing || !user}>
              {isProcessing ? "Processing..." : "Run scRNA clustering"}
            </button>
          ) : null}

          {isProcessing ? <p>Running scRNA workflow. This can take several minutes.</p> : null}
          {error ? <p style={{ color: "red" }}>{error}</p> : null}
        </>
      ) : (
        <div style={{ display: "grid", gap: 18 }}>
          <div
            style={{
              border: "1px solid rgba(130,205,120,0.45)",
              background: "rgba(52, 96, 58, 0.18)",
              borderRadius: 8,
              padding: 14,
            }}
          >
            <strong>scRNA analysis complete</strong>
            <p style={{ marginBottom: 0 }}>
              Tissue type used: {scrnaData.tissueTypeUsed || "Not recorded"}.
              Results are saved to this project run and can be used for reports.
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
            {plotUrls.umap_by_cluster ? (
              <div>
                <h3>UMAP by Cluster</h3>
                <img src={plotUrls.umap_by_cluster} alt="UMAP by cluster" style={{ width: "100%", background: "#fff", borderRadius: 8 }} />
              </div>
            ) : null}
            {plotUrls.umap_by_sctype ? (
              <div>
                <h3>UMAP by Cell Type</h3>
                <img src={plotUrls.umap_by_sctype} alt="UMAP by cell type" style={{ width: "100%", background: "#fff", borderRadius: 8 }} />
              </div>
            ) : null}
            {plotUrls.qc_violin_post ? (
              <div>
                <h3>Post-QC Metrics</h3>
                <img src={plotUrls.qc_violin_post} alt="Post-QC violin metrics" style={{ width: "100%", background: "#fff", borderRadius: 8 }} />
              </div>
            ) : null}
          </div>

          {resultLinks.length ? (
            <div>
              <h3>Result Files</h3>
              <ul>
                {resultLinks.map(([label, path]) => (
                  <li key={path}>
                    <span>{label}: </span>
                    <code>{path}</code>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <button onClick={reset} style={{ width: "fit-content" }}>
            Run another scRNA file
          </button>
        </div>
      )}
    </div>
  );
}


function DESeq2Analysis({
  setError,
  setIsProcessing,
  isProcessing,
  user,
  error,
  projectRunMetadata,
  importedCountsInput,
  importedMetadataInput,
  clearImportedInputs,
}) {
  const [deseq2Data, setDeseq2Data] = useState(null);
  const [countsFile, setCountsFile] = useState(null);
  const [metadataFile, setMetadataFile] = useState(null);
  const [processingTriggered, setProcessingTriggered] = useState(false);
  const [docId, setDocId] = useState(null);

  // Optional heatmap preview URL
  const [heatmapUrl, setHeatmapUrl] = useState(null);

  // AI interpretation state (kept but not emphasized for now)
  const [aiExplanation, setAiExplanation] = useState(null);
  const [isInterpreting, setIsInterpreting] = useState(false);

  const allResults =
    deseq2Data && Array.isArray(deseq2Data.results)
      ? deseq2Data.results
      : [];

  const significantResults =
    deseq2Data && Array.isArray(deseq2Data.significantResults)
      ? deseq2Data.significantResults
      : [];

  const strongHits =
    deseq2Data && Array.isArray(deseq2Data.strongHits)
      ? deseq2Data.strongHits
      : [];

  const summary =
    deseq2Data && deseq2Data.summary
      ? deseq2Data.summary
      : {
          totalTested: allResults.length,
          significantCount: significantResults.length,
          strongHitsCount: strongHits.length,
          upStrongHits: strongHits.filter((r) => Number(r.log2FoldChange) >= 1).length,
          downStrongHits: strongHits.filter((r) => Number(r.log2FoldChange) <= -1).length,
          contrastTestLevel: null,
          contrastReferenceLevel: null,
        };

  const handleCountsFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setCountsFile(selectedFile);
      setError(null);
    }
  };

  const handleMetadataFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setMetadataFile(selectedFile);
      setError(null);
    }
  };

  const handleRun = async () => {
    if ((!countsFile && !importedCountsInput?.storagePath) || (!metadataFile && !importedMetadataInput?.storagePath)) {
      setError("Please select both a counts file and a metadata file.");
      return;
    }

    if (!user) {
      setError("You must be signed in to upload files.");
      return;
    }

    setIsProcessing(true);

    try {
      const docRefNew = await addDoc(collection(db, "deseq2Data"), {
        status: "pending",
        createdAt: new Date(),
        ...getCombinedInputSource([countsFile, metadataFile], [importedCountsInput, importedMetadataInput]),
        ...projectRunMetadata,
      });

      const newDocId = docRefNew.id;
      setDocId(newDocId);

      let countsPath = `deseq2Data/${newDocId}_counts.csv`;
      let metadataPath = `deseq2Data/${newDocId}_metadata.csv`;

      const storage = getStorage(app, "gs://gator-7bf72.firebasestorage.app");

      if (importedCountsInput) {
        const preparedCounts = await prepareImportedInputForAnalysis({
          importedInput: importedCountsInput,
          runId: newDocId,
          targetTool: "deseq2",
          role: "counts",
        });
        countsPath = preparedCounts.destinationPath;
      } else {
        await uploadBytes(storageRef(storage, countsPath), countsFile);
      }

      if (importedMetadataInput) {
        const preparedMetadata = await prepareImportedInputForAnalysis({
          importedInput: importedMetadataInput,
          runId: newDocId,
          targetTool: "deseq2",
          role: "metadata",
        });
        metadataPath = preparedMetadata.destinationPath;
      } else {
        await uploadBytes(storageRef(storage, metadataPath), metadataFile);
      }

      const start = httpsCallable(getFunctions(app, "us-central1"), "startDESeq2");
      await start({ docId: newDocId, countsPath, metadataPath });

      setProcessingTriggered(true);
      setError(null);
    } catch (err) {
      console.error("DESeq2 start failed:", err);
      setError("Failed to start analysis: " + (err.message || err));
      setIsProcessing(false);
    }
  };

  const reset = () => {
    setCountsFile(null);
    setMetadataFile(null);
    clearImportedInputs?.();
    setDeseq2Data(null);
    setDocId(null);
    setProcessingTriggered(false);
    setHeatmapUrl(null);
    setAiExplanation(null);
    setError(null);
    setIsProcessing(false);
  };

  useEffect(() => {
    if (!docId || !processingTriggered) return;

    const timeoutId = setTimeout(() => {
      setError("Processing timed out. Please try again or use a smaller file.");
      setIsProcessing(false);
      setProcessingTriggered(false);
    }, 1200000); // 20 min

    const docRef_ = doc(db, "deseq2Data", docId);

    const unsubscribe = onSnapshot(
      docRef_,
      async (snap) => {
        if (!snap.exists()) return;

        const data = snap.data();
        if (data?.status) console.log("DESeq2 status:", data.status);

        if (data.status === "completed") {
          const finish = () => {
            setIsProcessing(false);
            setProcessingTriggered(false);
            clearTimeout(timeoutId);
          };

          const safeData = {
            results: Array.isArray(data.results) ? data.results : [],
            significantResults: Array.isArray(data.significantResults)
              ? data.significantResults
              : [],
            strongHits: Array.isArray(data.strongHits) ? data.strongHits : [],
            summary: data.summary || null,
            allResultsPath: data.allResultsPath || null,
            significantResultsPath: data.significantResultsPath || null,
            strongHitsPath: data.strongHitsPath || null,
            heatmapPath: data.heatmapPath || null,
          };

          setDeseq2Data(safeData);

          // Load heatmap preview URL if available
          if (safeData.heatmapPath) {
            try {
              const storage = getStorage(app, "gs://gator-7bf72.firebasestorage.app");
              const url = await getDownloadURL(storageRef(storage, safeData.heatmapPath));
              setHeatmapUrl(url);
            } catch (e) {
              console.warn("Could not load heatmap preview URL:", e);
              setHeatmapUrl(null);
            }
          } else {
            setHeatmapUrl(null);
          }

          finish();
        } else if (data.status === "failed") {
          setError("Processing failed: " + (data.error || "Unknown error"));
          setIsProcessing(false);
          setProcessingTriggered(false);
          clearTimeout(timeoutId);
        } else {
          console.log("Waiting for Cloud Function / runner to process the DESeq2 files...");
        }
      },
      (err) => {
        console.error("Error fetching Firestore data:", err);
        setError("Failed to fetch DESeq2 data from Firestore: " + err.message);
        setIsProcessing(false);
        setProcessingTriggered(false);
        clearTimeout(timeoutId);
      }
    );

    return () => {
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [docId, processingTriggered, setError, setIsProcessing]);

  const downloadRowsAsCsv = (rows, filename) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      alert("No rows available to download.");
      return;
    }

    const header = ["gene", "log2FoldChange", "pvalue", "padj"];

    const csvLines = [header, ...rows.map((row) => [
      row.gene ?? "",
      row.log2FoldChange ?? "",
      row.pvalue ?? "",
      row.padj ?? "",
    ])]
      .map((cols) =>
        cols
          .map((value) => {
            const v = value !== undefined && value !== null ? String(value) : "";
            const escaped = v.replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csvLines], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", filename);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadFromStoragePath = async (filePath, fallbackRows, fallbackFilename) => {
    try {
      if (filePath) {
        const storage = getStorage(app, "gs://gator-7bf72.firebasestorage.app");
        const url = await getDownloadURL(storageRef(storage, filePath));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fallbackFilename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      if (fallbackRows) {
        downloadRowsAsCsv(fallbackRows, fallbackFilename);
        return;
      }

      alert("No downloadable file is available.");
    } catch (err) {
      console.error("Download failed:", err);
      if (fallbackRows) {
        downloadRowsAsCsv(fallbackRows, fallbackFilename);
      } else {
        alert("Download failed.");
      }
    }
  };

  const handleDownloadAllResults = async () => {
    await downloadFromStoragePath(
      deseq2Data?.allResultsPath,
      allResults,
      docId ? `deseq2_all_results_${docId}.csv` : "deseq2_all_results.csv"
    );
  };

  const handleDownloadSignificantResults = async () => {
    await downloadFromStoragePath(
      deseq2Data?.significantResultsPath,
      significantResults,
      docId ? `deseq2_significant_results_${docId}.csv` : "deseq2_significant_results.csv"
    );
  };

  const handleDownloadStrongHits = async () => {
    await downloadFromStoragePath(
      deseq2Data?.strongHitsPath,
      strongHits,
      docId ? `deseq2_strong_hits_${docId}.csv` : "deseq2_strong_hits.csv"
    );
  };

  const handleDownloadHeatmap = async () => {
    if (!deseq2Data?.heatmapPath) {
      alert("No heatmap PNG is available for this analysis.");
      return;
    }

    try {
      const storage = getStorage(app, "gs://gator-7bf72.firebasestorage.app");
      const url = await getDownloadURL(storageRef(storage, deseq2Data.heatmapPath));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        docId ? `deseq2_significant_heatmap_${docId}.png` : "deseq2_significant_heatmap.png"
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
  console.error("Heatmap download failed:", {
    code: err?.code,
    message: err?.message,
    serverResponse: err?.customData?.serverResponse,
    fullError: err,
    heatmapPath: deseq2Data?.heatmapPath,
  });
  alert("Failed to download heatmap PNG.");
}
  };

  const handleInterpret = async () => {
    if (!allResults.length) {
      setError("No DESeq2 results available to interpret.");
      return;
    }

    setIsInterpreting(true);
    setError(null);

    try {
      const sig = significantResults.slice(0, 50);
      const topGenes = (sig.length > 0 ? sig : allResults.slice(0, 50)).map((row) => row.gene);

      const summaryText = `DESeq2 differential expression analysis with ${
        allResults.length
      } genes tested, ${summary.significantCount || 0} significant genes, and ${
        summary.strongHitsCount || 0
      } strong hits.`;

      const interpretFn = httpsCallable(getFunctions(app, "us-central1"), "interpretDeseq");
      const res = await interpretFn({ summaryText, topGenes });

      setAiExplanation(res.data.explanation);
    } catch (err) {
      console.error("interpretDeseq failed:", err);
      setError("AI interpretation failed: " + (err.message || err));
    } finally {
      setIsInterpreting(false);
    }
  };

  const volcanoX = allResults.map((row) => {
    const fc = Number(row.log2FoldChange);
    return Number.isFinite(fc) ? fc : 0;
  });

  const volcanoY = allResults.map((row) => {
    const padj = Number(row.padj);
    if (Number.isFinite(padj) && padj > 0) return -Math.log10(padj);
    return 0;
  });

  const volcanoText = allResults.map((row) => {
    const fc = Number(row.log2FoldChange);
    const padj = row.padj;
    return `${row.gene} | log2FC=${Number.isFinite(fc) ? fc.toFixed(3) : "NA"} | padj=${padj ?? "NA"}`;
  });

  const volcanoColors = allResults.map((row) => {
    const fc = Number(row.log2FoldChange);
    const padj = Number(row.padj);

    const isSig = Number.isFinite(padj) && padj <= 0.05;
    const isStrong = isSig && Number.isFinite(fc) && Math.abs(fc) >= 1;

    if (isStrong && fc >= 1) return "red";
    if (isStrong && fc <= -1) return "blue";
    if (isSig) return "orange";
    return "gray";
  });

  const significantTableRows = significantResults.length > 0 ? significantResults : [];

  return (
    <div style={{ flex: 1, padding: "20px" }}>
      <h2>DESeq2 Analysis</h2>

      {deseq2Data ? (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "12px",
              marginBottom: "20px",
              marginTop: "20px",
            }}
          >
            <div style={{ border: "1px solid #ccc", borderRadius: "8px", padding: "12px" }}>
              <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>Total Genes Tested</div>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                {summary.totalTested ?? allResults.length}
              </div>
            </div>

            <div style={{ border: "1px solid #ccc", borderRadius: "8px", padding: "12px" }}>
              <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>Significant Genes</div>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                {summary.significantCount ?? significantResults.length}
              </div>
              <div style={{ fontSize: "0.8rem", opacity: 0.7 }}>padj ≤ 0.05</div>
            </div>

            <div style={{ border: "1px solid #ccc", borderRadius: "8px", padding: "12px" }}>
              <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>Strong Hits</div>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                {summary.strongHitsCount ?? strongHits.length}
              </div>
              <div style={{ fontSize: "0.8rem", opacity: 0.7 }}>padj ≤ 0.05 and |log2FC| ≥ 1</div>
            </div>

            <div style={{ border: "1px solid #ccc", borderRadius: "8px", padding: "12px" }}>
              <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>Upregulated Strong Hits</div>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                {summary.upStrongHits ?? 0}
              </div>
            </div>

            <div style={{ border: "1px solid #ccc", borderRadius: "8px", padding: "12px" }}>
              <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>Downregulated Strong Hits</div>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                {summary.downStrongHits ?? 0}
              </div>
            </div>
          </div>

          {(summary.contrastTestLevel || summary.contrastReferenceLevel) && (
            <p style={{ marginBottom: "20px" }}>
              Contrast shown: <strong>{summary.contrastTestLevel ?? "Test"}</strong> vs{" "}
              <strong>{summary.contrastReferenceLevel ?? "Reference"}</strong>
            </p>
          )}

          <h3>Volcano Plot</h3>
          <Plot
            data={[
              {
                x: volcanoX,
                y: volcanoY,
                text: volcanoText,
                mode: "markers",
                type: "scattergl",
                marker: {
                  size: 6,
                  color: volcanoColors,
                  opacity: 0.8,
                },
                hovertemplate:
                  "<b>%{text}</b><extra></extra>",
              },
            ]}
            layout={{
              title: { text: "Volcano Plot (All Tested Genes)", x: 0.5 },
              width: 850,
              height: 600,
              margin: { l: 80, r: 30, t: 60, b: 80 },
              xaxis: {
                title: { text: "Log2 Fold Change" },
                zeroline: true,
                zerolinewidth: 1,
                zerolinecolor: "#444",
              },
              yaxis: {
                title: { text: "-log10(Adjusted P-Value)" },
                zeroline: true,
              },
              shapes: [
                {
                  type: "line",
                  x0: -1,
                  x1: -1,
                  y0: 0,
                  y1: Math.max(...volcanoY, 1),
                  line: { color: "blue", width: 1, dash: "dot" },
                },
                {
                  type: "line",
                  x0: 1,
                  x1: 1,
                  y0: 0,
                  y1: Math.max(...volcanoY, 1),
                  line: { color: "red", width: 1, dash: "dot" },
                },
                {
                  type: "line",
                  x0: Math.min(...volcanoX, -1),
                  x1: Math.max(...volcanoX, 1),
                  y0: -Math.log10(0.05),
                  y1: -Math.log10(0.05),
                  line: { color: "orange", width: 1, dash: "dot" },
                },
              ],
              annotations: [
                {
                  x: 1.02,
                  y: 1.02,
                  xref: "paper",
                  yref: "paper",
                  text: "Red = up strong hits | Blue = down strong hits | Orange = significant | Gray = not significant",
                  showarrow: false,
                  font: { size: 12 },
                  align: "left",
                },
              ],
              showlegend: false,
            }}
            config={{ responsive: true }}
            style={{ width: "100%", maxWidth: "1000px" }}
          />

          <div
            style={{
              marginTop: "20px",
              marginBottom: "15px",
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <button onClick={handleDownloadAllResults} style={{ padding: "8px 12px" }}>
              Download All Results CSV
            </button>

            <button onClick={handleDownloadSignificantResults} style={{ padding: "8px 12px" }}>
              Download Significant Genes CSV
            </button>

            <button onClick={handleDownloadStrongHits} style={{ padding: "8px 12px" }}>
              Download Strong Hits CSV
            </button>

            <button
              onClick={handleDownloadHeatmap}
              style={{ padding: "8px 12px" }}
              disabled={!deseq2Data?.heatmapPath}
            >
              Download Significant Genes Heatmap PNG
            </button>
          </div>

          {heatmapUrl && (
            <div style={{ marginTop: "25px", marginBottom: "25px" }}>
              <h3>Significant Genes Heatmap</h3>
              <img
                src={heatmapUrl}
                alt="DESeq2 significant genes heatmap"
                style={{
                  width: "100%",
                  maxWidth: "1000px",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  background: "#fff",
                }}
              />
            </div>
          )}

          <div
            style={{
              marginTop: "20px",
              marginBottom: "10px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3 style={{ margin: 0 }}>Significant Genes Table</h3>
          </div>

          <p style={{ marginTop: "10px" }}>
            Showing {significantTableRows.length} genes with adjusted p-value ≤ 0.05
            {summary.totalTested ? ` (out of ${summary.totalTested} tested)` : ""}.
          </p>

          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "5px",
              }}
            >
              <thead>
                <tr>
                  <th style={{ border: "1px solid #ccc", padding: "8px" }}>Gene</th>
                  <th style={{ border: "1px solid #ccc", padding: "8px" }}>Log2 Fold Change</th>
                  <th style={{ border: "1px solid #ccc", padding: "8px" }}>P-Value</th>
                  <th style={{ border: "1px solid #ccc", padding: "8px" }}>Adjusted P-Value</th>
                </tr>
              </thead>
              <tbody>
                {significantTableRows.length > 0 ? (
                  significantTableRows.map((row, index) => {
                    const fc = Number(row.log2FoldChange);
                    const p = row.pvalue;
                    const padj = row.padj;

                    return (
                      <tr key={index}>
                        <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                          {row.gene}
                        </td>
                        <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                          {Number.isFinite(fc) ? fc.toFixed(3) : "NA"}
                        </td>
                        <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                          {typeof p === "number" && Number.isFinite(p)
                            ? p.toExponential(2)
                            : p ?? "NA"}
                        </td>
                        <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                          {typeof padj === "number" && Number.isFinite(padj)
                            ? padj.toExponential(2)
                            : padj ?? "NA"}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      style={{ border: "1px solid #ccc", padding: "12px", textAlign: "center" }}
                    >
                      No significant genes found at padj ≤ 0.05.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: "20px" }}>
            <button
              onClick={handleInterpret}
              disabled={isInterpreting}
              style={{ padding: "5px 10px" }}
            >
              {isInterpreting ? "Getting AI interpretation..." : "AI Interpret Results"}
            </button>
          </div>

          {aiExplanation && (
            <div
              style={{
                marginTop: "15px",
                padding: "16px",
                border: "1px solid #3a4553",
                borderRadius: "8px",
                backgroundColor: "#151a20",
                color: "#f4f7fb",
                maxHeight: "250px",
                overflowY: "auto",
                whiteSpace: "pre-wrap",
                lineHeight: 1.55,
              }}
            >
              <h3 style={{ marginTop: 0 }}>AI Interpretation</h3>
              <p style={{ marginBottom: 0 }}>{aiExplanation}</p>
            </div>
          )}

          <button onClick={reset} style={{ marginTop: "20px" }}>
            Upload Another File
          </button>
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              gap: "20px",
              flexWrap: "wrap",
              marginTop: "20px",
              marginBottom: "20px",
            }}
          >
            <div style={{ flex: "1 1 220px", minWidth: "200px", maxWidth: "260px" }}>
              <p style={{ marginBottom: "8px", fontWeight: "bold" }}>
                Example counts CSV format
              </p>
              <img
                src="/combatseq_counts_example.png"
                alt="Example counts CSV format"
                style={{
                  width: "100%",
                  maxWidth: "260px",
                  border: "1px solid #444",
                  borderRadius: "6px",
                  background: "white",
                }}
              />
              <p style={{ fontSize: "0.9rem", marginTop: "8px", color: "#ccc" }}>
                First column should be <code>gene</code>. Each remaining column should be
                a sample name.
              </p>
            </div>

            <div style={{ flex: "1 1 220px", minWidth: "200px", maxWidth: "260px" }}>
              <p style={{ marginBottom: "8px", fontWeight: "bold" }}>
                Example DESeq2 metadata format
              </p>
              <img
                src="/example_DESeq2_metadata.png"
                alt="Example DESeq2 metadata format"
                style={{
                  width: "100%",
                  maxWidth: "260px",
                  border: "1px solid #444",
                  borderRadius: "6px",
                  background: "white",
                }}
              />
              <p style={{ fontSize: "0.9rem", marginTop: "8px", color: "#ccc" }}>
                Metadata must include <code>sample</code> and <code>condition</code>.
              </p>
            </div>
          </div>

          <p style={{ fontSize: "0.9rem", color: "#ccc", marginBottom: "20px" }}>
            Sample names in the metadata <code>sample</code> column must exactly match
            the sample column names in the counts file.
          </p>

          <div>
            <label htmlFor="counts-file-input">Counts File (CSV):</label>
            <input
              id="counts-file-input"
              type="file"
              accept=".csv"
              onChange={handleCountsFileChange}
              disabled={!!importedCountsInput}
              style={{ marginBottom: "20px", marginLeft: "10px" }}
            />
          </div>

          <div>
            <label htmlFor="metadata-file-input">Metadata File (CSV):</label>
            <input
              id="metadata-file-input"
              type="file"
              accept=".csv"
              onChange={handleMetadataFileChange}
              disabled={!!importedMetadataInput}
              style={{ marginBottom: "20px", marginLeft: "10px" }}
            />
          </div>

          <ImportedInputNotice
            importedCountsInput={importedCountsInput ? { ...importedCountsInput, role: "Counts" } : null}
            importedMetadataInput={importedMetadataInput ? { ...importedMetadataInput, role: "Metadata" } : null}
            onClear={clearImportedInputs}
          />

          <p>
            {(countsFile || importedCountsInput) && (metadataFile || importedMetadataInput)
              ? `Selected counts file: ${(importedCountsInput || countsFile).name}, metadata file: ${(importedMetadataInput || metadataFile).name}`
              : "Upload a counts file and a metadata file to run DESeq2 analysis."}
          </p>

          {(countsFile || importedCountsInput) && (metadataFile || importedMetadataInput) && (
            <button
              onClick={handleRun}
              disabled={isProcessing || !user}
              style={{ marginTop: "10px" }}
            >
              {isProcessing ? "Processing..." : "Run"}
            </button>
          )}

          {isProcessing && <p>Processing your files, please wait...</p>}
          {error && <p style={{ color: "red" }}>{error}</p>}
        </>
      )}
    </div>
  );
}


  function About() {
  return (
    <div
      className="about-section"
      style={{
        maxWidth: "900px",
        margin: "0 auto",
        textAlign: "left",
      }}
    >
      <h2 style={{ textAlign: "center" }}>About GATOR</h2>
      <p>
        Genomic Analysis Toward Official Research (GATOR) aims to make sequencing-data research more open,
        accessible, and exploratory. By guiding users through the analysis of publicly available datasets,
        GATOR helps researchers, students, and independent thinkers test their ideas, follow the evidence,
        and discover whether their theories hold true. This tool currently provides five main functionalities:
      </p>
      <ul>
        <li>
          <strong>Combat-Seq Batch Correction:</strong> Upload an RNAseq counts .csv file and a metadata .csv
          file to correct batch effects in RNA-seq count data caused by unwanted technical variation. A new Combat-Seq
          .csv file will be generated for download that has preserved true biological signals without the technical noise.                   
        </li>
        <li>
          <strong>Heatmap Generator:</strong> Upload an RNAseq normalized counts .csv file to generate an
          unsupervised heatmap. This visualization helps in understanding the expression patterns of genes across
          different samples.
        </li>
        <li>
          <strong>DESeq2 Analysis:</strong> Upload an RNA counts .csv file and a metadata .csv file to perform
          differential expression analysis using the DESeq2 method. The tool generates a volcano
          plot and a table of differentially expressed genes, helping users identify significant
          changes in gene expression between conditions.
        </li>
        <li>
          <strong>PCA Analysis:</strong> Upload an RNAseq counts .csv file to generate a 2D PCA
          plot, enabling visualization of sample variance.
        </li>
        <li>
          <strong>Gene Expression Predictor:</strong> Upload a counts .csv file and metadata .csv file to
          predict gene expression levels using a Random Forest model. This tool provides predictive
          insights into potential gene behavior, aiding in identifying genes for further analysis.
        </li>
      </ul>
      <p>
        This application is built to streamline the analysis of RNAseq data, making it easier for
        users to visualize and interpret their data without requiring extensive bioinformatics
        expertise or software dependencies. Future updates may include additional features and analysis 
        tools to further support RNAseq research.
      </p>
    </div>
  );
}


function PCAAnalysis({ setError, setIsProcessing, isProcessing, user, error, projectRunMetadata, importedInput, clearImportedInput }) {
  const [pcaData, setPcaData] = useState(null);
  const [file, setFile] = useState(null);
  const [timestamp, setTimestamp] = useState(null);
  const [processingTriggered, setProcessingTriggered] = useState(false);
  const plotRef = React.useRef(null); // will hold the graph DIV for toImage

  // NEW: AI interpretation state
  const [pcaExplanation, setPcaExplanation] = useState(null);
  const [isInterpretingPCA, setIsInterpretingPCA] = useState(false);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleRun = async () => {
    if (!file && !importedInput?.storagePath) return setError("Please select a file to upload or choose an imported project file.");
    if (!user) return setError("You must be signed in to upload files.");

    setIsProcessing(true);
    const newTimestamp = Date.now().toString();
    setTimestamp(newTimestamp);

    try {
      await setDoc(doc(collection(db, "pcaData"), newTimestamp), {
        status: "pending",
        timestamp: newTimestamp,
        ...(importedInput ? getImportedInputSource([importedInput]) : getUploadedInputSource([file])),
        ...projectRunMetadata,
      });

      if (importedInput) {
        await prepareImportedInputForAnalysis({
          importedInput,
          runId: newTimestamp,
          targetTool: "pca",
          role: "counts",
        });
      } else {
        const storage = getStorage(app);
        const fileRef = storageRef(storage, `pcaData/${newTimestamp}_${file.name}`);
        await uploadBytes(fileRef, file);
      }

      setProcessingTriggered(true);
      setError(null);
    } catch (err) {
      console.error("PCA upload error:", err);
      setError("Failed to upload file: " + err.message);
      setIsProcessing(false);
    }
  };

  const reset = () => {
    setFile(null);
    clearImportedInput?.();
    setPcaData(null);
    setTimestamp(null);
    setProcessingTriggered(false);
    setPcaExplanation(null);
    setError(null);
    setIsProcessing(false);
  };

  useEffect(() => {
    if (!timestamp || !processingTriggered) return;

    const timeoutId = setTimeout(() => {
      setError("Processing timed out. Please try again or use a smaller file.");
      setIsProcessing(false);
      setProcessingTriggered(false);
    }, 300000);

    const docRef_ = doc(db, "pcaData", timestamp);
    const unsubscribe = onSnapshot(
      docRef_,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();

        if (data.status === "completed") {
          if (!Array.isArray(data.pca)) {
            setError("PCA data missing in results.");
          } else {
            setPcaData(data);
          }
          setIsProcessing(false);
          setProcessingTriggered(false);
          clearTimeout(timeoutId);
        } else if (data.status === "failed") {
          setError("Processing failed: " + (data.error || "Unknown error"));
          setIsProcessing(false);
          setProcessingTriggered(false);
          clearTimeout(timeoutId);
        } else {
          console.log("Waiting for Cloud Function to process the PCA file...");
        }
      },
      (err) => {
        console.error("Error fetching Firestore data:", err);
        setError("Failed to fetch PCA data from Firestore: " + err.message);
        setIsProcessing(false);
        setProcessingTriggered(false);
        clearTimeout(timeoutId);
      }
    );

    return () => {
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [timestamp, processingTriggered, setError, setIsProcessing]);

  // --------- downloads / export ----------
  const downloadPcaCsv = () => {
    if (!pcaData?.pca) return;
    const header = "sample,pc1,pc2,pc3\n";
    const rows = pcaData.pca.map((r) => `${r.sample},${r.pc1},${r.pc2},${r.pc3}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pca_${timestamp || Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPcaJson = () => {
    if (!pcaData) return;
    const blob = new Blob([JSON.stringify(pcaData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pca_${timestamp || Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPng = async () => {
    try {
      if (!plotRef.current) return;
      const url = await Plotly.toImage(plotRef.current, {
        format: "png",
        width: 1000,
        height: 800,
        scale: 2,
      });
      const a = document.createElement("a");
      a.href = url;
      a.download = `pca_${timestamp || Date.now()}.png`;
      a.click();
    } catch (e) {
      console.error(e);
      setError("Failed to export image: " + e.message);
    }
  };

  // NEW: call interpretPCA Cloud Function
  const handleInterpretPCA = async () => {
    if (!pcaData || !Array.isArray(pcaData.pca) || pcaData.pca.length === 0) {
      setError("No PCA results available to interpret.");
      return;
    }

    setIsInterpretingPCA(true);
    setError(null);

    try {
      const summaryText =
        "PCA on RNAseq samples; coordinates for PC1 and PC2 (and PC3 where available).";

      const interpretFn = httpsCallable(
        getFunctions(app, "us-central1"),
        "interpretPCA"
      );

      const res = await interpretFn({
        summaryText,
        points: pcaData.pca, // [{ sample, pc1, pc2, pc3 }, ...]
      });

      setPcaExplanation(res.data.explanation);
    } catch (err) {
      console.error("interpretPCA failed:", err);
      setError("AI PCA interpretation failed: " + (err.message || err));
    } finally {
      setIsInterpretingPCA(false);
    }
  };

  return (
    <div style={{ flex: 1, padding: "20px", borderRight: "1px solid #ccc" }}>
      <h2>PCA Analysis</h2>

      {pcaData ? (
        <>
          {pcaData.variableGenesUsed ? (
            <p style={{ color: "rgba(255,255,255,0.72)", marginBottom: "10px" }}>
              PCA used {pcaData.variableGenesUsed} variable genes
              {pcaData.removedZeroVarianceGenes
                ? ` after removing ${pcaData.removedZeroVarianceGenes} zero-variance genes.`
                : "."}
            </p>
          ) : null}
          <div style={{ marginBottom: "20px" }}>
            <button onClick={downloadPng} style={{ padding: "5px 10px", marginRight: "10px" }}>
              Download PNG
            </button>
            <button onClick={downloadPcaCsv} style={{ padding: "5px 10px", marginRight: "10px" }}>
              Download CSV
            </button>
            <button onClick={downloadPcaJson} style={{ padding: "5px 10px", marginRight: "10px" }}>
              Download JSON
            </button>
            <button onClick={reset} style={{ padding: "5px 10px" }}>
              Upload Another File
            </button>
          </div>

          <Plot
            // IMPORTANT: this ref gives us the inner graph DIV for toImage
            ref={(node) => {
              if (node && node.el) plotRef.current = node.el;
            }}
            data={[
              {
                x: pcaData.pca.map((row) => Number(row.pc1)),
                y: pcaData.pca.map((row) => Number(row.pc2)),
                text: pcaData.pca.map((row) => row.sample),
                mode: "markers",
                type: "scatter", // 2D only
                marker: { size: 7, color: pcaData.pca.map((_, i) => i) },
              },
            ]}
            layout={{
              title: "2D PCA Plot of RNAseq Data",
              margin: { l: 50, r: 50, t: 80, b: 50 },
              annotations: [], // <- prevents "annotations" undefined crash in Plotly internals
              xaxis: { title: "PC1" },
              yaxis: { title: "PC2" },
            }}
            config={{
              responsive: true,
              displaylogo: false,
              modeBarButtonsToAdd: ["toImage"],
              toImageButtonOptions: {
                format: "png",
                filename: `pca_${timestamp || Date.now()}`,
                height: 800,
                width: 1000,
                scale: 2,
              },
            }}
            useResizeHandler
            style={{ width: "100%", height: "600px" }}
            onError={(err) => {
              console.error("Plot error:", err);
              setError("Plot error: " + (err?.message || err));
            }}
          />

          {/* AI interpretation controls */}
          <div style={{ marginTop: "20px" }}>
            <button
              onClick={handleInterpretPCA}
              disabled={isInterpretingPCA}
              style={{ padding: "5px 10px" }}
            >
              {isInterpretingPCA ? "Getting AI interpretation..." : "AI Interpret PCA"}
            </button>
          </div>

          {pcaExplanation && (
            <div
              style={{
                marginTop: "15px",
                padding: "16px",
                border: "1px solid #3a4553",
                borderRadius: "8px",
                backgroundColor: "#151a20",
                color: "#f4f7fb",
                maxHeight: "250px",
                overflowY: "auto",
                whiteSpace: "pre-wrap",
                lineHeight: 1.55,
              }}
            >
              <h3 style={{ marginTop: 0 }}>AI PCA Interpretation</h3>
              <p style={{ marginBottom: 0 }}>{pcaExplanation}</p>
            </div>
          )}
        </>
      ) : (
        <>
          <div>
            <label htmlFor="pca-file-input">Upload RNAseq Data CSV File:</label>
            <input
              id="pca-file-input"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={!!importedInput}
              style={{ marginBottom: "20px", marginLeft: "10px" }}
            />
          </div>

          <ImportedInputNotice importedInput={importedInput} onClear={clearImportedInput} />

          <p>
            {importedInput
              ? `Selected imported file: ${importedInput.name}`
              : file
              ? `Selected file: ${file.name}`
              : "Upload an RNAseq data CSV file to generate a PCA plot."}
          </p>

          {(file || importedInput) && (
            <button onClick={handleRun} disabled={isProcessing || !user} style={{ marginTop: "10px" }}>
              {isProcessing ? "Processing..." : "Run"}
            </button>
          )}

          {isProcessing && (
            <p style={{ marginTop: 10, fontStyle: "italic" }}>
              Processing in the background… this view will update automatically when results are ready.
            </p>
          )}

          {error && <p style={{ color: "red" }}>{error}</p>}
        </>
      )}
    </div>
  );
}


function GeneExpressionPredictor({ setError, setIsProcessing, isProcessing, user, error, projectRunMetadata }) {
  const [countsFile, setCountsFile] = useState(null);
  const [metadataFile, setMetadataFile] = useState(null);
  const [predictions, setPredictions] = useState(null);

  const handleCountsFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setCountsFile(selectedFile);
      setError(null);
    }
  };

  const handleMetadataFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setMetadataFile(selectedFile);
      setError(null);
    }
  };

  const handlePredict = async () => {
  if (!countsFile || !metadataFile) {
    setError("Please select both a counts file and a metadata file.");
    return;
  }

  if (!user) {
    setError("You must be signed in to process files.");
    return;
  }

  setIsProcessing(true);
  setError(null);

  const timeoutId = setTimeout(() => {
    setError("Processing timed out. Please try again or use a smaller file.");
    setIsProcessing(false);
  }, 20 * 60 * 1000); // 20 minutes

  try {
    const countsContent = await countsFile.text();
    const metadataContent = await metadataFile.text();

    const predictFunction = httpsCallable(functions, "predictGeneExpression", {
      timeout: 20 * 60 * 1000, // 20 minutes
    });

    const result = await predictFunction({ countsContent, metadataContent });

    clearTimeout(timeoutId);

    setPredictions(result.data.predictions || []);
    setError(null);

    const timestamp = Date.now().toString();
    await setDoc(doc(db, "geneExpressionData", timestamp), {
      status: "completed",
      predictions: result.data.predictions,
      timestamp,
      ...getUploadedInputSource([countsFile, metadataFile]),
      ...projectRunMetadata,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    console.error("Prediction failed:", err);
    setError("Prediction failed: " + err.message);
  } finally {
    clearTimeout(timeoutId);
    setIsProcessing(false);
  }
};

const reset = () => {
  setCountsFile(null);
  setMetadataFile(null);
  setPredictions(null);
  setError(null);
  setIsProcessing(false);
};

  // ---- NEW: sorted predictions for both chart and table ----
  const sortedPredictions = predictions
    ? [...predictions].sort((a, b) => b.r2_score - a.r2_score)
    : [];

  // limit number of bars in chart (e.g., top 20 genes)
  const TOP_N = 20;
  const topForChart = sortedPredictions.slice(0, TOP_N);

  return (
    <div style={{ flex: 1, padding: "20px" }}>
      <h2>Gene Expression Predictor</h2>

      {predictions && sortedPredictions.length > 0 ? (
        <>
          {/* ---------- Bar chart ---------- */}
          <h3>Top Predictable Genes (R² Score)</h3>
          <Plot
            data={[
              {
                type: "bar",
                x: topForChart.map((p) => p.gene),
                y: topForChart.map((p) => p.r2_score),
                text: topForChart.map((p) => p.r2_score.toFixed(3)),
                textposition: "auto",
              },
            ]}
            layout={{
              width: 700,
              height: 400,
              title: `Top ${Math.min(TOP_N, sortedPredictions.length)} Genes by R²`,
              margin: { l: 70, r: 30, t: 60, b: 100 },
              xaxis: {
                title: "Gene",
                tickangle: -45,
              },
              yaxis: {
                title: "R² Score",
                rangemode: "tozero",
              },
              showlegend: false,
            }}
            style={{ width: "100%", maxWidth: "800px" }}
            config={{ responsive: true, displaylogo: false }}
          />

          {/* ---------- Table ---------- */}
          <h3 style={{ marginTop: "30px" }}>
            Gene Predictability Results (Sorted by R² Score)
          </h3>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Gene</th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>R² Score</th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>
                  Feature Importances
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedPredictions.map((p, index) => (
                <tr key={index}>
                  <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                    {p.gene}
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                    {p.r2_score.toFixed(3)}
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                    {p.feature_importances
                      .map(
                        (fi) => `${fi.feature}: ${fi.importance.toFixed(3)}`
                      )
                      .join(", ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button onClick={reset} style={{ marginTop: "20px" }}>
            Upload Another File
          </button>
        </>
      ) : (
        <>
        <p style={{ marginBottom: "10px" }}>
  Upload a counts CSV file and a metadata CSV file to predict gene expression
  using patient or sample-level predictor variables.
</p>

<p style={{ fontSize: "0.95rem", color: "#ccc", marginBottom: "16px", lineHeight: 1.5 }}>
  <strong>Metadata formatting rules:</strong> The first column must contain
  sample IDs and must exactly match the sample column names in the counts file.
  Every column after the first is treated as a predictor variable.
</p>

<p style={{ fontSize: "0.95rem", color: "#ccc", marginBottom: "16px", lineHeight: 1.5 }}>
  <strong>Predictor variables must be numeric.</strong> For example:
  treatment group can be encoded as <code>0</code> = non-treatment and{" "}
  <code>1</code> = treatment; sex can be encoded as <code>0</code> = male and{" "}
  <code>1</code> = female. Continuous variables such as age or BMI can be left
  as numeric values.
</p>

<div
  style={{
    display: "flex",
    gap: "20px",
    flexWrap: "wrap",
    marginTop: "20px",
    marginBottom: "20px",
  }}
>
  <div style={{ flex: "1 1 220px", minWidth: "200px", maxWidth: "260px" }}>
    <p style={{ marginBottom: "8px", fontWeight: "bold" }}>
      Example counts CSV format
    </p>
    <img
      src="/combatseq_counts_example.png"
      alt="Example counts CSV format"
      style={{
        width: "100%",
        maxWidth: "260px",
        border: "1px solid #444",
        borderRadius: "6px",
        background: "white",
      }}
    />
    <p style={{ fontSize: "0.9rem", marginTop: "8px", color: "#ccc" }}>
      First column should be <code>gene</code>. Each remaining column should be
      a sample name.
    </p>
  </div>

  <div style={{ flex: "1 1 220px", minWidth: "200px", maxWidth: "260px" }}>
    <p style={{ marginBottom: "8px", fontWeight: "bold" }}>
      Example predictor metadata format
    </p>
    <img
      src="/example_predictor_metadata.png"
      alt="Example predictor metadata format"
      style={{
        width: "100%",
        maxWidth: "260px",
        border: "1px solid #444",
        borderRadius: "6px",
        background: "white",
      }}
    />
    <p style={{ fontSize: "0.9rem", marginTop: "8px", color: "#ccc" }}>
      First column should be <code>sample</code>. All remaining columns are
      numeric predictor variables.
    </p>
  </div>
</div>

<p style={{ fontSize: "0.9rem", color: "#ccc", marginBottom: "20px" }}>
  Sample names in the metadata <code>sample</code> column must exactly match
  the sample column names in the counts file.
</p>
          <div>
            <label htmlFor="predict-counts-file-input">Counts File (CSV):</label>
            <input
              id="predict-counts-file-input"
              type="file"
              accept=".csv"
              onChange={handleCountsFileChange}
              style={{ marginBottom: "20px", marginLeft: "10px" }}
            />
          </div>
          <div>
            <label htmlFor="predict-metadata-file-input">Metadata File (CSV):</label>
            <input
              id="predict-metadata-file-input"
              type="file"
              accept=".csv"
              onChange={handleMetadataFileChange}
              style={{ marginBottom: "20px", marginLeft: "10px" }}
            />
          </div>
          <button
  onClick={handlePredict}
  disabled={isProcessing || !user || !countsFile || !metadataFile}
  style={{ marginTop: "10px" }}
>
  {isProcessing ? "Predicting..." : "Predict"}
</button>

<p style={{ fontSize: "0.92rem", color: "#ccc", marginTop: "14px", lineHeight: 1.5 }}>
  The Gene Expression Predictor uses a machine learning model to determine how well sample metadata variables (such as age, treatment group, sex, or other predictors) explain differences in gene expression across samples.
For each gene, the tool builds a predictive model using the provided metadata variables and evaluates how accurately those variables can predict the gene’s expression levels.
The model uses a Random Forest regression algorithm with cross-validation to estimate predictive performance. The strength of prediction is summarized using an R² score, which represents the proportion of gene expression variation explained by the metadata variables.
Higher R² values indicate stronger relationships between the predictors and gene expression.
The tool also reports feature importance values, which indicate which metadata variables contribute most strongly to predicting each gene.
These results can help identify genes whose expression patterns are associated with specific patient characteristics, treatments, or biological factors.
</p>
{isProcessing && <p>Processing your files, please wait...</p>}
{error && <p style={{ color: "red" }}>{error}</p>}
        </>
      )}
    </div>
  );
}


function SignIn({ onSignIn }) {
  const provider = new GoogleAuthProvider();

  const handleSignIn = async () => {
    try {
      await signInWithPopup(auth, provider);
      onSignIn();
    } catch (err) {
      console.error("Sign-in error:", err);
      alert("Failed to sign in: " + (err.message || err));
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background: "#111",
      }}
    >
      <div
        style={{
          width: "min(900px, 100%)",
          borderRadius: 18,
          overflow: "hidden",
          background: "#0b0b0b",
          boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* Hero image (served from /public) */}
        <div style={{ position: "relative" }}>
          <img
            src="/signinGATOR.png" // <-- change extension if needed
            alt="GATOR"
            style={{
              width: "100%",
              display: "block",
              maxHeight: 560,
              objectFit: "cover",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.0), rgba(0,0,0,0.65))",
            }}
          />
        </div>

        {/* Footer/actions */}
        <div style={{ padding: 20, display: "grid", gap: 10 }}>
          <div
            style={{
              color: "rgba(255,255,255,0.92)",
              fontSize: 20,
              fontWeight: 700,
            }}
          >
            GATOR
          </div>

          <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 14 }}>
            Genomic Analysis Toward Official Research
          </div>

          <button
            onClick={handleSignIn}
            style={{
              marginTop: 8,
              padding: "12px 14px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.15)",
              background: "rgba(255,255,255,0.06)",
              color: "white",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Continue with Google
          </button>

          <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 12 }}>
            https://gator-7bf72.web.app
          </div>
        </div>
      </div>
    </div>
  );
}

function CombatSeqAnalysis({
  setError,
  setIsProcessing,
  isProcessing,
  user,
  error,
  projectRunMetadata,
  importedCountsInput,
  importedMetadataInput,
  clearImportedInputs,
}) {
  const [countsFile, setCountsFile] = useState(null);
  const [metadataFile, setMetadataFile] = useState(null);
  const [docId, setDocId] = useState(null);
  const [processingTriggered, setProcessingTriggered] = useState(false);
  const [pcaPre, setPcaPre] = useState(null);
  const [pcaPost, setPcaPost] = useState(null);
  const [correctedCountsPath, setCorrectedCountsPath] = useState(null);

  const handleCountsFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setCountsFile(selectedFile);
      setError(null);
    }
  };

  const handleMetadataFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setMetadataFile(selectedFile);
      setError(null);
    }
  };

  const handleRun = async () => {
    if ((!countsFile && !importedCountsInput?.storagePath) || (!metadataFile && !importedMetadataInput?.storagePath)) {
      setError("Please select both a counts file and a metadata file.");
      return;
    }
    if (!user) {
      setError("You must be signed in to run ComBat-Seq.");
      return;
    }

    setIsProcessing(true);

    try {
      // 1) Create Firestore doc to track this run
      const docRef = await addDoc(collection(db, "combatSeqResults"), {
        status: "pending",
        createdAt: new Date(),
        ...getCombinedInputSource([countsFile, metadataFile], [importedCountsInput, importedMetadataInput]),
        ...projectRunMetadata,
      });
      const newDocId = docRef.id;
      setDocId(newDocId);

      // 2) Upload or stage input CSVs to Storage
      const storage = getStorage(app);
      let countsPath = `combatSeqData/${newDocId}_counts.csv`;
      let metadataPath = `combatSeqData/${newDocId}_metadata.csv`;

      if (importedCountsInput) {
        const preparedCounts = await prepareImportedInputForAnalysis({
          importedInput: importedCountsInput,
          runId: newDocId,
          targetTool: "combat",
          role: "counts",
        });
        countsPath = preparedCounts.destinationPath;
      } else {
        await uploadBytes(storageRef(storage, countsPath), countsFile);
      }

      if (importedMetadataInput) {
        const preparedMetadata = await prepareImportedInputForAnalysis({
          importedInput: importedMetadataInput,
          runId: newDocId,
          targetTool: "combat",
          role: "metadata",
        });
        metadataPath = preparedMetadata.destinationPath;
      } else {
        await uploadBytes(storageRef(storage, metadataPath), metadataFile);
      }

      // 3) Call Cloud Function to trigger the Cloud Run ComBat-Seq runner
      const startCombatSeq = httpsCallable(
        getFunctions(app, "us-central1"),
        "startCombatSeq"
      );

      await startCombatSeq({ docId: newDocId, countsPath, metadataPath });

      setProcessingTriggered(true);
      setError(null);
    } catch (err) {
      console.error("Failed to start ComBat-Seq:", err);
      setError("Failed to start ComBat-Seq analysis: " + (err.message || err));
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (!docId || !processingTriggered) return;

    const timeoutId = setTimeout(() => {
      setError("Processing timed out. Please try again or use a smaller file.");
      setIsProcessing(false);
      setProcessingTriggered(false);
    }, 20 * 60 * 1000); // 20 minutes

    const docRef_ = doc(db, "combatSeqResults", docId);
    const unsubscribe = onSnapshot(
      docRef_,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();

        if (data.status === "completed") {
          setPcaPre(data.pcaPre || []);
          setPcaPost(data.pcaPost || []);
          setCorrectedCountsPath(data.correctedCountsPath || null);
          setIsProcessing(false);
          setProcessingTriggered(false);
          clearTimeout(timeoutId);
        } else if (data.status === "failed") {
          setError("ComBat-Seq failed: " + (data.error || "Unknown error"));
          setIsProcessing(false);
          setProcessingTriggered(false);
          clearTimeout(timeoutId);
        }
      },
      (err) => {
        console.error("Error fetching ComBat-Seq results:", err);
        setError("Failed to fetch ComBat-Seq results: " + err.message);
        setIsProcessing(false);
        setProcessingTriggered(false);
        clearTimeout(timeoutId);
      }
    );

    return () => {
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [docId, processingTriggered, setError, setIsProcessing]);

  const reset = () => {
    setCountsFile(null);
    setMetadataFile(null);
    clearImportedInputs?.();
    setDocId(null);
    setProcessingTriggered(false);
    setPcaPre(null);
    setPcaPost(null);
    setCorrectedCountsPath(null);
    setError(null);
    setIsProcessing(false);
  };

  const downloadCorrectedCsv = async () => {
    if (!correctedCountsPath) {
      alert("No corrected counts file is available yet.");
      return;
    }

    try {
      const storage = getStorage(app);
      const fileRef = storageRef(storage, correctedCountsPath);
      const url = await getDownloadURL(fileRef);

      const a = document.createElement("a");
      a.href = url;
      a.download =
        correctedCountsPath.split("/").pop() ||
        "combatseq_corrected_counts.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error("Failed to download corrected counts CSV:", err);
      setError(
        "Failed to download corrected counts CSV: " + (err.message || err)
      );
    }
  };

  const renderPcaPlot = (points, title) => {
  if (!Array.isArray(points) || points.length === 0) {
    return <p>No PCA data available.</p>;
  }

  // Keep only rows with valid numeric PC1/PC2
  const cleanPoints = points.filter(
    (r) =>
      Number.isFinite(Number(r.PC1)) &&
      Number.isFinite(Number(r.PC2))
  );

  if (cleanPoints.length === 0) {
    console.warn("No valid PCA rows for plotting:", points.slice(0, 5));
    return (
      <p style={{ color: "salmon" }}>
        PCA data was returned, but PC1/PC2 values were not valid numbers.
        Check browser console for sample rows.
      </p>
    );
  }

  const v1 = cleanPoints?.[0]?.varPC1;
  const v2 = cleanPoints?.[0]?.varPC2;

  const fmtPct = (v) =>
    typeof v === "number" && isFinite(v) ? `${(v * 100).toFixed(1)}%` : null;

  const xTitle = fmtPct(v1) ? `PC1 (${fmtPct(v1)})` : "PC1";
  const yTitle = fmtPct(v2) ? `PC2 (${fmtPct(v2)})` : "PC2";

  // Fixed batch colors
  const batchColors = {
    B1: "#3b82f6",
    B2: "#f97316",
    B3: "#22c55e",
    "B 1": "#3b82f6",
    "B 2": "#f97316",
    "B 3": "#22c55e",
    NA: "#9ca3af",
  };

  const byBatch = cleanPoints.reduce((acc, row) => {
    const b = row.batch ?? "NA";
    if (!acc[b]) acc[b] = [];
    acc[b].push(row);
    return acc;
  }, {});

  const traces = Object.entries(byBatch).map(([batchName, rows]) => ({
    name: batchName,
    type: "scatter",
    mode: "markers+text",
    x: rows.map((r) => Number(r.PC1)),
    y: rows.map((r) => Number(r.PC2)),
    text: rows.map((r) => r.sample),
    textposition: "top center",
    hoverinfo: "text",
    marker: {
      size: 8,
      color: batchColors[batchName] || "#9ca3af",
    },
  }));

  return (
    <Plot
      data={traces}
      layout={{
        title,
        autosize: true,
        margin: { l: 60, r: 20, t: 60, b: 60 },
        legend: { title: { text: "Batch" } },
        paper_bgcolor: "rgba(0,0,0,0)",
        plot_bgcolor: "rgba(0,0,0,0)",
        xaxis: { title: xTitle },
        yaxis: { title: yTitle },
      }}
      style={{ width: "100%", height: "520px" }}
      config={{
        responsive: true,
        displaylogo: false,
      }}
      onInitialized={(fig) => {
        console.log("ComBat PCA initialized:", fig);
        console.log("Trace count:", fig?.data?.length);
        console.log("First trace sample:", fig?.data?.[0]);
      }}
      onError={(err) => {
        console.error("ComBat PCA Plotly error:", err);
        setError("ComBat PCA plot error: " + (err?.message || String(err)));
      }}
    />
  );
};

  const hasResults = Array.isArray(pcaPost) && pcaPost.length > 0;

  return (
    <div style={{ flex: 1, padding: "20px" }}>
      <h2>ComBat-Seq Batch Correction</h2>

      {!hasResults ? (
        <>
  <p>
    Upload an RNA-seq counts matrix and a metadata file (with a{" "}
    <code>batch</code> column). The backend will:
  </p>
  <ul>
    <li>Compute PCA <strong>before</strong> batch correction</li>
    <li>Run ComBat-Seq to correct batch effects</li>
    <li>Compute PCA <strong>after</strong> correction</li>
  </ul>

  <div
    style={{
      display: "flex",
      gap: "20px",
      flexWrap: "wrap",
      marginTop: "20px",
      marginBottom: "20px",
    }}
  >
    <div style={{ flex: "1 1 220px", minWidth: "200px", maxWidth: "260px" }}>
  <p style={{ marginBottom: "8px", fontWeight: "bold" }}>
    Example counts CSV format
  </p>
  <img
    src="/combatseq_counts_example.png"
    alt="Example counts CSV format"
    style={{
      width: "100%",
      maxWidth: "260px",
      border: "1px solid #444",
      borderRadius: "6px",
      background: "white",
    }}
  />
  <p style={{ fontSize: "0.9rem", marginTop: "8px", color: "#ccc" }}>
    First column should be <code>gene</code>. Each remaining column should be
    a sample name.
  </p>
</div>

<div style={{ flex: "1 1 220px", minWidth: "200px", maxWidth: "260px" }}>
  <p style={{ marginBottom: "8px", fontWeight: "bold" }}>
    Example metadata CSV format
  </p>
  <img
    src="/combatseq_metadata_example.png"
    alt="Example metadata CSV format"
    style={{
      width: "100%",
      maxWidth: "260px",
      border: "1px solid #444",
      borderRadius: "6px",
      background: "white",
    }}
  />
  <p style={{ fontSize: "0.9rem", marginTop: "8px", color: "#ccc" }}>
    Metadata must include <code>sample</code> and <code>batch</code>.{" "}
    <code>group</code> is optional.
  </p>
</div>
  </div>

  <p style={{ fontSize: "0.9rem", color: "#ccc", marginBottom: "20px" }}>
    Sample names in the metadata <code>sample</code> column must exactly match
    the sample column names in the counts file.
  </p>

  <div>
    <label htmlFor="combat-counts-file-input">
      Counts File (CSV):
    </label>
    <input
      id="combat-counts-file-input"
      type="file"
      accept=".csv"
      onChange={handleCountsFileChange}
      disabled={!!importedCountsInput}
      style={{ marginBottom: "20px", marginLeft: "10px" }}
    />
  </div>

  <div>
    <label htmlFor="combat-metadata-file-input">
      Metadata File (CSV):
    </label>
    <input
      id="combat-metadata-file-input"
      type="file"
      accept=".csv"
      onChange={handleMetadataFileChange}
      disabled={!!importedMetadataInput}
      style={{ marginBottom: "20px", marginLeft: "10px" }}
    />
  </div>

  <ImportedInputNotice
    importedCountsInput={importedCountsInput ? { ...importedCountsInput, role: "Counts" } : null}
    importedMetadataInput={importedMetadataInput ? { ...importedMetadataInput, role: "Metadata" } : null}
    onClear={clearImportedInputs}
  />

  <p>
    {(countsFile || importedCountsInput) && (metadataFile || importedMetadataInput)
      ? `Selected counts file: ${(importedCountsInput || countsFile).name}, metadata file: ${(importedMetadataInput || metadataFile).name}`
      : "Upload a counts file and a metadata file to run ComBat-Seq."}
  </p>

  <button
    onClick={handleRun}
    disabled={isProcessing || !user || (!countsFile && !importedCountsInput) || (!metadataFile && !importedMetadataInput)}
    style={{ marginTop: "10px" }}
  >
    {isProcessing ? "Running ComBat-Seq..." : "Run ComBat-Seq"}
  </button>

  {isProcessing && (
    <p style={{ marginTop: 10, fontStyle: "italic" }}>
      Processing in the background… this view will update when results
      are ready.
    </p>
  )}

  {error && <p style={{ color: "red" }}>{error}</p>}
</>
          
      ) : (
        <>
          <p>
            PCA plots below show sample structure{" "}
            <strong>before</strong> and <strong>after</strong> ComBat-Seq
            batch correction.
          </p>

          <div
            style={{
              display: "flex",
              gap: "20px",
              flexWrap: "wrap",
              marginTop: "20px",
            }}
          >
            <div style={{ flex: 1, minWidth: "360px" }}>
              {renderPcaPlot(pcaPre, "PCA Before Batch Correction")}
            </div>
            <div style={{ flex: 1, minWidth: "360px" }}>
              {renderPcaPlot(pcaPost, "PCA After Batch Correction")}
            </div>
          </div>

          <div style={{ marginTop: "20px" }}>
            <button
              onClick={downloadCorrectedCsv}
              style={{ padding: "5px 10px", marginRight: "10px" }}
            >
              Download Corrected Counts CSV
            </button>
            <button onClick={reset} style={{ padding: "5px 10px" }}>
              Run Another ComBat-Seq Job
            </button>
          </div>

          {error && <p style={{ color: "red", marginTop: "10px" }}>{error}</p>}
        </>
      )}
    </div>
  );
}

function AdminKpiDashboard({
  adminKpis,
  adminKpisLoading,
  adminKpisError,
  refreshAdminKpis,
  staleJobs,
  staleJobsLoading,
  staleJobsError,
  staleJobsResult,
  selectedStaleJobs,
  previewStaleJobs,
  toggleSelectedStaleJob,
  toggleAllStaleJobs,
  deleteSelectedStaleJobs,
}) {
  const card = {
    padding: 14,
    borderRadius: 8,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.10)",
    minHeight: 0,
  };
  const eyebrow = {
    margin: 0,
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    color: "rgba(130,205,120,0.95)",
    textTransform: "uppercase",
  };
  const title = { margin: 0, fontSize: 22 };
  const muted = {
    margin: 0,
    color: "rgba(255,255,255,0.68)",
    fontSize: 13,
    lineHeight: 1.5,
  };
  const metricGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 10,
  };
  const metricCard = {
    padding: 12,
    borderRadius: 8,
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.10)",
  };
  const metricValue = {
    margin: "4px 0 0",
    fontSize: 24,
    fontWeight: 950,
  };
  const btn = {
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.08)",
    color: "white",
    fontWeight: 900,
    cursor: "pointer",
  };

  const metric = (label, value, helper) => (
    <div style={metricCard}>
      <p style={eyebrow}>{label}</p>
      <p style={metricValue}>{value}</p>
      {helper ? <p style={muted}>{helper}</p> : null}
    </div>
  );

  const runsByTool = Array.isArray(adminKpis?.runsByTool) ? adminKpis.runsByTool : [];
  const staleJobRows = Array.isArray(staleJobs?.jobs) ? staleJobs.jobs : [];
  const selectedCount = Object.values(selectedStaleJobs || {}).filter(Boolean).length;
  const allVisibleSelected =
    staleJobRows.length > 0 &&
    staleJobRows.every((job) => selectedStaleJobs?.[`${job.collectionName}:${job.id}`]);

  return (
    <div style={{ ...card, borderColor: "rgba(130,205,120,0.32)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
        <div>
          <p style={eyebrow}>Admin KPI Dashboard</p>
          <h2 style={title}>GATOR performance overview</h2>
          <p style={{ ...muted, marginTop: 6 }}>
            App-wide usage, workflow completion, failure rate, report readiness, and processing time.
          </p>
        </div>
        <button style={btn} type="button" onClick={refreshAdminKpis} disabled={adminKpisLoading}>
          {adminKpisLoading ? "Refreshing..." : "Refresh KPIs"}
        </button>
      </div>

      {adminKpisError ? (
        <p style={{ ...muted, marginTop: 12, color: "#ffb6b6" }}>{adminKpisError}</p>
      ) : null}

      {!adminKpis && !adminKpisLoading ? (
        <p style={{ ...muted, marginTop: 12 }}>
          KPI data is available to authorized admin accounts.
        </p>
      ) : null}

      {adminKpis ? (
        <div style={{ display: "grid", gap: 14, marginTop: 14 }}>
          <div style={metricGrid}>
            {metric("Total visits", Number(adminKpis.totalVisits || 0).toLocaleString())}
            {metric("Signed-in users", adminKpis.totalUsers === null ? "N/A" : Number(adminKpis.totalUsers || 0).toLocaleString())}
            {metric("Projects", Number(adminKpis.totalProjects || 0).toLocaleString())}
            {metric("Datasets", Number(adminKpis.totalDatasets || 0).toLocaleString())}
            {metric("Analysis runs", Number(adminKpis.totalRuns || 0).toLocaleString())}
            {metric("Completion rate", `${adminKpis.completionRate || 0}%`, `${adminKpis.completedRuns || 0} completed`)}
            {metric("Failure rate", `${adminKpis.failureRate || 0}%`, `${adminKpis.failedRuns || 0} failed`)}
            {metric("Active jobs", Number(adminKpis.activeRuns || 0).toLocaleString(), "Pending, queued, or processing in the last hour")}
            {metric("Stale jobs", Number(adminKpis.staleRuns || 0).toLocaleString(), "Old unfinished records, not active jobs")}
            {metric("Unknown jobs", Number(adminKpis.unknownRuns || 0).toLocaleString(), "Missing or unexpected status")}
            {metric("Avg run time", formatDuration(adminKpis.averageDurationSeconds))}
            {metric("Artifacts", Number(adminKpis.totalArtifacts || 0).toLocaleString(), `${adminKpis.reportReadyArtifacts || 0} report-ready`)}
          </div>

          {runsByTool.length ? (
            <div style={{ display: "grid", gap: 10 }}>
              <p style={eyebrow}>Runs by tool</p>
              <div style={{ display: "grid", gap: 8 }}>
                {runsByTool.map((tool) => (
                  <div
                    key={tool.collectionName}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(180px, 1.5fr) repeat(7, minmax(72px, 0.5fr))",
                      gap: 8,
                      alignItems: "center",
                      padding: 10,
                      borderRadius: 8,
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      overflowX: "auto",
                    }}
                  >
                    <strong>{tool.toolName}</strong>
                    <span>Total: {tool.total}</span>
                    <span>Done: {tool.completed}</span>
                    <span>Failed: {tool.failed}</span>
                    <span>Active: {tool.active || 0}</span>
                    <span>Stale: {tool.stale || 0}</span>
                    <span>Unknown: {tool.unknown || 0}</span>
                    <span>{formatDuration(tool.averageDurationSeconds)}</span>
                    {(tool.staleExamples?.length || tool.unknownExamples?.length) ? (
                      <div style={{ gridColumn: "1 / -1", color: "rgba(255,255,255,0.58)", fontSize: 12 }}>
                        {tool.staleExamples?.length ? (
                          <p style={{ margin: "4px 0 0" }}>
                            Stale examples:{" "}
                            {tool.staleExamples.map((item) => `${item.id} (${item.status}, ${item.ageMinutes ?? "?"} min old)`).join("; ")}
                          </p>
                        ) : null}
                        {tool.unknownExamples?.length ? (
                          <p style={{ margin: "4px 0 0" }}>
                            Unknown examples:{" "}
                            {tool.unknownExamples.map((item) => `${item.id} (${item.status}, ${item.ageMinutes ?? "?"} min old)`).join("; ")}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {adminKpis.generatedAt ? (
            <p style={{ ...muted, color: "rgba(255,255,255,0.48)" }}>
              Last generated: {new Date(adminKpis.generatedAt).toLocaleString()}
            </p>
          ) : null}
        </div>
      ) : null}

      <div
        style={{
          marginTop: 18,
          paddingTop: 16,
          borderTop: "1px solid rgba(255,255,255,0.10)",
          display: "grid",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
          <div>
            <p style={eyebrow}>Admin Stale Job Cleanup v1</p>
            <h2 style={title}>Preview and remove stale run records</h2>
            <p style={{ ...muted, marginTop: 6 }}>
              Review stale or unknown analysis records before deleting. This removes Firestore run records only;
              linked Storage files are left untouched.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
            <button style={btn} type="button" onClick={previewStaleJobs} disabled={staleJobsLoading}>
              {staleJobsLoading ? "Loading stale jobs..." : "Preview stale jobs"}
            </button>
            <button
              style={{
                ...btn,
                borderColor: selectedCount ? "rgba(255,107,107,0.45)" : "rgba(255,255,255,0.12)",
                background: selectedCount ? "rgba(255,107,107,0.12)" : "rgba(255,255,255,0.08)",
              }}
              type="button"
              onClick={deleteSelectedStaleJobs}
              disabled={!selectedCount || staleJobsLoading}
            >
              Delete selected ({selectedCount})
            </button>
          </div>
        </div>

        {staleJobsError ? <p style={{ ...muted, color: "#ffb6b6" }}>{staleJobsError}</p> : null}
        {staleJobsResult ? (
          <p style={{ ...muted, color: "rgba(130,205,120,0.95)" }}>{staleJobsResult}</p>
        ) : null}

        {staleJobs ? (
          <p style={muted}>
            Found {staleJobs.totalMatching || 0} stale/unknown jobs
            {staleJobs.truncated ? `; showing first ${staleJobRows.length}.` : "."}
          </p>
        ) : null}

        {staleJobRows.length ? (
          <div style={{ display: "grid", gap: 8, maxHeight: 460, overflow: "auto" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "rgba(255,255,255,0.76)",
                fontSize: 13,
                fontWeight: 900,
              }}
            >
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={(event) => toggleAllStaleJobs(event.target.checked)}
              />
              Select all visible stale/unknown jobs
            </label>

            {staleJobRows.map((job) => {
              const key = `${job.collectionName}:${job.id}`;
              return (
                <div
                  key={key}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "28px minmax(190px, 1fr) minmax(120px, 0.8fr) minmax(160px, 1fr) minmax(160px, 1fr)",
                    gap: 8,
                    padding: 10,
                    borderRadius: 8,
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    alignItems: "start",
                    minWidth: 820,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!selectedStaleJobs?.[key]}
                    onChange={(event) => toggleSelectedStaleJob(job, event.target.checked)}
                  />
                  <div>
                    <strong>{job.toolName}</strong>
                    <p style={{ ...muted, marginTop: 4 }}>Run ID: {job.id}</p>
                    <p style={{ ...muted, marginTop: 4 }}>Collection: {job.collectionName}</p>
                  </div>
                  <div>
                    <p style={muted}>Status: {job.status}</p>
                    <p style={muted}>Class: {job.classification}</p>
                    <p style={muted}>Age: {job.ageMinutes ?? "?"} min</p>
                  </div>
                  <div>
                    <p style={muted}>Email: {job.ownerEmail || "Unknown / not recorded"}</p>
                    <p style={muted}>UID: {job.ownerUid || "Unknown"}</p>
                    <p style={muted}>Project: {job.projectName || job.projectId || "None recorded"}</p>
                  </div>
                  <div>
                    <p style={muted}>
                      Date: {job.updatedAt ? new Date(job.updatedAt).toLocaleString() : "Unknown"}
                    </p>
                    <p style={muted}>Input: {job.inputSourceLabel || job.inputFileNames?.join(", ") || "None recorded"}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : staleJobs ? (
          <p style={muted}>No stale or unknown jobs were found.</p>
        ) : null}
      </div>
    </div>
  );
}

function WorkspaceOverview({
  setActiveTool,
  projects,
  activeProjectId,
  activeProject,
  setActiveProjectId,
  projectForm,
  setProjectForm,
  createProject,
  deleteProject,
  isCreatingProject,
  projectsLoading,
  projectsError,
  datasetForm,
  setDatasetForm,
  createDataset,
  deleteDataset,
  selectDatasetForAnalysis,
  datasets,
  datasetsLoading,
  datasetsError,
  isCreatingDataset,
  importPublicDataset,
  importingDatasetId,
  searchGeoDatasets,
  geoSearchResults,
  geoSearchLoading,
  geoSearchError,
  geoSupplementaryAccession,
  setGeoSupplementaryAccession,
  geoSupplementaryFiles,
  geoSupplementaryLoading,
  geoSupplementaryError,
  listGeoSupplementaryFiles,
  importGeoSupplementaryFile,
  importingGeoFileId,
  artifactForm,
  setArtifactForm,
  createArtifact,
  deleteArtifact,
  artifacts,
  artifactsLoading,
  artifactsError,
  isCreatingArtifact,
  toggleArtifactReportInclusion,
  runHistory,
  runHistoryLoading,
  runHistoryError,
  toggleRunReportInclusion,
  deleteRun,
  exportProjectReport,
  exportScientificPoster,
  generatePosterSummary,
  posterSummary,
  posterSummaryError,
  isGeneratingPosterSummary,
  adminKpis,
  adminKpisLoading,
  adminKpisError,
  refreshAdminKpis,
  staleJobs,
  staleJobsLoading,
  staleJobsError,
  staleJobsResult,
  selectedStaleJobs,
  previewStaleJobs,
  toggleSelectedStaleJob,
  toggleAllStaleJobs,
  deleteSelectedStaleJobs,
}) {
  const safeProjectForm = projectForm || { name: "", question: "", context: "" };
  const safeDatasetForm = datasetForm || { name: "", source: "", context: "", notes: "" };
  const safeArtifactForm = artifactForm || {
    title: "",
    type: "",
    runId: "",
    urlOrPath: "",
    notes: "",
  };
  const safeProjects = Array.isArray(projects) ? projects : [];
  const safeDatasets = Array.isArray(datasets) ? datasets : [];
  const safeArtifacts = Array.isArray(artifacts) ? artifacts : [];
  const safeRunHistory = Array.isArray(runHistory) ? runHistory : [];
  const reportRunCount = safeRunHistory.filter(shouldIncludeRunInReport).length;
  const reportArtifactCount = safeArtifacts.filter(shouldIncludeArtifactInReport).length;
  const safeGeoSearchResults = Array.isArray(geoSearchResults) ? geoSearchResults : [];
  const [publicDatasetQuery, setPublicDatasetQuery] = useState("");
  const normalizedPublicDatasetQuery = publicDatasetQuery.trim().toLowerCase();
  const publicDatasetMatches = PUBLIC_DATASET_LEADS.filter((dataset) =>
    [
      dataset.title,
      dataset.source,
      dataset.organism,
      dataset.topic,
      dataset.analysisFit,
      dataset.notes,
    ]
      .join(" ")
      .toLowerCase()
      .includes(normalizedPublicDatasetQuery)
  );

  const panel = {
    display: "grid",
    gap: 18,
  };
  const intro = {
    display: "grid",
    gap: 10,
    maxWidth: 920,
  };
  const eyebrow = {
    margin: 0,
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    color: "rgba(130,205,120,0.95)",
    textTransform: "uppercase",
  };
  const h2 = {
    margin: 0,
    fontSize: 26,
  };
  const p = {
    margin: 0,
    color: "rgba(255,255,255,0.72)",
    lineHeight: 1.55,
  };
  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
  };
  const item = {
    padding: 14,
    borderRadius: 8,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.10)",
    minHeight: 120,
  };
  const itemTitle = {
    marginTop: 0,
    marginBottom: 8,
    fontSize: 15,
  };
  const itemText = {
    margin: 0,
    color: "rgba(255,255,255,0.68)",
    fontSize: 13,
    lineHeight: 1.5,
  };
  const actions = {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  };
  const btn = {
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.08)",
    color: "white",
    fontWeight: 900,
    cursor: "pointer",
  };
  const input = {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    color: "white",
    outline: "none",
  };
  const label = {
    display: "grid",
    gap: 6,
    color: "rgba(255,255,255,0.78)",
    fontSize: 12,
    fontWeight: 900,
  };
  const projectCard = (active) => ({
    ...item,
    minHeight: 0,
    cursor: "pointer",
    borderColor: active ? "rgba(126,217,87,0.55)" : "rgba(255,255,255,0.10)",
    background: active ? "rgba(126,217,87,0.10)" : "rgba(255,255,255,0.04)",
  });
  const dangerBtn = {
    ...btn,
    padding: "7px 9px",
    borderColor: "rgba(255,107,107,0.35)",
    background: "rgba(255,107,107,0.10)",
    fontSize: 12,
  };
  const statusBadge = (status) => {
    const normalized = (status || "unknown").toLowerCase();
    const isDone = normalized === "completed" || normalized === "done";
    const isError = normalized === "error" || normalized === "failed";
    return {
      display: "inline-flex",
      alignItems: "center",
      width: "fit-content",
      padding: "4px 8px",
      borderRadius: 999,
      border: isError
        ? "1px solid rgba(255,107,107,0.35)"
        : isDone
          ? "1px solid rgba(126,217,87,0.35)"
          : "1px solid rgba(255,255,255,0.14)",
      background: isError
        ? "rgba(255,107,107,0.10)"
        : isDone
          ? "rgba(126,217,87,0.10)"
          : "rgba(255,255,255,0.06)",
      color: isError ? "#ffb6b6" : isDone ? "#c7f7c1" : "rgba(255,255,255,0.72)",
      fontSize: 11,
      fontWeight: 900,
      textTransform: "uppercase",
    };
  };

  const workspaceBlocks = [
    {
      title: "Project Folders",
      text: "Organize each research question into a workspace with datasets, analysis runs, notes, plots, AI interpretations, and exports.",
    },
    {
      title: "Dataset Upload / Import",
      text: "Keep direct CSV upload, then add guided public dataset import for GEO, SRA, TCGA-style sources, and classroom demo datasets.",
    },
    {
      title: "Analysis Tool Suite",
      text: "Keep DESeq2, PCA, heatmaps, ComBat-Seq, and the Gene Expression Predictor as reusable workflows inside each project.",
    },
    {
      title: "Neutral AI Interpretation",
      text: "Explain results without assuming disease area, tissue, organism, or phenotype unless the user provides that context.",
    },
    {
      title: "Report Export",
      text: "Generate a reproducible report with datasets, parameters, plots, result tables, interpretation, caveats, and methods language.",
    },
    {
      title: "Admin / User Roles",
      text: "Prepare for institutions with owner, instructor, student, researcher, and viewer roles plus audit-friendly access controls.",
    },
  ];

  const offerBlocks = [
    {
      title: "GATOR for Education",
      text: "Teach RNA-seq analysis without requiring students to install R, Bioconductor, Docker, or command-line pipelines.",
    },
    {
      title: "GATOR for Public Data Discovery",
      text: "Help users test biological hypotheses against public sequencing datasets and follow the evidence through guided workflows.",
    },
  ];

  return (
    <div style={panel}>
      <AdminKpiDashboard
        adminKpis={adminKpis}
        adminKpisLoading={adminKpisLoading}
        adminKpisError={adminKpisError}
        refreshAdminKpis={refreshAdminKpis}
        staleJobs={staleJobs}
        staleJobsLoading={staleJobsLoading}
        staleJobsError={staleJobsError}
        staleJobsResult={staleJobsResult}
        selectedStaleJobs={selectedStaleJobs}
        previewStaleJobs={previewStaleJobs}
        toggleSelectedStaleJob={toggleSelectedStaleJob}
        toggleAllStaleJobs={toggleAllStaleJobs}
        deleteSelectedStaleJobs={deleteSelectedStaleJobs}
      />

      <div style={{ ...item, minHeight: 0 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
          <form onSubmit={createProject} style={{ display: "grid", gap: 10 }}>
            <div>
              <p style={eyebrow}>Project Folders</p>
              <h2 style={{ ...h2, fontSize: 22 }}>Create a research project</h2>
            </div>
            <label style={label}>
              Project name
              <input
                style={input}
                value={safeProjectForm.name}
                onChange={(e) => setProjectForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Example: Diabetes public RNA-seq review"
              />
            </label>
            <label style={label}>
              Research question
              <input
                style={input}
                value={safeProjectForm.question}
                onChange={(e) => setProjectForm((prev) => ({ ...prev, question: e.target.value }))}
                placeholder="What do you want to investigate?"
              />
            </label>
            <label style={label}>
              Dataset or context
              <input
                style={input}
                value={safeProjectForm.context}
                onChange={(e) => setProjectForm((prev) => ({ ...prev, context: e.target.value }))}
                placeholder="Optional: tissue, disease, organism, class, or dataset source"
              />
            </label>
            <button style={btn} type="submit" disabled={isCreatingProject}>
              {isCreatingProject ? "Creating project..." : "Create project folder"}
            </button>
            {projectsError ? <p style={{ ...itemText, color: "#ffb6b6" }}>{projectsError}</p> : null}
          </form>

          <div style={{ display: "grid", gap: 10, alignContent: "start" }}>
            <div>
              <p style={eyebrow}>Active Workspace</p>
              <h2 style={{ ...h2, fontSize: 22 }}>Your projects</h2>
            </div>
            {projectsLoading ? (
              <p style={itemText}>Loading projects...</p>
            ) : safeProjects.length === 0 ? (
              <p style={itemText}>
                No project folders yet. Create one to start organizing datasets, runs, AI
                interpretations, and reports.
              </p>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {safeProjects.map((project) => (
                  <div
                    key={project.id}
                    style={projectCard(activeProjectId === project.id)}
                    onClick={() => setActiveProjectId(project.id)}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <div>
                        <h3 style={itemTitle}>{project.name}</h3>
                        {project.question ? <p style={itemText}>{project.question}</p> : null}
                        {project.context ? (
                          <p style={{ ...itemText, marginTop: 6, color: "rgba(255,255,255,0.50)" }}>
                            Context: {project.context}
                          </p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        style={dangerBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteProject(project.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ ...item, minHeight: 0 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
          <form onSubmit={createDataset} style={{ display: "grid", gap: 10 }}>
            <div>
              <p style={eyebrow}>Dataset Library</p>
              <h2 style={{ ...h2, fontSize: 22 }}>Register a dataset</h2>
              <p style={{ ...itemText, marginTop: 6 }}>
                Add uploaded datasets, public dataset leads, or classroom examples to the active project.
              </p>
            </div>
            <label style={label}>
              Dataset name
              <input
                style={input}
                value={safeDatasetForm.name}
                onChange={(e) => setDatasetForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Example: GSE diabetes beta-cell counts"
              />
            </label>
            <label style={label}>
              Source
              <input
                style={input}
                value={safeDatasetForm.source}
                onChange={(e) => setDatasetForm((prev) => ({ ...prev, source: e.target.value }))}
                placeholder="Upload, GEO, SRA, TCGA, ArrayExpress, classroom demo"
              />
            </label>
            <label style={label}>
              Dataset context
              <input
                style={input}
                value={safeDatasetForm.context}
                onChange={(e) => setDatasetForm((prev) => ({ ...prev, context: e.target.value }))}
                placeholder="Optional: organism, tissue, disease, treatment, cohort"
              />
            </label>
            <label style={label}>
              Notes
              <textarea
                style={{ ...input, minHeight: 82, resize: "vertical" }}
                value={safeDatasetForm.notes}
                onChange={(e) => setDatasetForm((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Why this dataset matters, accessions, sample notes, or planned analyses"
              />
            </label>
            <button style={btn} type="submit" disabled={isCreatingDataset}>
              {isCreatingDataset ? "Adding dataset..." : "Add dataset to project"}
            </button>
            {datasetsError ? <p style={{ ...itemText, color: "#ffb6b6" }}>{datasetsError}</p> : null}
          </form>

          <div style={{ display: "grid", gap: 10, alignContent: "start" }}>
            <div>
              <p style={eyebrow}>Project Datasets</p>
              <h2 style={{ ...h2, fontSize: 22 }}>Dataset library</h2>
            </div>
            {!activeProjectId ? (
              <p style={itemText}>Select a project folder to see its dataset library.</p>
            ) : datasetsLoading ? (
              <p style={itemText}>Loading datasets...</p>
            ) : safeDatasets.length === 0 ? (
              <p style={itemText}>
                No datasets registered yet. Add a dataset record to prepare for analysis, reporting,
                and future public dataset imports.
              </p>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {safeDatasets.map((dataset) => (
                  <div key={dataset.id} style={{ ...projectCard(false), cursor: "default" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <div>
                        <h3 style={itemTitle}>{dataset.name}</h3>
                        {dataset.source ? <p style={itemText}>Source: {dataset.source}</p> : null}
                        <p style={{ ...itemText, marginTop: 6, color: "rgba(130,205,120,0.82)" }}>
                          Kind: {dataset.datasetKind || "dataset-lead"}
                          {dataset.reviewStatus ? ` | ${dataset.reviewStatus}` : ""}
                        </p>
                        {dataset.storagePath ? (
                          <p style={{ ...itemText, marginTop: 6 }}>Imported file: {dataset.storagePath}</p>
                        ) : null}
                        {dataset.context ? (
                          <p style={{ ...itemText, marginTop: 6 }}>Context: {dataset.context}</p>
                        ) : null}
                        {dataset.notes ? (
                          <p style={{ ...itemText, marginTop: 6, color: "rgba(255,255,255,0.52)" }}>
                            {dataset.notes}
                          </p>
                        ) : null}
                        {dataset.sourceUrl ? (
                          <button
                            type="button"
                            style={{ ...btn, marginTop: 8, padding: "7px 9px", fontSize: 12 }}
                            onClick={() => window.open(dataset.sourceUrl, "_blank", "noopener,noreferrer")}
                          >
                            Open source
                          </button>
                        ) : null}
                        {dataset.storagePath ? (
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                            <button
                              type="button"
                              style={{ ...btn, padding: "7px 9px", fontSize: 12 }}
                              onClick={() => selectDatasetForAnalysis(dataset, "heatmap")}
                            >
                              Use for Heatmap
                            </button>
                            <button
                              type="button"
                              style={{ ...btn, padding: "7px 9px", fontSize: 12 }}
                              onClick={() => selectDatasetForAnalysis(dataset, "pca")}
                            >
                              Use for PCA
                            </button>
                            <button
                              type="button"
                              style={{ ...btn, padding: "7px 9px", fontSize: 12 }}
                              onClick={() => selectDatasetForAnalysis(dataset, "deseq2Counts")}
                            >
                              Use as DESeq2 counts
                            </button>
                            <button
                              type="button"
                              style={{ ...btn, padding: "7px 9px", fontSize: 12 }}
                              onClick={() => selectDatasetForAnalysis(dataset, "deseq2Metadata")}
                            >
                              Use as DESeq2 metadata
                            </button>
                            <button
                              type="button"
                              style={{ ...btn, padding: "7px 9px", fontSize: 12 }}
                              onClick={() => selectDatasetForAnalysis(dataset, "combatCounts")}
                            >
                              Use as ComBat counts
                            </button>
                            <button
                              type="button"
                              style={{ ...btn, padding: "7px 9px", fontSize: 12 }}
                              onClick={() => selectDatasetForAnalysis(dataset, "combatMetadata")}
                            >
                              Use as ComBat metadata
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        style={dangerBtn}
                        onClick={() => deleteDataset(dataset.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ ...item, minHeight: 0, borderColor: "rgba(91,176,255,0.35)" }}>
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
            <div style={{ maxWidth: 760 }}>
              <p style={eyebrow}>Public Dataset Explorer v2</p>
              <h2 style={{ ...h2, fontSize: 22 }}>Search GEO dataset metadata</h2>
              <p style={{ ...itemText, marginTop: 6 }}>
                Search live GEO dataset metadata first, then import promising candidates into the
                active project dataset library for review and downstream analysis planning.
              </p>
            </div>
            <label style={{ ...label, minWidth: 260, flex: "1 1 260px", maxWidth: 420 }}>
              Search term
              <input
                style={input}
                value={publicDatasetQuery}
                onChange={(e) => setPublicDatasetQuery(e.target.value)}
                placeholder="Example: diabetes beta cell, breast cancer, glioblastoma"
              />
            </label>
          </div>

          <div style={actions}>
            <button
              style={btn}
              type="button"
              onClick={() => searchGeoDatasets(publicDatasetQuery)}
              disabled={geoSearchLoading || publicDatasetQuery.trim().length < 2}
            >
              {geoSearchLoading ? "Searching GEO..." : "Search GEO"}
            </button>
            <button
              style={btn}
              type="button"
              onClick={() => setPublicDatasetQuery("diabetes beta cell RNA-seq")}
            >
              Diabetes example
            </button>
            <button
              style={btn}
              type="button"
              onClick={() => setPublicDatasetQuery("breast cancer RNA-seq")}
            >
              Cancer example
            </button>
          </div>

          {geoSearchError ? (
            <p style={{ ...itemText, color: "#ffb6b6" }}>{geoSearchError}</p>
          ) : null}

          {safeGeoSearchResults.length > 0 ? (
            <div style={{ display: "grid", gap: 10 }}>
              <p style={{ ...eyebrow, color: "rgba(91,176,255,0.95)" }}>Live GEO Results</p>
              <div style={{ ...grid, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
                {safeGeoSearchResults.map((dataset) => (
                  <div
                    key={dataset.id}
                    style={{
                      ...projectCard(false),
                      cursor: "default",
                      borderColor: "rgba(91,176,255,0.30)",
                    }}
                  >
                    <div style={{ display: "grid", gap: 8 }}>
                      <div>
                        <h3 style={itemTitle}>
                          {dataset.accession ? `${dataset.accession}: ` : ""}
                          {dataset.title}
                        </h3>
                        <p style={itemText}>
                          GEO | {dataset.organism || "Organism not specified"}
                        </p>
                      </div>
                      {dataset.summary ? (
                        <p style={{ ...itemText, color: "rgba(255,255,255,0.56)" }}>
                          {dataset.summary.length > 280
                            ? `${dataset.summary.slice(0, 280)}...`
                            : dataset.summary}
                        </p>
                      ) : null}
                      <p style={itemText}>
                        Samples: {dataset.sampleCount || "not listed"} | Type:{" "}
                        {dataset.gdsType || "not listed"}
                      </p>
                      {dataset.publicationDate ? (
                        <p style={itemText}>Date: {dataset.publicationDate}</p>
                      ) : null}
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                        <button
                          style={btn}
                          type="button"
                          onClick={() => window.open(dataset.sourceUrl, "_blank", "noopener,noreferrer")}
                        >
                          Open GEO
                        </button>
                        <button
                          style={btn}
                          type="button"
                          disabled={!activeProjectId || importingDatasetId === dataset.id}
                          onClick={() => importPublicDataset(dataset)}
                        >
                          {importingDatasetId === dataset.id ? "Importing..." : "Import to project"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div style={{ display: "grid", gap: 10 }}>
            <p style={eyebrow}>Curated Starting Points</p>
            <p style={itemText}>
              These are starter leads for GEO, SRA, GDC/TCGA, and classroom demos while direct
              SRA/GDC import workflows are being built.
            </p>
          </div>
          <div style={{ ...grid, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
            {publicDatasetMatches.length === 0 ? (
              <p style={itemText}>No public dataset leads match that search yet.</p>
            ) : (
              publicDatasetMatches.map((dataset) => (
                <div
                  key={dataset.id}
                  style={{
                    ...projectCard(false),
                    cursor: "default",
                    borderColor: "rgba(91,176,255,0.22)",
                  }}
                >
                  <div style={{ display: "grid", gap: 8 }}>
                    <div>
                      <h3 style={itemTitle}>{dataset.title}</h3>
                      <p style={itemText}>
                        {dataset.source} | {dataset.organism}
                      </p>
                    </div>
                    <p style={itemText}>Topic: {dataset.topic}</p>
                    <p style={itemText}>Best fit: {dataset.analysisFit}</p>
                    <p style={{ ...itemText, color: "rgba(255,255,255,0.52)" }}>{dataset.notes}</p>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                      <button
                        style={btn}
                        type="button"
                        onClick={() => window.open(dataset.url, "_blank", "noopener,noreferrer")}
                      >
                        Open source
                      </button>
                      <button
                        style={btn}
                        type="button"
                        disabled={!activeProjectId || importingDatasetId === dataset.id}
                        onClick={() => importPublicDataset(dataset)}
                      >
                        {importingDatasetId === dataset.id ? "Importing..." : "Import to project"}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div style={{ ...item, minHeight: 0, borderColor: "rgba(91,176,255,0.28)" }}>
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
            <div style={{ maxWidth: 760 }}>
              <p style={eyebrow}>GEO Supplementary File Import v1</p>
              <h2 style={{ ...h2, fontSize: 22 }}>Import an analysis file from GEO</h2>
              <p style={{ ...itemText, marginTop: 6 }}>
                Use a GEO accession to find supplementary files, then import selected count or metadata
                files into the active project. Imported files are tracked separately from dataset leads.
              </p>
            </div>
            <label style={{ ...label, minWidth: 220, flex: "1 1 220px", maxWidth: 360 }}>
              GEO accession
              <input
                style={input}
                value={geoSupplementaryAccession}
                onChange={(e) => setGeoSupplementaryAccession(e.target.value.toUpperCase())}
                placeholder="Example: GSE305855"
              />
            </label>
          </div>

          <div style={actions}>
            <button
              style={btn}
              type="button"
              onClick={listGeoSupplementaryFiles}
              disabled={geoSupplementaryLoading || !activeProjectId}
            >
              {geoSupplementaryLoading ? "Finding files..." : "Find GEO files"}
            </button>
          </div>

          {geoSupplementaryError ? (
            <p
              style={{
                ...itemText,
                color: geoSupplementaryError.startsWith("Imported") ? "rgba(130,205,120,0.9)" : "#ffb6b6",
              }}
            >
              {geoSupplementaryError}
            </p>
          ) : null}

          {geoSupplementaryFiles.length ? (
            <div style={{ display: "grid", gap: 10 }}>
              {geoSupplementaryFiles.map((file) => (
                <div key={file.id} style={{ ...projectCard(false), cursor: "default" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <div>
                      <h3 style={itemTitle}>{file.filename}</h3>
                      <p style={itemText}>
                        {file.likelyAnalysisFile ? "Likely analysis file" : "Supplementary file"} |{" "}
                        {file.isCompressed ? "compressed" : "not compressed"}
                      </p>
                      <p style={{ ...itemText, marginTop: 6, color: "rgba(255,255,255,0.52)" }}>
                        {file.url}
                      </p>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
                      <button
                        style={btn}
                        type="button"
                        onClick={() => window.open(file.url, "_blank", "noopener,noreferrer")}
                      >
                        Open file
                      </button>
                      <button
                        style={btn}
                        type="button"
                        disabled={importingGeoFileId === file.id || !activeProjectId}
                        onClick={() => importGeoSupplementaryFile(file)}
                      >
                        {importingGeoFileId === file.id ? "Importing..." : "Import file"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div style={intro}>
        <p style={eyebrow}>Research Workspace v1</p>
        <h2 style={h2}>Turn GATOR into a guided research workspace.</h2>
        <p style={p}>
          GATOR can grow from a set of analysis tools into a project-based environment where users
          upload or import datasets, run reproducible RNA-seq workflows, interpret results with
          careful AI support, and export reports they can use for teaching, exploration, or research.
        </p>
      </div>

      <div style={{ ...item, minHeight: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <p style={eyebrow}>Run History</p>
            <h2 style={{ ...h2, fontSize: 22 }}>Project analysis runs</h2>
          </div>
          <p style={{ ...itemText, maxWidth: 420 }}>
            New runs from the analysis tools are attached to the selected project and appear here.
          </p>
        </div>

        {runHistoryError ? (
          <p style={{ ...itemText, color: "#ffb6b6", marginTop: 12 }}>{runHistoryError}</p>
        ) : runHistoryLoading ? (
          <p style={{ ...itemText, marginTop: 12 }}>Loading run history...</p>
        ) : safeRunHistory.length === 0 ? (
          <p style={{ ...itemText, marginTop: 12 }}>
            No runs are attached to this project yet. Select this project, open an analysis tool,
            and start a new run.
          </p>
        ) : (
          <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
            {safeRunHistory.map((run) => (
              <div
                key={`${run.collectionName}-${run.id}`}
                style={{ ...projectCard(false), cursor: "default" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ display: "grid", gap: 6 }}>
                    <h3 style={itemTitle}>{run.toolName}</h3>
                    <span style={statusBadge(run.status)}>{run.status || "unknown"}</span>
                    <p style={itemText}>{formatRunTime(run)}</p>
                    <p style={{ ...itemText, color: "rgba(255,255,255,0.48)" }}>
                      Run ID: {run.id}
                    </p>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        color: "rgba(255,255,255,0.72)",
                        fontSize: 12,
                        fontWeight: 900,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={shouldIncludeRunInReport(run)}
                        onChange={(e) => toggleRunReportInclusion(run, e.target.checked)}
                      />
                      Include in report
                    </label>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
                    <button style={btn} type="button" onClick={() => setActiveTool(run.toolKey)}>
                      Open tool
                    </button>
                    <button style={dangerBtn} type="button" onClick={() => deleteRun(run)}>
                      Delete run
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ ...item, minHeight: 0 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
          <form onSubmit={createArtifact} style={{ display: "grid", gap: 10 }}>
            <div>
              <p style={eyebrow}>Artifact Library</p>
              <h2 style={{ ...h2, fontSize: 22 }}>Register a result artifact</h2>
              <p style={{ ...itemText, marginTop: 6 }}>
                Track plots, tables, CSVs, AI interpretations, summaries, and figure candidates for reports or posters.
              </p>
            </div>
            <label style={label}>
              Artifact title
              <input
                style={input}
                value={safeArtifactForm.title}
                onChange={(e) => setArtifactForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Example: DESeq2 volcano plot"
              />
            </label>
            <label style={label}>
              Artifact type
              <input
                style={input}
                value={safeArtifactForm.type}
                onChange={(e) => setArtifactForm((prev) => ({ ...prev, type: e.target.value }))}
                placeholder="Plot, CSV, table, AI interpretation, summary, poster figure"
              />
            </label>
            <label style={label}>
              Related run ID
              <input
                style={input}
                value={safeArtifactForm.runId}
                onChange={(e) => setArtifactForm((prev) => ({ ...prev, runId: e.target.value }))}
                placeholder="Optional: paste a Run History ID"
              />
            </label>
            <label style={label}>
              URL or storage path
              <input
                style={input}
                value={safeArtifactForm.urlOrPath}
                onChange={(e) => setArtifactForm((prev) => ({ ...prev, urlOrPath: e.target.value }))}
                placeholder="Optional: Storage path, download URL, or figure reference"
              />
            </label>
            <label style={label}>
              Notes
              <textarea
                style={{ ...input, minHeight: 82, resize: "vertical" }}
                value={safeArtifactForm.notes}
                onChange={(e) => setArtifactForm((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Why this output matters, figure caption draft, or poster/report notes"
              />
            </label>
            <button style={btn} type="submit" disabled={isCreatingArtifact}>
              {isCreatingArtifact ? "Adding artifact..." : "Add artifact to project"}
            </button>
            {artifactsError ? <p style={{ ...itemText, color: "#ffb6b6" }}>{artifactsError}</p> : null}
          </form>

          <div style={{ display: "grid", gap: 10, alignContent: "start" }}>
            <div>
              <p style={eyebrow}>Project Artifacts</p>
              <h2 style={{ ...h2, fontSize: 22 }}>Artifact library</h2>
            </div>
            {!activeProjectId ? (
              <p style={itemText}>Select a project folder to see its artifact library.</p>
            ) : artifactsLoading ? (
              <p style={itemText}>Loading artifacts...</p>
            ) : safeArtifacts.length === 0 ? (
              <p style={itemText}>
                No artifacts registered yet. Add important outputs now; later GATOR can capture plots,
                tables, and AI interpretations automatically.
              </p>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {safeArtifacts.map((artifact) => (
                  <div key={artifact.id} style={{ ...projectCard(false), cursor: "default" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <div>
                        <h3 style={itemTitle}>{artifact.title}</h3>
                        {artifact.type ? <p style={itemText}>Type: {artifact.type}</p> : null}
                        {artifact.runId ? (
                          <p style={{ ...itemText, marginTop: 6 }}>Run ID: {artifact.runId}</p>
                        ) : null}
                        {artifact.urlOrPath ? (
                          <p style={{ ...itemText, marginTop: 6 }}>Path: {artifact.urlOrPath}</p>
                        ) : null}
                        {artifact.notes ? (
                          <p style={{ ...itemText, marginTop: 6, color: "rgba(255,255,255,0.52)" }}>
                            {artifact.notes}
                          </p>
                        ) : null}
                        {artifact.autoCaptured ? (
                          <p style={{ ...itemText, marginTop: 6, color: "rgba(130,205,120,0.78)" }}>
                            Auto-captured from run history
                          </p>
                        ) : null}
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginTop: 8,
                            color: "rgba(255,255,255,0.72)",
                            fontSize: 12,
                            fontWeight: 900,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={shouldIncludeArtifactInReport(artifact)}
                            onChange={(e) => toggleArtifactReportInclusion(artifact.id, e.target.checked)}
                          />
                          Include in report
                        </label>
                      </div>
                      <button
                        type="button"
                        style={dangerBtn}
                        onClick={() => deleteArtifact(artifact.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ ...item, minHeight: 0, borderColor: "rgba(130,205,120,0.28)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
          <div style={{ maxWidth: 680 }}>
            <p style={eyebrow}>Report Export</p>
            <h2 style={{ ...h2, fontSize: 22 }}>Export project report</h2>
            <p style={{ ...itemText, marginTop: 8 }}>
              Download a curated Markdown report for reproducibility, or export a scientific poster
              HTML file for visual communication and PDF printing.
            </p>
            <p style={{ ...itemText, marginTop: 8, color: "rgba(255,255,255,0.52)" }}>
              Current report: {activeProject?.name || "No active project"} | {safeDatasets.length} dataset
              {safeDatasets.length === 1 ? "" : "s"} | {reportRunCount} report run
              {reportRunCount === 1 ? "" : "s"} | {reportArtifactCount} report artifact
              {reportArtifactCount === 1 ? "" : "s"}
            </p>
            {posterSummary ? (
              <p style={{ ...itemText, marginTop: 8, color: "rgba(130,205,120,0.82)" }}>
                {posterSummary.localFallback || posterSummary.aiUnavailable
                  ? "Poster summary fallback is ready and will be included in the scientific poster."
                  : "AI poster summary is ready and will be included in the scientific poster."}
              </p>
            ) : null}
            {posterSummaryError ? (
              <p style={{ ...itemText, marginTop: 8, color: "#ffb6b6" }}>{posterSummaryError}</p>
            ) : null}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
            <button
              style={btn}
              type="button"
              onClick={generatePosterSummary}
              disabled={!activeProject || isGeneratingPosterSummary}
            >
              {isGeneratingPosterSummary ? "Generating AI summary..." : "Generate AI Poster Summary"}
            </button>
            <button
              style={btn}
              type="button"
              onClick={exportProjectReport}
              disabled={!activeProject}
            >
              Download Markdown report
            </button>
            <button
              style={btn}
              type="button"
              onClick={exportScientificPoster}
              disabled={!activeProject}
            >
              Download Scientific Poster HTML
            </button>
          </div>
        </div>
      </div>

      <div style={grid}>
        {workspaceBlocks.map((block) => (
          <div key={block.title} style={item}>
            <h3 style={itemTitle}>{block.title}</h3>
            <p style={itemText}>{block.text}</p>
          </div>
        ))}
      </div>

      <div style={grid}>
        {offerBlocks.map((block) => (
          <div key={block.title} style={{ ...item, borderColor: "rgba(130,205,120,0.28)" }}>
            <h3 style={itemTitle}>{block.title}</h3>
            <p style={itemText}>{block.text}</p>
          </div>
        ))}
      </div>

      <div style={actions}>
        <button style={btn} onClick={() => setActiveTool("deseq2")}>
          Start with DESeq2
        </button>
        <button style={btn} onClick={() => setActiveTool("pca")}>
          Explore PCA
        </button>
        <button style={btn} onClick={() => setActiveTool("predictor")}>
          Open Predictor
        </button>
      </div>
    </div>
  );
}

function WorkspaceCardVisual({ type }) {
  const base = {
    height: 120,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "#101820",
    overflow: "hidden",
    display: "grid",
    padding: 12,
    boxSizing: "border-box",
  };
  const header = {
    height: 8,
    borderRadius: 999,
    background: "rgba(255,255,255,0.16)",
    marginBottom: 10,
  };
  const miniText = {
    height: 7,
    borderRadius: 999,
    background: "rgba(255,255,255,0.20)",
  };
  const accent = {
    background: "linear-gradient(135deg, #7ed957, #40b9a2)",
  };

  if (type === "education") {
    return (
      <div style={{ ...base, background: "linear-gradient(135deg, #101820, #17222c)" }}>
        <div style={{ ...header, width: "44%" }} />
        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 10 }}>
          <div style={{ display: "grid", gap: 7 }}>
            <div style={{ ...miniText, ...accent, width: "82%" }} />
            <div style={{ ...miniText, width: "64%" }} />
            <div style={{ ...miniText, width: "74%" }} />
            <div style={{ ...miniText, width: "52%" }} />
          </div>
          <div
            style={{
              borderRadius: 10,
              border: "1px solid rgba(126,217,87,0.35)",
              background: "rgba(126,217,87,0.10)",
              display: "grid",
              placeItems: "center",
              color: "rgba(255,255,255,0.86)",
              fontSize: 12,
              fontWeight: 900,
            }}
          >
            LESSON
          </div>
        </div>
      </div>
    );
  }

  if (type === "discovery") {
    return (
      <div style={{ ...base, background: "linear-gradient(135deg, #101820, #18212a)" }}>
        <div
          style={{
            height: 24,
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.14)",
            background: "rgba(255,255,255,0.06)",
            display: "flex",
            alignItems: "center",
            padding: "0 10px",
            gap: 8,
            marginBottom: 10,
          }}
        >
          <div style={{ width: 8, height: 8, borderRadius: "50%", ...accent }} />
          <div style={{ ...miniText, width: "66%" }} />
        </div>
        <div style={{ display: "grid", gap: 7 }}>
          {[72, 88, 58].map((width) => (
            <div
              key={width}
              style={{
                height: 14,
                borderRadius: 6,
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.08)",
                width: `${width}%`,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...base, background: "linear-gradient(135deg, #101820, #1c252d)" }}>
      <div style={{ ...header, width: "38%" }} />
      <div style={{ display: "grid", gridTemplateColumns: "0.8fr 1.2fr", gap: 10 }}>
        <div style={{ display: "grid", gap: 7 }}>
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              style={{
                height: 18,
                borderRadius: 7,
                background: n === 1 ? "rgba(126,217,87,0.18)" : "rgba(255,255,255,0.07)",
                border: n === 1 ? "1px solid rgba(126,217,87,0.35)" : "1px solid rgba(255,255,255,0.08)",
              }}
            />
          ))}
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ ...miniText, ...accent, width: "70%" }} />
          <div style={{ ...miniText, width: "88%" }} />
          <div style={{ ...miniText, width: "58%" }} />
          <div style={{ height: 26, borderRadius: 8, background: "rgba(255,255,255,0.08)" }} />
        </div>
      </div>
    </div>
  );
}

function App() {
  const [activeTool, setActiveTool] = useState("workspace");
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [visitCount, setVisitCount] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [projectForm, setProjectForm] = useState({
    name: "",
    question: "",
    context: "",
  });
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [runHistory, setRunHistory] = useState([]);
  const [runHistoryLoading, setRunHistoryLoading] = useState(false);
  const [runHistoryError, setRunHistoryError] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [datasetsLoading, setDatasetsLoading] = useState(false);
  const [datasetsError, setDatasetsError] = useState(null);
  const [datasetForm, setDatasetForm] = useState({
    name: "",
    source: "",
    context: "",
    notes: "",
  });
  const [isCreatingDataset, setIsCreatingDataset] = useState(false);
  const [importingDatasetId, setImportingDatasetId] = useState(null);
  const [geoSearchResults, setGeoSearchResults] = useState([]);
  const [geoSearchLoading, setGeoSearchLoading] = useState(false);
  const [geoSearchError, setGeoSearchError] = useState(null);
  const [geoSupplementaryAccession, setGeoSupplementaryAccession] = useState("");
  const [geoSupplementaryFiles, setGeoSupplementaryFiles] = useState([]);
  const [geoSupplementaryLoading, setGeoSupplementaryLoading] = useState(false);
  const [geoSupplementaryError, setGeoSupplementaryError] = useState(null);
  const [importingGeoFileId, setImportingGeoFileId] = useState(null);
  const [analysisInputs, setAnalysisInputs] = useState({
    heatmap: null,
    pca: null,
    deseq2Counts: null,
    deseq2Metadata: null,
    combatCounts: null,
    combatMetadata: null,
  });
  const [artifacts, setArtifacts] = useState([]);
  const [artifactsLoading, setArtifactsLoading] = useState(false);
  const [artifactsError, setArtifactsError] = useState(null);
  const [artifactForm, setArtifactForm] = useState({
    title: "",
    type: "",
    runId: "",
    urlOrPath: "",
    notes: "",
  });
  const [isCreatingArtifact, setIsCreatingArtifact] = useState(false);
  const [posterSummary, setPosterSummary] = useState(null);
  const [posterSummaryError, setPosterSummaryError] = useState(null);
  const [isGeneratingPosterSummary, setIsGeneratingPosterSummary] = useState(false);
  const [adminKpis, setAdminKpis] = useState(null);
  const [adminKpisLoading, setAdminKpisLoading] = useState(false);
  const [adminKpisError, setAdminKpisError] = useState(null);
  const [staleJobs, setStaleJobs] = useState(null);
  const [staleJobsLoading, setStaleJobsLoading] = useState(false);
  const [staleJobsError, setStaleJobsError] = useState(null);
  const [staleJobsResult, setStaleJobsResult] = useState(null);
  const [selectedStaleJobs, setSelectedStaleJobs] = useState({});
  const autoCaptureKeysRef = React.useRef(new Set());

  const refreshAdminKpis = async () => {
    if (!user) {
      setAdminKpis(null);
      setAdminKpisError("Sign in to view admin KPIs.");
      return;
    }

    setAdminKpisLoading(true);
    setAdminKpisError(null);

    try {
      const getAdminKpis = httpsCallable(getFunctions(app, "us-central1"), "getAdminKpis");
      const response = await getAdminKpis();
      setAdminKpis(response.data || null);
    } catch (err) {
      console.error("Failed to load admin KPIs:", err);
      const detail = err?.details || err?.message || err?.code || err;
      setAdminKpis(null);
      setAdminKpisError("Could not load admin KPIs: " + detail);
    } finally {
      setAdminKpisLoading(false);
    }
  };

  const previewStaleJobs = async () => {
    if (!user) {
      setStaleJobsError("Sign in to preview stale jobs.");
      return;
    }

    setStaleJobsLoading(true);
    setStaleJobsError(null);
    setStaleJobsResult(null);

    try {
      const preview = httpsCallable(getFunctions(app, "us-central1"), "previewStaleJobs");
      const response = await preview({ includeUnknown: true, limit: 500 });
      setStaleJobs(response.data || null);
      setSelectedStaleJobs({});
    } catch (err) {
      console.error("Failed to preview stale jobs:", err);
      const detail = err?.details || err?.message || err?.code || err;
      setStaleJobsError("Could not preview stale jobs: " + detail);
    } finally {
      setStaleJobsLoading(false);
    }
  };

  const toggleSelectedStaleJob = (job, selected) => {
    const key = `${job.collectionName}:${job.id}`;
    setSelectedStaleJobs((prev) => ({
      ...prev,
      [key]: selected,
    }));
  };

  const toggleAllStaleJobs = (selected) => {
    const rows = Array.isArray(staleJobs?.jobs) ? staleJobs.jobs : [];
    const next = {};
    rows.forEach((job) => {
      next[`${job.collectionName}:${job.id}`] = selected;
    });
    setSelectedStaleJobs(next);
  };

  const deleteSelectedStaleJobs = async () => {
    const rows = Array.isArray(staleJobs?.jobs) ? staleJobs.jobs : [];
    const selectedJobs = rows
      .filter((job) => selectedStaleJobs[`${job.collectionName}:${job.id}`])
      .map((job) => ({ collectionName: job.collectionName, id: job.id }));

    if (!selectedJobs.length) {
      setStaleJobsError("Select at least one stale job to delete.");
      return;
    }

    const confirmed = window.confirm(
      `Delete ${selectedJobs.length} selected stale/unknown Firestore run record(s)? This will not delete Storage files.`
    );
    if (!confirmed) return;

    setStaleJobsLoading(true);
    setStaleJobsError(null);
    setStaleJobsResult(null);

    try {
      const deleteFn = httpsCallable(getFunctions(app, "us-central1"), "deleteStaleJobs");
      const response = await deleteFn({ jobs: selectedJobs });
      const deletedCount = response.data?.deletedCount || 0;
      const skippedCount = response.data?.skippedCount || 0;
      await previewStaleJobs();
      await refreshAdminKpis();
      setStaleJobsResult(`Deleted ${deletedCount} record(s). Skipped ${skippedCount}.`);
    } catch (err) {
      console.error("Failed to delete stale jobs:", err);
      const detail = err?.details || err?.message || err?.code || err;
      setStaleJobsError("Could not delete stale jobs: " + detail);
    } finally {
      setStaleJobsLoading(false);
    }
  };

  useEffect(() => {
    console.log("Starting authentication process...");
    authPromise
      .then(() => {
        console.log("authPromise resolved, setting up onAuthStateChanged...");
        setTimeout(() => {
          const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            console.log("onAuthStateChanged fired, currentUser:", currentUser);
            setUser(currentUser);
            setAuthLoading(false);
          });
          return () => {
            console.log("Cleaning up onAuthStateChanged listener");
            unsubscribe();
          };
        }, 3000);
      })
      .catch((err) => {
        console.error("authPromise rejected:", err);
        setAuthLoading(false);
      });
  }, []);
  
  useEffect(() => {
  // Only do this after we know who the user is
  if (!user) return;

  const run = async () => {
    try {
      const functions = getFunctions(app, "us-central1");
      const incrementVisitorCount = httpsCallable(
        functions,
        "incrementVisitorCount"
      );

      const res = await incrementVisitorCount();
      if (res?.data?.totalVisits !== undefined) {
        setVisitCount(res.data.totalVisits);
      }
    } catch (err) {
      console.error("Failed to increment visitor count:", err);
      // no need to show this error in the UI; it's just a nicety
    }
  };

  run();
}, [user]);

  useEffect(() => {
    if (!user) {
      setAdminKpis(null);
      setAdminKpisError(null);
      return;
    }

    refreshAdminKpis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setProjects([]);
      setActiveProjectId(null);
      setProjectsLoading(false);
      return undefined;
    }

    setProjectsLoading(true);
    setProjectsError(null);

    const projectsRef = collection(db, "users", user.uid, "projects");
    const projectsQuery = query(projectsRef, orderBy("updatedAt", "desc"));

    const unsubscribe = onSnapshot(
      projectsQuery,
      (snapshot) => {
        const nextProjects = snapshot.docs.map((projectDoc) => ({
          id: projectDoc.id,
          ...projectDoc.data(),
        }));

        setProjects(nextProjects);
        setProjectsLoading(false);
        setActiveProjectId((currentId) => {
          if (currentId && nextProjects.some((project) => project.id === currentId)) {
            return currentId;
          }
          return nextProjects[0]?.id || null;
        });
      },
      (err) => {
        console.error("Failed to load project folders:", err);
        setProjectsError("Could not load project folders: " + (err.message || err));
        setProjectsLoading(false);
      }
    );

    return unsubscribe;
  }, [user]);

  const createProject = async (event) => {
    event.preventDefault();
    if (!user?.uid) return;

    const name = projectForm.name.trim();
    const question = projectForm.question.trim();
    const context = projectForm.context.trim();

    if (!name) {
      setProjectsError("Project name is required.");
      return;
    }

    setIsCreatingProject(true);
    setProjectsError(null);

    try {
      const projectRef = await addDoc(collection(db, "users", user.uid, "projects"), {
        name,
        question,
        context,
        ownerUid: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setProjectForm({ name: "", question: "", context: "" });
      setActiveProjectId(projectRef.id);
    } catch (err) {
      console.error("Failed to create project folder:", err);
      setProjectsError("Could not create project folder: " + (err.message || err));
    } finally {
      setIsCreatingProject(false);
    }
  };

  const deleteProject = async (projectId) => {
    if (!user?.uid || !projectId) return;

    try {
      await deleteDoc(doc(db, "users", user.uid, "projects", projectId));
      setActiveProjectId((currentId) => (currentId === projectId ? null : currentId));
    } catch (err) {
      console.error("Failed to delete project folder:", err);
      setProjectsError("Could not delete project folder: " + (err.message || err));
    }
  };

  useEffect(() => {
    if (!user?.uid || !activeProjectId) {
      setDatasets([]);
      setDatasetsLoading(false);
      setDatasetsError(null);
      return undefined;
    }

    setDatasetsLoading(true);
    setDatasetsError(null);

    const datasetsRef = collection(db, "users", user.uid, "projects", activeProjectId, "datasets");
    const datasetsQuery = query(datasetsRef, orderBy("updatedAt", "desc"));

    const unsubscribe = onSnapshot(
      datasetsQuery,
      (snapshot) => {
        setDatasets(snapshot.docs.map((datasetDoc) => ({
          id: datasetDoc.id,
          ...datasetDoc.data(),
        })));
        setDatasetsLoading(false);
      },
      (err) => {
        console.error("Failed to load dataset library:", err);
        setDatasetsError("Could not load dataset library: " + (err.message || err));
        setDatasetsLoading(false);
      }
    );

    return unsubscribe;
  }, [user, activeProjectId]);

  const createDataset = async (event) => {
    event.preventDefault();

    if (!user?.uid || !activeProjectId) {
      setDatasetsError("Select a project before adding datasets.");
      return;
    }

    const name = datasetForm.name.trim();
    const source = datasetForm.source.trim();
    const context = datasetForm.context.trim();
    const notes = datasetForm.notes.trim();

    if (!name) {
      setDatasetsError("Dataset name is required.");
      return;
    }

    setIsCreatingDataset(true);
    setDatasetsError(null);

    try {
      await addDoc(collection(db, "users", user.uid, "projects", activeProjectId, "datasets"), {
        name,
        source,
        context,
        notes,
        datasetKind: "registered-dataset",
        reviewStatus: "Registered",
        ownerUid: user.uid,
        projectId: activeProjectId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setDatasetForm({ name: "", source: "", context: "", notes: "" });
    } catch (err) {
      console.error("Failed to create dataset record:", err);
      setDatasetsError("Could not add dataset: " + (err.message || err));
    } finally {
      setIsCreatingDataset(false);
    }
  };

  const deleteDataset = async (datasetId) => {
    if (!user?.uid || !activeProjectId || !datasetId) return;

    try {
      await deleteDoc(doc(db, "users", user.uid, "projects", activeProjectId, "datasets", datasetId));
    } catch (err) {
      console.error("Failed to delete dataset record:", err);
      setDatasetsError("Could not delete dataset: " + (err.message || err));
    }
  };

  const importPublicDataset = async (publicDataset) => {
    if (!user?.uid || !activeProjectId) {
      setDatasetsError("Select a project before importing public dataset leads.");
      return;
    }

    setImportingDatasetId(publicDataset.id);
    setDatasetsError(null);

    try {
      const sourceUrl = publicDataset.sourceUrl || publicDataset.url || "";
      const context = [
        publicDataset.organism,
        publicDataset.topic,
        publicDataset.accession ? `Accession: ${publicDataset.accession}` : "",
      ]
        .filter(Boolean)
        .join(" | ");
      const notes = [
        publicDataset.notes || publicDataset.summary || "",
        publicDataset.analysisFit ? `Suggested analyses: ${publicDataset.analysisFit}` : "",
        publicDataset.sampleCount ? `Sample count: ${publicDataset.sampleCount}` : "",
        publicDataset.gdsType ? `GEO type: ${publicDataset.gdsType}` : "",
        publicDataset.publicationDate ? `Publication/date: ${publicDataset.publicationDate}` : "",
        sourceUrl ? `Source link: ${sourceUrl}` : "",
      ]
        .filter(Boolean)
        .join("\n");

      await addDoc(collection(db, "users", user.uid, "projects", activeProjectId, "datasets"), {
        name: publicDataset.title,
        source: publicDataset.source || "GEO",
        context,
        notes,
        publicDatasetLeadId: publicDataset.id,
        geoAccession: publicDataset.accession || "",
        geoUid: publicDataset.uid || "",
        sourceUrl,
        datasetKind: "dataset-lead",
        reviewStatus: "Needs review",
        ownerUid: user.uid,
        projectId: activeProjectId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("Failed to import public dataset lead:", err);
      setDatasetsError("Could not import public dataset lead: " + (err.message || err));
    } finally {
      setImportingDatasetId(null);
    }
  };

  const searchGeoDatasets = async (queryText) => {
    const trimmed = queryText.trim();

    if (trimmed.length < 2) {
      setGeoSearchError("Enter at least 2 characters to search GEO.");
      return;
    }

    if (!user?.uid) {
      setGeoSearchError("Sign in before searching GEO.");
      return;
    }

    setGeoSearchLoading(true);
    setGeoSearchError(null);

    try {
      const searchGeo = httpsCallable(getFunctions(app, "us-central1"), "searchGeoDatasets");
      const response = await searchGeo({ query: trimmed, limit: 10 });
      setGeoSearchResults(response.data?.results || []);
      if (!response.data?.results?.length) {
        setGeoSearchError("No GEO results found for that search.");
      }
    } catch (err) {
      console.error("Failed to search GEO datasets:", err);
      setGeoSearchError("Could not search GEO: " + (err.message || err));
    } finally {
      setGeoSearchLoading(false);
    }
  };

  const listGeoSupplementaryFiles = async () => {
    const accession = geoSupplementaryAccession.trim().toUpperCase();

    if (!accession) {
      setGeoSupplementaryError("Enter a GEO accession such as GSE305855.");
      return;
    }

    setGeoSupplementaryLoading(true);
    setGeoSupplementaryError(null);

    try {
      const listFiles = httpsCallable(getFunctions(app, "us-central1"), "listGeoSupplementaryFiles");
      const response = await listFiles({ accession });
      setGeoSupplementaryFiles(response.data?.files || []);
      if (!response.data?.files?.length) {
        setGeoSupplementaryError("No supplementary files were found for that accession.");
      }
    } catch (err) {
      console.error("Failed to list GEO supplementary files:", err);
      setGeoSupplementaryError(
        "Could not list GEO files: " +
          (err.details?.message || err.message || err.code || err)
      );
    } finally {
      setGeoSupplementaryLoading(false);
    }
  };

  const importGeoSupplementaryFile = async (file) => {
    if (!user?.uid || !activeProjectId || !activeProject) {
      setGeoSupplementaryError("Select a project before importing GEO files.");
      return;
    }

    setImportingGeoFileId(file.id);
    setGeoSupplementaryError(null);

    try {
      const importFile = httpsCallable(getFunctions(app, "us-central1"), "importGeoSupplementaryFile");
      const response = await importFile({
        projectId: activeProjectId,
        projectName: activeProject.name || "",
        accession: file.accession || geoSupplementaryAccession.trim().toUpperCase(),
        filename: file.filename,
        fileUrl: file.url,
      });

      const compressedNote = response.data?.isCompressed
        ? " Imported file is compressed and needs preparation before direct use in analysis tools."
        : "";
      setGeoSupplementaryError(`Imported ${file.filename} into the project Dataset Library.${compressedNote}`);
    } catch (err) {
      console.error("Failed to import GEO supplementary file:", err);
      setGeoSupplementaryError("Could not import GEO file: " + (err.message || err));
    } finally {
      setImportingGeoFileId(null);
    }
  };

  const selectDatasetForAnalysis = (dataset, target) => {
    const importedInput = importedDatasetSummary(dataset);
    if (!importedInput?.storagePath) {
      setDatasetsError("This dataset does not have an imported Storage file yet.");
      return;
    }

    const targetToTool = {
      heatmap: "heatmap",
      pca: "pca",
      deseq2Counts: "deseq2",
      deseq2Metadata: "deseq2",
      combatCounts: "combat",
      combatMetadata: "combat",
    };

    setAnalysisInputs((prev) => ({
      ...prev,
      [target]: importedInput,
    }));
    setActiveTool(targetToTool[target] || "workspace");
    setError(null);
  };

  const clearAnalysisInput = (target) => {
    setAnalysisInputs((prev) => ({ ...prev, [target]: null }));
  };

  const clearAnalysisInputs = (targets) => {
    setAnalysisInputs((prev) => {
      const next = { ...prev };
      targets.forEach((target) => {
        next[target] = null;
      });
      return next;
    });
  };

  useEffect(() => {
    if (!user?.uid || !activeProjectId) {
      setArtifacts([]);
      setArtifactsLoading(false);
      setArtifactsError(null);
      return undefined;
    }

    setArtifactsLoading(true);
    setArtifactsError(null);

    const artifactsRef = collection(db, "users", user.uid, "projects", activeProjectId, "artifacts");
    const artifactsQuery = query(artifactsRef, orderBy("updatedAt", "desc"));

    const unsubscribe = onSnapshot(
      artifactsQuery,
      (snapshot) => {
        setArtifacts(snapshot.docs.map((artifactDoc) => ({
          id: artifactDoc.id,
          ...artifactDoc.data(),
        })));
        setArtifactsLoading(false);
      },
      (err) => {
        console.error("Failed to load artifact library:", err);
        setArtifactsError("Could not load artifact library: " + (err.message || err));
        setArtifactsLoading(false);
      }
    );

    return unsubscribe;
  }, [user, activeProjectId]);

  const createArtifact = async (event) => {
    event.preventDefault();

    if (!user?.uid || !activeProjectId) {
      setArtifactsError("Select a project before adding artifacts.");
      return;
    }

    const title = artifactForm.title.trim();
    const type = artifactForm.type.trim();
    const runId = artifactForm.runId.trim();
    const urlOrPath = artifactForm.urlOrPath.trim();
    const notes = artifactForm.notes.trim();

    if (!title) {
      setArtifactsError("Artifact title is required.");
      return;
    }

    setIsCreatingArtifact(true);
    setArtifactsError(null);

    try {
      await addDoc(collection(db, "users", user.uid, "projects", activeProjectId, "artifacts"), {
        title,
        type,
        runId,
        urlOrPath,
        notes,
        ownerUid: user.uid,
        projectId: activeProjectId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setArtifactForm({ title: "", type: "", runId: "", urlOrPath: "", notes: "" });
    } catch (err) {
      console.error("Failed to create artifact record:", err);
      setArtifactsError("Could not add artifact: " + (err.message || err));
    } finally {
      setIsCreatingArtifact(false);
    }
  };

  const deleteArtifact = async (artifactId) => {
    if (!user?.uid || !activeProjectId || !artifactId) return;

    try {
      await deleteDoc(doc(db, "users", user.uid, "projects", activeProjectId, "artifacts", artifactId));
    } catch (err) {
      console.error("Failed to delete artifact record:", err);
      setArtifactsError("Could not delete artifact: " + (err.message || err));
    }
  };

  useEffect(() => {
    if (
      !user?.uid ||
      !activeProjectId ||
      runHistoryLoading ||
      artifactsLoading ||
      !runHistory.length
    ) {
      return;
    }

    const existingKeys = new Set(
      artifacts
        .map((artifact) => artifact.autoArtifactKey)
        .filter(Boolean)
    );

    const missingArtifacts = runHistory
      .flatMap(getAutoArtifactsForRun)
      .filter((artifact) => {
        if (!artifact.autoArtifactKey) return false;
        if (existingKeys.has(artifact.autoArtifactKey)) return false;
        if (autoCaptureKeysRef.current.has(artifact.autoArtifactKey)) return false;
        return true;
      });

    if (!missingArtifacts.length) return;

    missingArtifacts.forEach((artifact) => {
      autoCaptureKeysRef.current.add(artifact.autoArtifactKey);
    });

    const artifactsRef = collection(db, "users", user.uid, "projects", activeProjectId, "artifacts");

    Promise.all(
      missingArtifacts.map((artifact) =>
        addDoc(artifactsRef, {
          ...artifact,
          ownerUid: user.uid,
          projectId: activeProjectId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
      )
    ).catch((err) => {
      console.error("Failed to auto-capture artifacts:", err);
      setArtifactsError("Could not auto-capture artifacts: " + (err.message || err));
      missingArtifacts.forEach((artifact) => {
        autoCaptureKeysRef.current.delete(artifact.autoArtifactKey);
      });
    });
  }, [
    user,
    activeProjectId,
    runHistory,
    artifacts,
    runHistoryLoading,
    artifactsLoading,
  ]);

  const activeProject = projects.find((project) => project.id === activeProjectId) || null;
  const projectRunMetadata = getProjectRunMetadata(user, activeProject);

  const exportProjectReport = () => {
    if (!activeProject) {
      setProjectsError("Select a project before exporting a report.");
      return;
    }

    const reportRuns = runHistory.filter(shouldIncludeRunInReport);
    const reportArtifacts = artifacts.filter(shouldIncludeArtifactInReport);
    const report = buildProjectReport({
      project: activeProject,
      datasets,
      runHistory: reportRuns,
      artifacts: reportArtifacts,
    });

    downloadTextFile(`${sanitizeFilename(activeProject.name)}-gator-report.md`, report);
  };

  const exportScientificPoster = () => {
    if (!activeProject) {
      setProjectsError("Select a project before exporting a scientific poster.");
      return;
    }

    const reportRuns = runHistory.filter(shouldIncludeRunInReport);
    const reportArtifacts = artifacts.filter(shouldIncludeArtifactInReport);
    const posterHtml = buildScientificPosterHtml({
      project: activeProject,
      datasets,
      runHistory: reportRuns,
      artifacts: reportArtifacts,
      posterSummary,
    });

    downloadTextFile(
      `${sanitizeFilename(activeProject.name)}-gator-scientific-poster.html`,
      posterHtml,
      "text/html;charset=utf-8"
    );
  };

  const generatePosterSummary = async () => {
    if (!activeProject) {
      setPosterSummaryError("Select a project before generating a poster summary.");
      return;
    }

    const reportRuns = runHistory.filter(shouldIncludeRunInReport);
    const reportArtifacts = artifacts.filter(shouldIncludeArtifactInReport);

    setIsGeneratingPosterSummary(true);
    setPosterSummaryError(null);

    try {
      const generateSummary = httpsCallable(getFunctions(app, "us-central1"), "generatePosterSummary");
      const response = await generateSummary({
        project: {
          name: activeProject.name || "",
          question: activeProject.question || "",
          context: activeProject.context || "",
        },
        datasets: datasets.map((dataset) => ({
          name: dataset.name || "",
          source: dataset.source || "",
          context: dataset.context || "",
          notes: dataset.notes || "",
          datasetKind: dataset.datasetKind || "",
          reviewStatus: dataset.reviewStatus || "",
          storagePath: dataset.storagePath || "",
        })),
        runs: reportRuns.map((run) => ({
          toolName: run.toolName || "",
          status: run.status || "",
          id: run.id || "",
          collectionName: run.collectionName || "",
          inputSourceLabel: run.inputSourceLabel || "",
          inputSourceType: run.inputSourceType || "",
          summary: run.summary || null,
          displayedGenes: run.displayedGenes || null,
          totalGenes: run.totalGenes || null,
        })),
        artifacts: reportArtifacts.map((artifact) => ({
          title: artifact.title || "",
          type: artifact.type || "",
          notes: artifact.notes || "",
          relatedRunCollection: artifact.relatedRunCollection || "",
        })),
      });

      setPosterSummary(response.data || null);
    } catch (err) {
      console.error("Failed to generate poster summary:", err);
      setPosterSummary(buildLocalPosterSummary(activeProject, reportRuns, reportArtifacts));
      const detail = err?.details || err?.message || err?.code || err;
      setPosterSummaryError(
        "Cloud AI summary was unavailable, so GATOR generated a local poster summary instead. Detail: " +
          detail
      );
    } finally {
      setIsGeneratingPosterSummary(false);
    }
  };

  const toggleArtifactReportInclusion = async (artifactId, includeInReport) => {
    if (!user?.uid || !activeProjectId || !artifactId) return;

    try {
      await updateDoc(doc(db, "users", user.uid, "projects", activeProjectId, "artifacts", artifactId), {
        includeInReport,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("Failed to update artifact report inclusion:", err);
      setArtifactsError("Could not update artifact report inclusion: " + (err.message || err));
    }
  };

  const toggleRunReportInclusion = async (run, includeInReport) => {
    if (!run?.collectionName || !run?.id) return;

    try {
      await updateDoc(doc(db, run.collectionName, run.id), {
        includeInReport,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("Failed to update run report inclusion:", err);
      setRunHistoryError("Could not update report inclusion: " + (err.message || err));
    }
  };

  const deleteRun = async (run) => {
    if (!run?.collectionName || !run?.id) return;

    const confirmed = window.confirm(
      `Delete this ${run.toolName || "analysis"} run from the project history? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, run.collectionName, run.id));
    } catch (err) {
      console.error("Failed to delete analysis run:", err);
      setRunHistoryError("Could not delete run: " + (err.message || err));
    }
  };

  useEffect(() => {
    if (!activeProjectId) {
      setRunHistory([]);
      setRunHistoryLoading(false);
      setRunHistoryError(null);
      return undefined;
    }

    setRunHistoryLoading(true);
    setRunHistoryError(null);

    const runsByCollection = {};
    let loadedCollections = 0;
    const unsubscribes = RUN_COLLECTIONS.map((runConfig) => {
      const runQuery = query(
        collection(db, runConfig.collectionName),
        where("projectId", "==", activeProjectId)
      );

      return onSnapshot(
        runQuery,
        (snapshot) => {
          runsByCollection[runConfig.collectionName] = snapshot.docs.map((runDoc) => ({
            id: runDoc.id,
            collectionName: runConfig.collectionName,
            toolName: runConfig.toolName,
            toolKey: runConfig.toolKey,
            ...runDoc.data(),
          }));

          loadedCollections += 1;
          const mergedRuns = Object.values(runsByCollection)
            .flat()
            .sort((a, b) => getRunTime(b) - getRunTime(a));

          setRunHistory(mergedRuns);
          if (loadedCollections >= RUN_COLLECTIONS.length) {
            setRunHistoryLoading(false);
          }
        },
        (err) => {
          console.error(`Failed to load ${runConfig.collectionName} run history:`, err);
          setRunHistoryError("Could not load run history: " + (err.message || err));
          setRunHistoryLoading(false);
        }
      );
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [activeProjectId]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Sign-out error:", err);
      alert("Failed to sign out: " + err.message);
    }
  };

  if (authLoading) {
    return <div>Loading authentication...</div>;
  }

  if (!user) {
    return <SignIn onSignIn={() => setUser(auth.currentUser)} />;
  }

  const ui = {
    page: {
      minHeight: "100vh",
      background: "#0b0f14",
      color: "rgba(255,255,255,0.92)",
      padding: "22px 16px 30px",
    },
    container: {
      width: "min(1150px, 100%)",
      margin: "0 auto",
    },
    topbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      marginBottom: 14,
    },
    brand: { display: "grid", gap: 6 },
    title: { margin: 0, fontSize: 34, letterSpacing: 0.2 },
    subtitle: { margin: 0, fontSize: 13, color: "rgba(255,255,255,0.60)" },

    userChip: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
      borderRadius: 14,
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.10)",
    },
    email: { fontSize: 12, color: "rgba(255,255,255,0.70)" },
    signOutBtn: {
      padding: "10px 12px",
      borderRadius: 12,
      border: "1px solid rgba(255,255,255,0.12)",
      background: "rgba(255,255,255,0.06)",
      color: "white",
      fontWeight: 800,
      cursor: "pointer",
    },

    error: {
      marginTop: 8,
      marginBottom: 10,
      padding: "10px 12px",
      borderRadius: 12,
      border: "1px solid rgba(255,107,107,0.35)",
      background: "rgba(255,107,107,0.10)",
      color: "rgba(255,220,220,0.95)",
      fontSize: 13,
    },

    card: {
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 18,
      boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
    },

    aboutWrap: {
      display: "grid",
      placeItems: "center",
      marginTop: 14,
      marginBottom: 18,
    },
    aboutCard: {
      width: "min(980px, 100%)",
      overflow: "hidden",
    },
    aboutContent: {
      display: "grid",
      gridTemplateColumns: "280px 1fr",
      gap: 16,
      padding: 16,
      alignItems: "center",
    },
    aboutImg: {
      width: "100%",
      borderRadius: 14,
      border: "1px solid rgba(255,255,255,0.08)",
      display: "block",
    },
    aboutH: { margin: 0, fontSize: 18, fontWeight: 900 },
    aboutP: {
      marginTop: 8,
      marginBottom: 12,
      color: "rgba(255,255,255,0.70)",
      fontSize: 14,
      lineHeight: 1.5,
    },
    primaryBtn: {
      padding: "10px 12px",
      borderRadius: 12,
      border: "1px solid rgba(255,255,255,0.12)",
      background: "rgba(255,255,255,0.08)",
      color: "white",
      fontWeight: 900,
      cursor: "pointer",
    },

    sectionTitle: {
      marginTop: 6,
      marginBottom: 10,
      fontSize: 13,
      fontWeight: 900,
      letterSpacing: 0.6,
      color: "rgba(255,255,255,0.65)",
      textTransform: "uppercase",
    },

    toolsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: 14,
      marginBottom: 18,
    },
    toolCard: (active) => ({
      padding: 14,
      borderRadius: 18,
      background: active ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.04)",
      border: active
        ? "1px solid rgba(120,190,255,0.45)"
        : "1px solid rgba(255,255,255,0.08)",
      boxShadow: "0 10px 28px rgba(0,0,0,0.25)",
      cursor: "pointer",
      transition: "transform 120ms ease, border-color 120ms ease",
      display: "grid",
      gap: 10,
    }),
    toolImg: {
      width: "100%",
      height: 120,
      objectFit: "cover",
      borderRadius: 14,
      border: "1px solid rgba(255,255,255,0.08)",
      display: "block",
    },
    toolName: { margin: 0, fontSize: 14, fontWeight: 950 },
    toolDesc: { margin: 0, fontSize: 12, color: "rgba(255,255,255,0.65)", lineHeight: 1.4 },

    main: { padding: 16 },

    activeProject: {
      marginBottom: 14,
      padding: "12px 14px",
      borderRadius: 12,
      border: activeProject
        ? "1px solid rgba(126,217,87,0.35)"
        : "1px solid rgba(255,255,255,0.10)",
      background: activeProject ? "rgba(126,217,87,0.09)" : "rgba(255,255,255,0.04)",
      display: "grid",
      gap: 4,
    },
    activeProjectKicker: {
      margin: 0,
      fontSize: 11,
      fontWeight: 900,
      letterSpacing: 0.6,
      color: activeProject ? "rgba(126,217,87,0.95)" : "rgba(255,255,255,0.50)",
      textTransform: "uppercase",
    },
    activeProjectTitle: {
      margin: 0,
      fontSize: 15,
      fontWeight: 900,
    },
    activeProjectText: {
      margin: 0,
      color: "rgba(255,255,255,0.68)",
      fontSize: 12,
      lineHeight: 1.45,
    },

    footer: {
      marginTop: 20,
      textAlign: "center",
      fontSize: 12,
      color: "rgba(255,255,255,0.45)",
    },
  };

  const toolCards = [
    { key: "combat", name: "ComBat-Seq Batch Correction", desc: "Correct batch effects and visualize pre/post structure.", img: combatIcon },
    { key: "heatmap", name: "Heatmap Generator", desc: "Unsupervised clustered heatmap from normalized data.", img: heatmapIcon },
    { key: "deseq2", name: "DESeq2 Analysis", desc: "Differential expression + volcano plot + DEG table.", img: volcanoIcon },
    { key: "pca", name: "PCA Analysis", desc: "Explore sample variance with PCA outputs and exports.", img: pcaIcon },
    { key: "predictor", name: "Gene Expression Predictor", desc: "Predictability ranking with feature importances.", img: predictIcon },
    { key: "scrna", name: "scRNA Cluster Explorer", desc: "10x .h5 clustering, UMAP, cell typing, and marker outputs.", img: pcaIcon },
  ];

  return (
    <div style={ui.page}>
      <div style={ui.container}>
        {/* Top bar */}
        <div style={ui.topbar}>
          <div style={ui.brand}>
            <h1 style={ui.title}>RNAseq Analysis Tools</h1>
            <p style={ui.subtitle}>Research workspace for public RNA-seq exploration and guided analysis.</p>
          </div>

          <div style={ui.userChip}>
            <div style={{ display: "grid", gap: 2 }}>
              <div style={{ fontSize: 12, fontWeight: 900 }}>Signed in</div>
              <div style={ui.email}>{user?.email || "—"}</div>
            </div>
            <button onClick={handleSignOut} style={ui.signOutBtn}>
              Sign out
            </button>
          </div>
        </div>

        {/* Global error */}
        {error ? <div style={ui.error}>{error}</div> : null}

        {/* About card centered directly under title */}
        <div style={ui.aboutWrap}>
          <div style={{ ...ui.card, ...ui.aboutCard }}>
            <div style={ui.aboutContent}>
              <img src={infoIcon} alt="About GATOR" style={ui.aboutImg} />
              <div>
                <h2 style={ui.aboutH}>About GATOR</h2>
                <p style={ui.aboutP}>
                  Genomic Analysis Toward Official Research (GATOR) is a cloud-based platform for
                  RNA-seq research, education, and biological discovery. Analyze your own datasets,
                  learn RNA-seq through guided lessons, or explore publicly available sequencing data
                  all within a unified browser-based workspace. GATOR streamlines complex bioinformatics
                  workflows, making RNA-seq analysis accessible without requiring local installation of
                  R, Bioconductor, or specialized software.
                </p>
                <button style={ui.primaryBtn} onClick={() => setActiveTool("workspace")}>
                  Open Research Workspace
                </button>
              </div>
            </div>
          </div>
        </div>

        <div style={ui.sectionTitle}>Research Workspace</div>
        <div style={ui.toolsGrid}>
          {[
            {
              key: "workspace",
              name: "Research Workspace v1",
              desc: "Organize projects, import datasets, execute guided RNA-seq analysis workflows, generate interactive reports, and manage research artifacts throughout the analysis lifecycle.",
              visual: "workspace",
            },
            {
              key: "education",
              name: "GATOR for Education",
              desc: "Learn RNA-seq through interactive lessons. Practice guided analysis workflows directly in your browser without installing R, Bioconductor, or other bioinformatics software.",
              visual: "education",
            },
            {
              key: "discovery",
              name: "Public Data Discovery",
              desc: "Explore public RNA-seq datasets to discover gene expression patterns, compare experiments, generate biological hypotheses, and identify datasets of interest.",
              visual: "discovery",
            },
          ].map((t) => (
            <div
              key={t.key}
              style={ui.toolCard(activeTool === t.key)}
              onClick={() => setActiveTool(t.key)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0px)";
              }}
            >
              <WorkspaceCardVisual type={t.visual} />
              <h3 style={ui.toolName}>{t.name}</h3>
              <p style={ui.toolDesc}>{t.desc}</p>
            </div>
          ))}
        </div>

        {/* Tools */}
        <div style={ui.sectionTitle}>Analysis Tools</div>
        <div style={ui.toolsGrid}>
          {toolCards.map((t) => (
            <div
              key={t.key}
              style={ui.toolCard(activeTool === t.key)}
              onClick={() => setActiveTool(t.key)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0px)";
              }}
            >
              <img src={t.img} alt={t.name} style={ui.toolImg} />
              <h3 style={ui.toolName}>{t.name}</h3>
              <p style={ui.toolDesc}>{t.desc}</p>
            </div>
          ))}
        </div>

        {/* Main tool area */}
        <div style={ui.card}>
          <div style={ui.main}>
            <div style={ui.activeProject}>
              <p style={ui.activeProjectKicker}>Active Project Context</p>
              {activeProject ? (
                <>
                  <h3 style={ui.activeProjectTitle}>{activeProject.name}</h3>
                  {activeProject.question ? (
                    <p style={ui.activeProjectText}>Question: {activeProject.question}</p>
                  ) : null}
                  {activeProject.context ? (
                    <p style={ui.activeProjectText}>Context: {activeProject.context}</p>
                  ) : null}
                </>
              ) : (
                <>
                  <h3 style={ui.activeProjectTitle}>No active project selected</h3>
                  <p style={ui.activeProjectText}>
                    Create or select a project folder above to attach project metadata to new analysis runs.
                  </p>
                </>
              )}
            </div>

            {activeTool === "workspace" || activeTool === "education" || activeTool === "discovery" ? (
              <WorkspaceOverview
                setActiveTool={setActiveTool}
                projects={projects}
                activeProjectId={activeProjectId}
                activeProject={activeProject}
                setActiveProjectId={setActiveProjectId}
                projectForm={projectForm}
                setProjectForm={setProjectForm}
                createProject={createProject}
                deleteProject={deleteProject}
                isCreatingProject={isCreatingProject}
                projectsLoading={projectsLoading}
                projectsError={projectsError}
                datasetForm={datasetForm}
                setDatasetForm={setDatasetForm}
                createDataset={createDataset}
                deleteDataset={deleteDataset}
                selectDatasetForAnalysis={selectDatasetForAnalysis}
                datasets={datasets}
                datasetsLoading={datasetsLoading}
                datasetsError={datasetsError}
                isCreatingDataset={isCreatingDataset}
                importPublicDataset={importPublicDataset}
                importingDatasetId={importingDatasetId}
                searchGeoDatasets={searchGeoDatasets}
                geoSearchResults={geoSearchResults}
                geoSearchLoading={geoSearchLoading}
                geoSearchError={geoSearchError}
                geoSupplementaryAccession={geoSupplementaryAccession}
                setGeoSupplementaryAccession={setGeoSupplementaryAccession}
                geoSupplementaryFiles={geoSupplementaryFiles}
                geoSupplementaryLoading={geoSupplementaryLoading}
                geoSupplementaryError={geoSupplementaryError}
                listGeoSupplementaryFiles={listGeoSupplementaryFiles}
                importGeoSupplementaryFile={importGeoSupplementaryFile}
                importingGeoFileId={importingGeoFileId}
                artifactForm={artifactForm}
                setArtifactForm={setArtifactForm}
                createArtifact={createArtifact}
                deleteArtifact={deleteArtifact}
                artifacts={artifacts}
                artifactsLoading={artifactsLoading}
                artifactsError={artifactsError}
                isCreatingArtifact={isCreatingArtifact}
                toggleArtifactReportInclusion={toggleArtifactReportInclusion}
                runHistory={runHistory}
                runHistoryLoading={runHistoryLoading}
                runHistoryError={runHistoryError}
                toggleRunReportInclusion={toggleRunReportInclusion}
                deleteRun={deleteRun}
                exportProjectReport={exportProjectReport}
                exportScientificPoster={exportScientificPoster}
                generatePosterSummary={generatePosterSummary}
                posterSummary={posterSummary}
                posterSummaryError={posterSummaryError}
                isGeneratingPosterSummary={isGeneratingPosterSummary}
                adminKpis={adminKpis}
                adminKpisLoading={adminKpisLoading}
                adminKpisError={adminKpisError}
                refreshAdminKpis={refreshAdminKpis}
                staleJobs={staleJobs}
                staleJobsLoading={staleJobsLoading}
                staleJobsError={staleJobsError}
                staleJobsResult={staleJobsResult}
                selectedStaleJobs={selectedStaleJobs}
                previewStaleJobs={previewStaleJobs}
                toggleSelectedStaleJob={toggleSelectedStaleJob}
                toggleAllStaleJobs={toggleAllStaleJobs}
                deleteSelectedStaleJobs={deleteSelectedStaleJobs}
              />
            ) : activeTool === "about" ? (
              <About />
            ) : activeTool === "combat" ? (
              <CombatSeqAnalysis
                setError={setError}
                setIsProcessing={setIsProcessing}
                isProcessing={isProcessing}
                user={user}
                error={error}
                projectRunMetadata={projectRunMetadata}
                importedCountsInput={analysisInputs.combatCounts}
                importedMetadataInput={analysisInputs.combatMetadata}
                clearImportedInputs={() => clearAnalysisInputs(["combatCounts", "combatMetadata"])}
              />
            ) : activeTool === "heatmap" ? (
              <HeatmapGenerator
                setError={setError}
                setIsProcessing={setIsProcessing}
                isProcessing={isProcessing}
                user={user}
                error={error}
                projectRunMetadata={projectRunMetadata}
                importedInput={analysisInputs.heatmap}
                clearImportedInput={() => clearAnalysisInput("heatmap")}
              />
            ) : activeTool === "deseq2" ? (
              <DESeq2Analysis
                setError={setError}
                setIsProcessing={setIsProcessing}
                isProcessing={isProcessing}
                user={user}
                error={error}
                projectRunMetadata={projectRunMetadata}
                importedCountsInput={analysisInputs.deseq2Counts}
                importedMetadataInput={analysisInputs.deseq2Metadata}
                clearImportedInputs={() => clearAnalysisInputs(["deseq2Counts", "deseq2Metadata"])}
              />
            ) : activeTool === "pca" ? (
              <PCAAnalysis
                setError={setError}
                setIsProcessing={setIsProcessing}
                isProcessing={isProcessing}
                user={user}
                error={error}
                projectRunMetadata={projectRunMetadata}
                importedInput={analysisInputs.pca}
                clearImportedInput={() => clearAnalysisInput("pca")}
              />
            ) : activeTool === "scrna" ? (
              <ScRNAClusterExplorer
                setError={setError}
                setIsProcessing={setIsProcessing}
                isProcessing={isProcessing}
                user={user}
                error={error}
                projectRunMetadata={projectRunMetadata}
              />
            ) : (
              <GeneExpressionPredictor
                setError={setError}
                setIsProcessing={setIsProcessing}
                isProcessing={isProcessing}
                user={user}
                error={error}
                projectRunMetadata={projectRunMetadata}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={ui.footer}>
          {visitCount === null
            ? "Loading total visits..."
            : `Total visits recorded: ${visitCount.toLocaleString()}`}
        </div>
      </div>
    </div>
  );
}
export default App;
